import type { User } from './user'
import type { OnboardingAnswersPatch, OnboardingState, QuestionId } from './onboarding'

// Base API response types
export interface ApiResponse<T = unknown> {
  success: boolean
  error?: string
  message?: string
  data?: T
}

export interface ApiSuccessResponse<T> {
  success: true
  data: T
  message?: string
}

export interface ApiErrorResponse {
  success: false
  error: string
}

// Auth endpoints
export interface LoginRequest {
  email: string
}

export interface LoginResponse {
  success: boolean
  message: string
  email: string
  expires_in: number
}

export interface VerifyOtpRequest {
  email: string
  otp_code: string
}

export interface VerifyOtpResponse {
  success: boolean
  user: User
  /** Access token (JWT) — curto; o refresh token vai so no cookie httpOnly. */
  token: string
  /** Validade do access token, em segundos (`ACCESS_TOKEN_TTL_MINUTES` na API). */
  expires_in: number
  message: string
}

/** `POST /auth/refresh` — 401 com `code: 'refresh_invalid'` encerra a sessao. */
export interface RefreshResponse {
  success: boolean
  token: string
  expires_in: number
}

export interface MeResponse {
  success: boolean
  user: User
}

// Dashboard types
export interface DashboardMetrics {
  total_quizzes: number
  quizzes_completed: number
  average_score: number
  study_hours: number
  specialty_scores: SpecialtyScore[]
}

export interface SpecialtyScore {
  specialty: string
  score: number
  questions_answered: number
}

/**
 * Status de um dia da sequencia de estudo (r6-01), decidido no SERVIDOR
 * (`Dashboard::StudyWeek`): a tela so traduz em cor.
 *
 * - `goal`: TED do dia bateu a meta do dia
 * - `partial`: registrou estudo abaixo da meta, ou sem meta definida
 * - `none`: nenhum registro
 * - `future`: dia ainda por vir — nao e "nao estudou"
 */
export type StudyWeekStatus = 'goal' | 'partial' | 'none' | 'future'

export interface StudyWeekDay {
  /** YYYY-MM-DD */
  date: string
  /** Seg..Dom */
  day_name: string
  is_today: boolean
  status: StudyWeekStatus
  /** TED realizado no dia, em minutos (o mesmo numero do grafico diario). */
  ted_min: number
  /** Meta do dia em minutos; null sem meta semanal definida. */
  goal_min: number | null
}

/** `Dashboard::StudyWeek`: a semana corrente, segunda a domingo, sempre 7 dias. */
export interface StudyWeek {
  week_start: string
  week_end: string
  days: StudyWeekDay[]
}

export interface StudyStats {
  current_streak: number
  longest_streak: number
  total_study_days: number
  studied_today: boolean
  week: StudyWeek
}

/** Um indicador de 30 dias com a base de comparacao (`Dashboard::Kpis#compare`). */
export interface DashboardKpiComparison {
  /** null quando nao ha registro na janela. */
  value: number | null
  /** null quando nao ha registro na janela anterior. */
  previous: number | null
  /** Variacao RELATIVA em % (60 -> 70 = 16.7); null sem base de comparacao. */
  delta_pct: number | null
  /** Questoes que entraram no numero desta janela. */
  questions: number
}

export interface DashboardKpiRetention extends DashboardKpiComparison {
  /** Provas medidas na janela. */
  exams: number
  /**
   * Quantas das `exams` sao simulados do Manual medidos por ESPECIALIDADE (aproximacao
   * da r6-15), e nao por tema. O card diz isso ao aluno quando > 0.
   */
  specialty_scope_exams: number
}

/** Uma grande area no pentagono de provas (r6-04). `score_pct` null = sem questao no periodo. */
export interface ExamTrackScore {
  track_code: string
  track_name: string
  color: string
  questions: number
  /** Acertos dentro de `questions` (r6-16). */
  hits: number
  /** `null` = nenhuma questao da area no periodo (nao mensuravel, nao zero). */
  score_pct: number | null
}

/** `GET /dashboard/exam_track_scores` — pentagono do Dashboard: provas dos ultimos 30 dias, tres origens. */
export interface ExamTrackScores {
  period: { from: string; to: string }
  /** Eixo sensivel: `min = max(0, menor nota − 20)`, `max = 100`; `rule` e o texto para o tooltip. */
  axis: { min: number; max: number; rule: string }
  /** Provas que entraram no periodo, nas tres origens. */
  exams: number
  tracks: ExamTrackScore[]
}

/** `GET /dashboard/kpis` — os cinco cards do topo do Dashboard (r6-02). */
export interface DashboardKpis {
  /** Temporada = desde o primeiro registro do aluno; `from` null sem registro. */
  season: { from: string | null; to: string }
  /** Janela dos cards 3-5: 30 dias moveis terminando hoje. */
  period: { from: string; to: string }
  previous_period: { from: string; to: string }
  questions_total: { value: number; primary: number; revision: number; exams: number }
  ted_total_min: number
  primary_accuracy: DashboardKpiComparison
  revision_accuracy: DashboardKpiComparison
  retention_index: DashboardKpiRetention
}

/**
 * Resultado de um simulado (Api::V1::QuizzesController#result).
 *
 * `attempt` sai de `serialize_attempt`; `results` e montado inline na propria acao — nao
 * ha serializer compartilhado, entao este e o espelho manual dos dois hashes.
 */
export interface QuizAttemptResult {
  id: number
  quiz_id: number
  quiz_title: string | null
  score: number | null
  correct_answers: number | null
  total_questions: number | null
  completed_at: string | null
  created_at: string
}

export interface QuizQuestionResult {
  question_id: number
  question_text: string
  selected_answer_id: number | null
  correct_answer_id: number | null
  is_correct: boolean
  options: Array<{ id: number; text: string; correct: boolean }>
}

export interface RecentActivity {
  type: 'quiz' | 'study'
  title: string
  score?: number
  duration_minutes?: number
  completed_at?: string
  created_at: string
}

// Progress Benchmark types
export type BenchmarkStatus = 'excellent' | 'above_average' | 'on_track' | 'below_average' | 'needs_attention'

export interface PrimaryStudyStats {
  total_sessions: number
  total_study_time: number
  total_questions: number
  total_hits: number
  avg_hit_rate: number
  specialties_count: number
  first_study: string | null
  last_study: string | null
  specialties_breakdown: Record<string, number>
}

export interface RevisionStats {
  total_revisions: number
  total_revision_time: number
  total_questions: number
  total_hits: number
  avg_hit_rate: number
  first_revision: string | null
  last_revision: string | null
}

export interface TestSimulationStats {
  total_tests: number
  avg_performance: number
  avg_ir: number
  first_test: string | null
  last_test: string | null
}

export interface RecommendedRevisionStats {
  total_recommended: number
  completed: number
  completion_rate: number
  avg_score: number
}

export interface BenchmarkAdvice {
  status: BenchmarkStatus
  current_description: string
  recommendations: string[]
}

export interface ProgressBenchmark {
  /**
   * ATENCAO — `UserProgressBenchmarkService#analyze` NAO devolve estes tres campos: ele
   * responde `user_stats` / `platform_benchmarks` / `top_performer_benchmarks` /
   * `comparisons` / `advice`, e nenhum deles tem `combined_score`. Ficam opcionais porque
   * o Dashboard ainda os le; na pratica os tres cards de pontuacao mostram sempre 0%.
   * Consertar de verdade e mudanca de contrato no backend.
   */
  user_score?: { combined_score?: number }
  platform_benchmark?: { combined_score?: number }
  top_performer?: { combined_score?: number }
  user_stats: {
    primary_study: PrimaryStudyStats
    revisions: RevisionStats
    test_and_simulations: TestSimulationStats
    recommended_revisions: RecommendedRevisionStats
  }
  platform_benchmarks: {
    primary_study: {
      avg_sessions: number
      avg_total_study_time: number
      avg_total_questions: number
      avg_hit_rate: number
    }
    revisions: {
      avg_revisions: number
      avg_total_revision_time: number
      avg_total_questions: number
      avg_hit_rate: number
    }
    test_and_simulations: {
      avg_tests: number
      avg_performance: number
      avg_ir: number
    }
    recommended_revisions: {
      avg_completion_rate: number
    }
  }
  top_performer_benchmarks: {
    primary_study: {
      total_sessions: number
      total_study_time: number
      total_questions: number
      avg_hit_rate: number
      specialties_count: number
    }
    revisions: {
      total_revisions: number
      total_revision_time: number
      total_questions: number
      avg_hit_rate: number
    }
    test_and_simulations: {
      total_tests: number
      avg_performance: number
      avg_ir: number
    }
    recommended_revisions: {
      total_recommended: number
      completion_rate: number
    }
  }
  comparisons: {
    primary_study: {
      sessions_vs_platform: number
      sessions_vs_top: number
      questions_vs_platform: number
      questions_vs_top: number
      hit_rate_vs_platform: number
      hit_rate_vs_top: number
    }
    revisions: {
      revisions_vs_platform: number
      revisions_vs_top: number
      questions_vs_platform: number
      questions_vs_top: number
      hit_rate_vs_platform: number
      hit_rate_vs_top: number
    }
    test_and_simulations: {
      tests_vs_platform: number
      tests_vs_top: number
      performance_vs_platform: number
      performance_vs_top: number
    }
    recommended_revisions: {
      completion_rate_vs_platform: number
    }
  }
  advice: {
    primary_study: BenchmarkAdvice
    revisions: BenchmarkAdvice
    test_and_simulations: BenchmarkAdvice
    recommended_revisions: BenchmarkAdvice
  }
}

// Quiz types
export interface Quiz {
  id: number
  title: string
  description: string | null
  questions_count: number
  /** `quizzes.quiz_type` ('entrance_test', 'january'..'december'). */
  quiz_type: string
  /** `Quiz#quiz_type_name` ("Simulado de Entrada", "Março"...). */
  quiz_type_name: string
  /** Derivado do quiz_type (1-12); nil no simulado de entrada. */
  month: number | null
  /** Nao ha coluna de ano em `quizzes`: sempre null hoje. */
  year: number | null
  is_entrance: boolean
  created_at: string
}

export interface QuizQuestion {
  id: number
  text: string
  /** `questions.image_url` — nulo na maioria; so as questoes com figura o preenchem. */
  image_url: string | null
  options: QuizOption[]
  order: number
}

export interface QuizOption {
  id: number
  text: string
  order: number
}

/** POST /quizzes/:id/answer (Quizzes::SubmitAttempt). `score` e `percentage` sao a mesma porcentagem. */
export interface QuizResult {
  attempt_id: number
  score: number
  correct_count: number
  total_questions: number
  percentage: number
  details: Array<{
    question_id: number
    selected_answer_id: number | null
    correct_answer_id: number | null
    is_correct: boolean
  }>
}

// Integration types
export interface IntegrationPlatform {
  id: string
  name: string
  description: string
  icon: string
  connected: boolean
  email: string | null
  sync_status: string | null
  sync_error: string | null
  last_sync_at: string | null
  features: string[]
}

export interface MedwayTrail {
  id: number
  name: string
  exam_name: string
  total_questions: number
  answered_questions: number
  correct_questions: number
  accuracy: number
  completion_percentage: number
  themes_count: number
  url: string
  manual_url: string | null
  category: string | null
  status: 'completed' | 'in_progress' | 'not_started'
  avg_time_seconds: number | null
  completed_at: string | null
  last_sync_at: string
  created_at: string
}

export interface MedwayTrailDetail extends MedwayTrail {
  description: string | null
  exam_id: string | null
  has_valid_url: boolean
}

export interface MedwayQuestion {
  id: number
  question_number: number | null
  speciality: string | null
  study_theme: string | null
  sub_study_theme: string | null
  correct: boolean | null
  result: 'correct' | 'incorrect' | 'not_answered'
  // New API fields
  difficulty: 'e' | 'm' | 'h' | null
  difficulty_label: string | null
  priority: 'l' | 'm' | 'h' | 'p' | null
  institution: string | null
  institution_state: string | null
  year: number | null
  canceled: boolean
  is_outdated: boolean
  scraped_at: string | null
}

export interface MedwayTrailStats {
  total_questions: number
  synced_questions: number
  answered_questions: number
  correct_questions: number
  accuracy: number
  avg_time_seconds: number | null
  by_speciality: Record<string, number>
  by_study_theme: Record<string, number>
}

// Primary Study types (from Medway API)
export interface MedwayPrimaryStudy {
  id: number
  study_date: string | null
  study_speciality: string | null
  study_theme: string | null
  theorical_study_time: number | null
  ted_questions_time: number | null
  filtered_questions: number
  hits: number
  realized_questions: number
  percentage_hits_questions: number
  content_was: string | null
  is_first_study: boolean
  source: string
  medway_trail_id: number | null
  medway_trail_name: string | null
  created_at: string
  updated_at: string
}

export interface PrimaryStudyStats {
  total: number
  total_questions: number
  total_hits: number
  avg_accuracy: number
  by_specialty: Record<string, number>
}

// Revision types (from Medway API)
export interface MedwayRevision {
  id: number
  date_of_revision: string | null
  revision_speciality: string | null
  revision_theme: string | null
  ted_theorical_revision: number | null
  ted_revision_questions: number | null
  realized_questions: number
  hits: number
  filtered_questions: number
  accuracy: number
  revision_number: number
  days_since_first_study: number | null
  source: string
  medway_trail_id: number | null
  medway_trail_name: string | null
  created_at: string
  updated_at: string
}

export interface RevisionStats {
  total: number
  total_questions: number
  total_hits: number
  avg_accuracy: number
  avg_days_gap: number
  by_specialty: Record<string, number>
  by_revision_number: Record<string, number>
}

// Challenge (Simulado) types from Medway API
export interface MedwayChallenge {
  id: number
  external_id: string
  name: string
  question_count: number
  year: number | null
  institution_name: string | null
  course_name: string | null
  track_type: string | null
  thumbnail_url: string | null
  progress_percentage: number
  correct_answer_count: number
  answered_count: number
  score: number | null
  elapsed_time_seconds: number | null
  elapsed_time_formatted: string | null
  accuracy: number
  status: 'completed' | 'in_progress' | 'not_started'
  completed_at: string | null
  synced_at: string | null
  created_at: string
}

export interface ChallengeStats {
  total: number
  completed: number
  in_progress: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  avg_score: number
  by_institution: Record<string, number>
  by_year: Record<string, number>
}

// Recommended Revision types
export interface MedwayRecommendedRevision {
  id: number
  speciality: string
  theme: string | null
  sub_theme: string | null
  total_questions: number
  correct_answers: number
  accuracy: number
  avg_difficulty: string | null
  last_studied_at: string | null
  days_since_study: number
  revision_interval: number | null
  recap: 'initial' | 'week' | 'month' | 'quarter'
  priority_score: number
  priority_level: 'high' | 'medium' | 'low'
  reason: string | null
  completed: boolean
  completed_at: string | null
  due: boolean
  overdue: boolean
  days_until_due: number | null
  medway_trail_id: number | null
  medway_trail_name: string | null
  created_at: string
}

export interface RecommendedRevisionStats {
  total: number
  pending: number
  completed: number
  high_priority: number
  overdue: number
  by_specialty: Record<string, number>
  by_priority: Record<string, number>
}

// ---------- Videoaulas de curso, lado do aluno (spec r4/12) ----------
// Espelho de `VideoCourses::StudentPayload` (medhunter-api). Substitui os tipos do
// `courses`/`lectures` legado (marketplace), que a aba Video Aulas listava.

export interface StudentVideoLesson {
  id: number
  video_course_id: number
  title: string
  description: string | null
  position: number
  /** R5: a aula bloqueada vem sem URL. `hide_locked` nem a lista. */
  unlocked: boolean
  /** R7: o aluno ja abriu esta aula. */
  watched: boolean
  /** O que falta para liberar ("Assista antes: ..."); null quando liberada. */
  unlock_hint: string | null
}

/**
 * A resposta de `POST /video_lessons/:id/open` — a UNICA forma com `embed_url`. A
 * listagem nunca traz URL, nem da aula liberada.
 */
export interface StudentOpenedLesson extends StudentVideoLesson {
  embed_url: string
}

export interface StudentVideoCourse {
  id: number
  /** A SPA separa as abas Modulos e Reunioes Periodicas por este campo. */
  kind: VideoCourseKind
  title: string
  description: string | null
  thumbnail_url: string | null
  display_mode: VideoCourseDisplayMode
  /** R6: o "Curso de Onboarding". */
  onboarding: boolean
  /** Falso quando o curso tem pré-requisito que o aluno ainda não concluiu. */
  unlocked: boolean
  /** "Conclua antes o curso: X"; null quando liberado. */
  unlock_hint: string | null
  /** Aulas VISIVEIS (em `hide_locked`, so as liberadas). */
  lessons_count: number
  unlocked_count: number
  watched_count: number
  /** So em `GET /video_courses/:id`. */
  lessons?: StudentVideoLesson[]
}

// Subscription types
export type SubscriptionType = 'trial' | 'manual' | 'stripe' | 'asaas' | 'none'
export type SubscriptionStatus = 'active' | 'inactive' | 'expired' | 'trial' | 'trial_expired' | 'past_due' | 'canceled'

export interface SubscriptionPayment {
  id: number
  installment_number: number
  amount: number
  status: 'pending' | 'overdue' | 'received' | 'confirmed' | 'refunded' | 'cancelled'
  due_date: string | null
  paid_at: string | null
  invoice_url: string | null
}

export interface Subscription {
  type: SubscriptionType
  status: string
  plan_name: string | null
  plan_interval?: 'month' | 'year'
  plan_price?: number
  subscription_id?: string
  installment_id?: string
  current_period_start?: string
  current_period_end?: string
  starts_at?: string
  expires_at: string | null
  days_remaining: number | null
  auto_renew?: boolean
  cancel_at_period_end?: boolean
  total_value?: number
  expiring_soon?: boolean
  payments?: SubscriptionPayment[]
}

export interface TrialInfo {
  available: boolean
  active: boolean
  expired: boolean
  started_at: string | null
  ends_at: string | null
  days_remaining: number
  expiring_soon: boolean
}

export interface Plan {
  id: number
  /** r7-25: so nos planos Asaas; o checkout e `/checkout/<slug>`. */
  slug?: string
  type: 'stripe' | 'asaas'
  name: string
  description: string | null
  price: number
  full_price?: number
  /** MH14-12: os planos Asaas tambem mandam `day` e `fixed_date`. */
  interval: PlanInterval
  stripe_price_id?: string
  duration_years?: number
  /** MH14-12: 'YYYY-MM-DD' com `interval` `fixed_date`. */
  fixed_end_date?: string | null
  duration_description?: string
  installment_value?: number
  price_with_interest?: number
}

export interface SubscriptionResponse {
  subscription: Subscription
  trial: TrialInfo
  plans: Plan[]
}

export interface SubscriptionStatusResponse {
  has_active: boolean
  status: SubscriptionStatus
  expires_at: string | null
  days_remaining: number
}

// Calendar Event types
/** Tipo do evento do calendario (r6-06). */
export type EventKind = 'study' | 'routine' | 'other'
/** Estagio do estudo; so existe quando `kind === 'study'`, e e null em prova/simulado. */
export type EventStudyStage = 'primary' | 'review'

export interface CalendarEvent {
  id: number
  title: string
  description: string | null
  start: string // ISO8601
  end: string | null // ISO8601
  all_day: boolean
  color: string
  completed: boolean
  kind: EventKind
  study_stage: EventStudyStage | null
  eventable_type: string | null
  eventable_id: number | null
  created_at: string
}

export interface CreateEventData {
  title: string
  description?: string
  start_time: string
  end_time?: string
  all_day?: boolean
  color?: string
  eventable_type?: string
  eventable_id?: number
  completed?: boolean
  kind?: EventKind
  study_stage?: EventStudyStage | null
}

// Study Goal types
export interface Goal {
  id: number
  ted_goal: number // Weekly study goal in minutes
  ted_goal_hours: number // Weekly study goal in hours
  /** r5-06: segunda-feira da semana a que a meta pertence. */
  week_start: string
  /** MH9-05: quem gravou a meta, quando foi o padrinho (`ted` do ciclo). null = o aluno. */
  set_by?: GoalSetBy | null
  created_at: string
  updated_at: string
}

/** MH9-05: o padrinho que definiu a meta TED (ao gravar o `ted` do ciclo do mes). */
export interface GoalSetBy {
  id: number
  name: string
}

// ---------------------------------------------------------------------------
// Meta TED semanal e as tres curvas da semana (r5-06)
// ---------------------------------------------------------------------------

export interface TedWeekDay {
  /** YYYY-MM-DD */
  date: string
  /** Seg..Dom */
  weekday: string
  is_today: boolean
  /** Tempo disponivel declarado para o dia (soma dos turnos), em minutos. */
  available_min: number
  /** Meta diaria, proporcional ao disponivel. null quando a semana nao tem meta. */
  goal_min: number | null
  /** TED realizado no dia (estudo primario + revisao + prova + flashcards), em minutos. */
  realized_min: number
}

export interface TedWeekGoal {
  id: number
  minutes: number
  week_start: string
  /** true quando a semana nao tem meta propria e usa a ultima anterior. */
  inherited: boolean
  /**
   * MH9-05: o padrinho que gravou esta meta pelo `ted` do ciclo. null = o proprio aluno.
   * Opcional so para fixtures antigas; o backend sempre envia.
   */
  set_by?: GoalSetBy | null
}

export interface TedWeekPlan {
  success: boolean
  week_start: string
  week_end: string
  week_offset: number
  is_current_week: boolean
  available_total_min: number
  /** 65% do disponivel, em multiplos de 15 min. null sem disponibilidade. */
  suggested_goal_min: number | null
  goal: TedWeekGoal | null
  realized_total_min: number
  days: TedWeekDay[]
}

export interface SetTedGoalResponse extends TedWeekPlan {
  message: string
}

export interface GoalProgress {
  total_minutes: number
  total_hours: number
  goal_minutes: number
  goal_hours: number
  percentage: number
  remaining_minutes: number
  remaining_hours: number
  on_track: boolean
  breakdown: {
    primary_studies_hours: number
    primary_studies_minutes: number
    revisions_count: number
    revisions_minutes: number
    events_count: number
    events_minutes: number
    medway_trails_count: number
    medway_questions_answered: number
    medway_questions_correct: number
    medway_questions_errors: number
    medway_minutes: number
  }
}

// ==================== MEDWAY TYPES ====================

// Medway Estudos (aggregated by study_theme)
export interface MedwayEstudo {
  id: string
  name: string
  specialty: string
  total_questions: number
  total_correct: number
  accuracy: number
  study_count: number
  first_studied_at: string | null
  last_studied_at: string | null
  status: 'revised' | 'completed'
}

export interface MedwayEstudosStats {
  total: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  by_specialty: Record<string, number>
}

// Medway Revisoes (themes studied multiple times)
export interface MedwayRevisao {
  id: string
  name: string
  specialty: string
  revision_count: number
  total_questions: number
  total_correct: number
  current_accuracy: number
  days_since_first_study: number
  date_of_revision: string | null
  previous_accuracy: number
  improvement: number
}

export interface MedwayRevisoesStats {
  total: number
  total_correct: number
  total_questions: number
  avg_accuracy: number
  avg_days_gap: number
  avg_improvement: number
  by_specialty: Record<string, number>
}

// Medway Recommended Revisions
export interface MedwayRecommendation {
  id: string
  name: string
  specialty: string
  sub_theme: string | null
  total_questions: number
  total_correct: number
  accuracy: number
  last_studied_at: string | null
  days_since_study: number
  priority: 'urgent' | 'high' | 'normal'
  reason: string
}

export interface MedwayRecommendationsStats {
  total: number
  urgent: number
  high: number
  avg_accuracy: number
  avg_days_since_study: number
}

// Medway Simulados (challenges)
export interface MedwaySimulado {
  id: number
  external_id: string
  name: string
  specialty: string
  year: number | null
  total_questions: number
  correct_answers: number
  accuracy: number
  score: number | null
  duration_seconds: number | null
  duration_minutes: number | null
  progress: number | null
  completed_at: string | null
  status: 'completed' | 'in_progress'
  institution: string | null
  institution_state: string | null
}

export interface MedwaySimuladosStats {
  total: number
  completed: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  total_time_minutes: number
  avg_score: number
  by_year: Record<string, number>
}

// ==================== MEDCOF TYPES ====================

export interface MedcofProfile {
  id: number
  external_id: number
  name: string | null
  email: string | null
  anom_name: string | null
  desired_vacancy: string | null
  has_dashboard_access: boolean
  synced_at: string | null
}

export interface MedcofProduct {
  id: number
  external_id: string
  name: string
  bundle_name: string | null
}

export interface MedcofPreset {
  id: number
  external_id: string
  name: string | null
  question_count: number
  duration_in_months: number | null
  status: string | null
  synced_at: string | null
}

export interface MedcofInstitution {
  id: number
  institution_id: number
  institution_name: string
  question_count: number
  index_order: number
}

export interface MedcofReport {
  id: number
  external_id: string
  test_name: string
  question_count: number
  hit_rate: number | null
  accuracy: number
  test_duration: string
  avg_time_per_question: number | null
  tests_done_count: number
  your_medium_answered_right: number | null
  users_medium_answered_right: number | null
  synced_at: string | null
}

export interface MedcofReportDetail extends MedcofReport {
  question_nulled: number
  test_time: { hours: number; minutes: number; seconds: number }
  total_tips: number
  performance: {
    easy: { answered: number; total: number }
    medium: { answered: number; total: number }
    difficult: { answered: number; total: number }
  }
  area_stats: MedcofAreaStat[]
  tags: MedcofTagNode[]
}

export interface MedcofAreaStat {
  area: string
  answered_count: number
  was_right_count: number
  accuracy: number
}

export interface MedcofAreaStatAggregated {
  area: string
  total_answered: number
  total_correct: number
  accuracy: number
}

export interface MedcofTagNode {
  id: number
  external_id: string
  name: string
  questions_count: number
  your_answered_right: number | null
  users_medium_answered_right: number | null
  accuracy: number
  above_average: boolean
  comparison: number
  children: MedcofTagNode[]
}

export interface MedcofTagAggregated {
  name: string
  total_questions: number
  total_correct: number
  avg_users_correct: number
  accuracy: number
}

export interface MedcofDailyProduct {
  id: number
  product_id: number
  product_name: string
  has_daily_cof: boolean
  in_use: boolean
  current_product_name: string | null
}

export interface MedcofReportStats {
  total_reports: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  avg_time_per_question: number
}

// Medcof Estudos (Primary Studies)
export interface MedcofEstudo {
  id: string
  name: string
  specialty: string
  total_questions: number
  total_correct: number
  accuracy: number
  study_count: number
  first_studied_at: string | null
  last_studied_at: string | null
  status: 'revised' | 'completed'
}

export interface MedcofEstudosStats {
  total: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  by_specialty: Record<string, number>
}

// Medcof Revisoes (Revisions)
export interface MedcofRevisao {
  id: string
  name: string
  specialty: string
  revision_number: number
  questions_count: number
  correct_count: number
  accuracy: number
  days_since_last: number
  date_of_revision: string | null
  previous_accuracy: number
  improvement: number
}

export interface MedcofRevisoesStats {
  total: number
  total_questions: number
  avg_accuracy: number
  avg_days_gap: number
  avg_improvement: number
  by_specialty: Record<string, number>
}

// Medcof Recommended Revisions
export interface MedcofRecommendedRevision {
  id: string
  name: string
  specialty: string
  total_questions: number
  total_correct: number
  accuracy: number
  study_count: number
  last_studied_at: string | null
  days_since_last_study: number
  priority: number
  reason: string
  depth: number
}

export interface MedcofRecommendedStats {
  total: number
  low_accuracy: number
  needs_improvement: number
  stale: number
  by_specialty: Record<string, number>
}

// Medcof Simulados (from ranking endpoint or reports fallback)
export interface MedcofSimulado {
  id: number
  external_id: string
  name: string
  institution_id: number | null
  institution_name: string | null
  year: number | null
  question_count: number
  time_limit_minutes: number | null
  exam_type: string | null // 'online' | 'presencial'
  status: string | null // 'Expirado' | 'Ativo' | 'Finalizado' etc.
  start_date: string | null
  end_date: string | null
  // User ranking data (null when using reports fallback)
  general_position: number | null
  score: number | null
  student_count: number | null
  accuracy: number
  percentile: number | null
  is_ranking_active: boolean
  // Vacancy ranking (null when using reports fallback)
  desired_vacancy_id: number | null
  desired_vacancy_name: string | null
  desired_vacancy_students_count: number | null
  placing: number | null
  vacancy_percentile: number | null
  completed: boolean
  synced_at: string | null
  source: 'ranking' | 'reports' // Indicates data source
}

export interface MedcofSimuladosStats {
  total: number
  completed: number
  total_questions: number
  total_score: number
  avg_accuracy: number
  avg_position: number | null
  avg_percentile: number | null
  by_year: Record<string, number>
  by_status: Record<string, number>
  source: 'ranking' | 'reports' // Indicates data source
}

// Aristo data types
export interface AristoEstudo {
  id: number
  study_date: string
  specialty: string
  theme: string
  questions_count: number
  realized_questions: number
  hits: number
  accuracy: number
  study_time_hours: number
  content_description: string
  is_first_study: boolean
  created_at: string
}

export interface AristoEstudosStats {
  total: number
  total_questions: number
  total_realized: number
  total_hits: number
  avg_accuracy: number
  total_study_time: number
  by_specialty: Record<string, number>
  by_theme: Record<string, number>
}

export interface AristoRevisao {
  id: number
  date_of_revision: string
  specialty: string
  theme: string
  is_completed: boolean
  content_description: string
  created_at: string
}

export interface AristoRevisoesStats {
  total: number
  completed: number
  pending: number
  by_specialty: Record<string, number>
}

export interface AristoQuestionData {
  question_id: string
  classification: string[] | null
  difficulty: string | null
  institution: string | null
  uf: string | null
  year: number | null
  is_answered: boolean
  is_correct: boolean
  user_selected: number | null
  correct_choice: number
}

export interface AristoSimulado {
  id: number
  external_id: string
  title: string
  institution: string | null
  uf: string | null
  year: number | null
  total_questions: number
  answered_questions: number
  correct_answers: number
  accuracy_percent: number
  status: 'not_started' | 'in_progress' | 'completed'
  started_at: string | null
  completed_at: string | null
  created_at: string
  question_data: AristoQuestionData[]
}

export interface AristoSimuladosStats {
  total: number
  completed: number
  in_progress: number
  total_questions: number
  total_answered: number
  total_correct: number
  avg_accuracy: number
  by_institution: Record<string, number>
  by_year: Record<string, number>
}

export interface AristoRecommendation {
  specialty: string
  theme: string
  total_questions: number
  correct_answers: number
  accuracy: number
  priority_score: number
  priority_level: 'high' | 'medium' | 'low'
  reason: string
}

export interface AristoRecommendationsStats {
  total: number
  high_priority: number
  medium_priority: number
  low_accuracy: number
  needs_improvement: number
  by_specialty: Record<string, number>
}

// Unified data types (combined from all platforms)
export interface UnifiedEstudo {
  id: string
  source: 'medway' | 'medcof' | 'aristo' | 'combined' | 'manual'
  sources?: string[]
  name: string
  specialty: string
  total_questions: number
  total_correct: number
  accuracy: number
  study_count: number
  first_studied_at: string | null
  last_studied_at: string | null
  status: 'revised' | 'completed'
}

export interface UnifiedEstudosStats {
  total: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  by_specialty: Record<string, number>
  by_source: Record<string, number>
}

export interface UnifiedRevisao {
  id: string
  source: 'medway' | 'medcof' | 'aristo' | 'manual'
  name: string
  specialty: string
  revision_number: number
  questions_count: number
  correct_count: number
  accuracy: number
  days_since_last: number
  date_of_revision: string | null
  previous_accuracy: number
  improvement: number
}

export interface UnifiedRevisoesStats {
  total: number
  total_questions: number
  avg_accuracy: number
  avg_days_gap: number
  avg_improvement: number
  by_specialty: Record<string, number>
  by_source: Record<string, number>
}

export interface UnifiedRecommendation {
  id: string
  // 'aristo' vem de UnifiedStudyDataService#recommended_revisions.
  source: 'medway' | 'medcof' | 'aristo' | 'manual'
  name: string
  specialty: string
  total_questions: number
  total_correct: number
  accuracy: number
  study_count: number
  last_studied_at: string | null
  days_since_last_study: number
  priority: number
  priority_level?: string
  reason: string
  depth: number

  /**
   * ATENCAO — os quatro campos abaixo sao do formato ANTIGO de revisao recomendada e
   * `UnifiedStudyDataService#recommended_revisions` NAO os envia: ele ja filtra por
   * `completed: false` e monta o hash sem eles. Ficam opcionais porque a tela ainda os
   * le; na pratica chegam `undefined`, entao os filtros de "concluida"/"atrasada" e o
   * selo de recap nunca acionam. Preencher isso e mudanca de contrato no backend.
   */
  completed?: boolean
  completed_at?: string | null
  overdue?: boolean
  recap?: string
}

export interface UnifiedRecommendationsStats {
  total: number
  low_accuracy: number
  needs_improvement: number
  stale: number
  by_specialty: Record<string, number>
  by_source: Record<string, number>
}

export interface UnifiedSimulado {
  id: string
  // 'aristo' vem de UnifiedStudyDataService#simulados (MedwayChallenge com source 'aristo').
  source: 'medway' | 'medcof' | 'aristo' | 'manual'
  external_id: string
  name: string
  specialty: string
  question_count: number
  correct_count: number
  accuracy: number
  duration_minutes: number
  avg_time_per_question: number | null
  date: string | null
  performance: {
    easy: { answered: number; total: number }
    medium: { answered: number; total: number }
    difficult: { answered: number; total: number }
  }
  tests_done_count: number
  your_vs_avg: { your: number | null; avg: number | null }
  status?: string
  institution?: string
  year?: number | null
}

export interface UnifiedSimuladosStats {
  total: number
  total_questions: number
  total_correct: number
  avg_accuracy: number
  total_time_minutes: number
  avg_questions_per_test: number
  by_specialty: Record<string, number>
  by_source: Record<string, number>
}

export interface IntegrationStatus {
  medway: boolean
  medcof: boolean
  aristo: boolean
}

// ==================== PROVAS NA INTEGRA TYPES ====================

export interface ProvaInstitution {
  id: number
  external_id: string
  name: string
  abbreviation: string | null
  display_name: string
  state: string | null
  city: string | null
  location: string
  exam_count: number
  thumbnail_url: string | null
  synced_at: string | null
}

export interface ProvaExam {
  id: number
  external_id: string
  name: string
  display_name: string
  full_name: string
  year: number | null
  exam_type: string | null
  specialty: string | null
  question_count: number
  track_id: string | null
  thumbnail_url: string | null
  is_active: boolean
  has_comments: boolean
  institution_id: number | null
  institution_name: string
  institution_abbreviation: string | null
  synced_at: string | null
}

export interface ProvaQuestionOption {
  id: number
  letter: string
  content: string
  response_percentage: number
  /** R11-01: figura da alternativa. */
  images?: Array<{ url: string; order?: number }>
}

export interface ProvaQuestionTag {
  id: number
  name: string
  parent_name: string | null
  tag_type: string
  residency_degree: string | null
}

export interface ProvaQuestion {
  id: number
  question_id: string
  question_number: number
  content: string | null
  speciality: string
  main_speciality: string | null
  study_theme: string | null
  difficulty: string | null
  difficulty_label: string
  difficulty_color: string
  canceled: boolean
  is_outdated: boolean
  status: 'active' | 'canceled' | 'outdated'
  statement: string | null
  options: ProvaQuestionOption[]
  images: Array<{ url: string; kind: string }>
  tags: ProvaQuestionTag[]
  institution_id: number | null
  institution_name: string | null
  institution_state: string | null
  year: number | null
  residency_degree: string | null
  question_type: string | null
  response_stats: {
    total_responses?: number
    most_chosen_letter?: string
    most_chosen_percentage?: number
  }
  synced_at: string | null
}

export interface ProvaExamDetail extends ProvaExam {
  questions: ProvaQuestion[]
  questions_loaded: boolean
  speciality_stats: Record<string, number>
  tag_stats: Record<string, number>
  difficulty_stats: Record<string, number>
  institution_external_id: number | null
  institution_state: string | null
}

export interface ProvasFilters {
  years: number[]
  exam_types: string[]
}

// ==================== MEDCOF PROVAS NA INTEGRA TYPES ====================

export interface MedcofProvaGroup {
  id: number
  name: string
  exam_count: number
  synced_at: string | null
}

export interface MedcofProvaExam {
  id: number
  external_id: string
  name: string
  display_name: string
  group_id: number | null
  group_name: string | null
  year: number | null
  exam_type: string | null
  institution_name: string | null
  question_count: number
  global_average: number | null
  times_made: number
  is_active: boolean
  synced_at: string | null
}

export interface MedcofProvaQuestionAnswer {
  letter: string
  text: string
  is_correct: boolean
}

export interface MedcofProvaQuestion {
  id: number
  external_id: string
  question_number: number
  statement: string | null
  comment: string | null
  answers: MedcofProvaQuestionAnswer[]
  correct_answer: string | null
  tags: Array<{ id?: number; name: string; type?: string }>
  institution_name: string | null
  institution_id: number | null
  year: number | null
  difficulty: string | null
  difficulty_label: string
  difficulty_color: string
  difficulty_index: number | null
  total_times_answered: number
  success_rate: number | null
  difficulty_stats: Record<string, unknown>
  canceled: boolean
  is_outdated: boolean
  status: 'active' | 'canceled' | 'outdated'
  images: Array<{ url?: string }>
  synced_at: string | null
}

export interface MedcofProvaExamDetail extends MedcofProvaExam {
  questions: MedcofProvaQuestion[]
  questions_loaded: boolean
  institution_stats: Record<string, number>
  tag_stats: Record<string, number>
  difficulty_stats: Record<string, number>
  year_stats: Record<string, number>
}

export interface MedcofProvasFilters {
  years: number[]
  exam_types: string[]
  institutions: string[]
}

// Available user tests (tests the user has already started via web)
export interface MedcofProvaAvailableUserTest {
  user_test_id: string
  prebuilt_id: string | null
  name: string
  question_count: number
  exam_in_db: boolean
  exam_has_questions: boolean
  created_at: string | null
}

// ==================== MANUAL ENTRIES ====================

export interface ManualPrimaryStudy {
  id: number
  study_date: string
  study_speciality: string
  study_theme: string
  theorical_study_time: number | null
  ted_questions_time: number | null
  filtered_questions: number | null
  hits: number | null
  realized_questions: number | null
  percentage_hits_questions: number | null
  content_was: string | null
  is_first_study: boolean
  source: 'manual'
  /**
   * Por que este registro caiu em estudo primario. `null` para o que ja estava
   * gravado antes da regra e para o que entra pelas integracoes.
   */
  classification: StudyClassification | null
  course_lesson_ids: number[]
  topic_ids: number[]
  created_at: string
  updated_at: string
  /**
   * r11-08: a lista do Registro de Estudos traz estudos E revisoes, no mesmo formato.
   * Opcional: resposta de API antiga nao tem (e era sempre estudo primario).
   */
  kind?: StudyRecordKind
  /** Numero da revisao (1 = primeira). So em `kind: 'revision'`. */
  revision_number?: number | null
}

/** r11-08: de qual tabela veio o registro da lista. */
export type StudyRecordKind = 'primary' | 'revision'

/** r11-08: o filtro de tipo da lista do Registro de Estudos. */
export type StudyRecordKindFilter = StudyRecordKind | 'all'

/** r11-08: quantos registros de cada tipo o aluno tem (sem os outros filtros). */
export interface StudyRecordKindCounts {
  primary: number
  revision: number
}

/**
 * Rastreabilidade da regra primario vs. revisao (`.spec/primario-revisao.md`).
 * `out_of_catalog` e R4: o aluno escolheu, mas sobre um termo que o catalogo nao tem —
 * quem diz o que ele escolheu e o modelo em que o registro caiu.
 */
export type StudyClassification =
  | 'auto_primary'
  | 'auto_revision'
  | 'user_primary'
  | 'user_revision'
  | 'out_of_catalog'

export interface CreatePrimaryStudyData {
  study_date: string
  study_speciality: string
  study_theme: string
  theorical_study_time?: number
  ted_questions_time?: number
  filtered_questions?: number
  hits?: number
  realized_questions?: number
  content_was?: string
  is_first_study?: boolean
  // Plano 2J refined: when student picks a lesson item in autocomplete, the
  // CourseLesson id is sent here so the backend creates the PrimaryStudyLesson
  // association (discriminator for lesson-study vs topic-study).
  course_lesson_id?: number | null
}

export interface ManualRevision {
  id: number
  date_of_revision: string
  revision_speciality: string
  revision_theme: string
  ted_theorical_revision: number | null
  ted_revision_questions: number | null
  revison_realized_questions: number | null
  revision_questions_hits: number | null
  revision_number: number
  days_since_first_study: number | null
  content_was: string | null
  source: 'manual'
  classification: StudyClassification | null
  course_lesson_ids: number[]
  topic_ids: number[]
  created_at: string
  updated_at: string
}

export interface CreateRevisionData {
  date_of_revision: string
  revision_speciality: string
  revision_theme: string
  ted_theorical_revision?: number
  ted_revision_questions?: number
  revison_realized_questions?: number
  revision_questions_hits?: number
  /** r11-08: editado pelo mesmo formulario do estudo primario. */
  filtered_questions?: number
  revision_number?: number
  content_was?: string
  // Plano 2J refined: same role as CreatePrimaryStudyData.course_lesson_id
  course_lesson_id?: number | null
}

export interface ManualTestAndSimulation {
  id: number
  test_simulation_date: string
  test_name: string
  test_year: string | null
  test_ted: number | null
  test_correction: number | null
  // GO
  go_number_of_questions: number | null
  go_number_of_hits: number | null
  go_number_of_studied_questions: number | null
  go_number_of_hits_in_studied_questions: number | null
  go_performance: number | null
  // PED
  ped_number_of_questions: number | null
  ped_number_of_hits: number | null
  ped_number_of_studied_questions: number | null
  ped_number_of_hits_in_studied_questions: number | null
  ped_performance: number | null
  // PREV
  prev_number_of_questions: number | null
  prev_number_of_hits: number | null
  prev_number_of_studied_questions: number | null
  prev_number_of_hits_in_studied_questions: number | null
  prev_performance: number | null
  // CLM
  clm_number_of_questions: number | null
  clm_number_of_hits: number | null
  clm_number_of_studied_questions: number | null
  clm_number_of_hits_in_studied_questions: number | null
  clm_performance: number | null
  // CIR
  cir_number_of_questions: number | null
  cir_number_of_hits: number | null
  cir_number_of_studied_questions: number | null
  cir_number_of_hits_in_studied_questions: number | null
  cir_performance: number | null
  // Resultado por especialidade sem limite de cinco. Para simulado antigo (gravado antes
  // da tabela `test_and_simulation_specialty_results`) o backend deriva estas linhas das
  // colunas acima, entao a lista nunca vem vazia quando ha numeros.
  specialty_results: ManualTestSpecialtyResult[]
  // Totals
  total_performance: number | null
  created_at: string
  updated_at: string
}

/** Uma linha de "Resultado por Especialidade" de um simulado ja gravado. */
export interface ManualTestSpecialtyResult {
  /** `specialties.id`. Nulo quando a especialidade nao esta no catalogo. */
  specialty_id: number | null
  specialty_code: string
  specialty_name: string
  number_of_questions: number | null
  number_of_hits: number | null
  number_of_studied_questions: number | null
  number_of_hits_in_studied_questions: number | null
  /** Aproveitamento (%). Nulo quando nao ha questoes — nao e 0. */
  performance: number | null
}

/** O que o formulario envia por especialidade. */
export interface TestSpecialtyResultInput {
  specialty_id?: number | null
  specialty_code: string
  specialty_name: string
  number_of_questions?: number
  number_of_hits?: number
  number_of_studied_questions?: number
  number_of_hits_in_studied_questions?: number
}

/**
 * De onde saiu a lista de especialidades do formulario.
 *
 * - `priority_institutions` — provas das instituicoes prioritarias no processo seletivo
 * - `priority_institutions_all_degrees` — idem, sem o filtro de grau (nao havia questao
 *   classificada no grau do aluno)
 * - `selection_process` — nenhuma instituicao util; lista derivada so do processo seletivo
 * - `default` — as cinco grandes areas (nada configurado ou catalogo de provas vazio)
 */
export type TestSpecialtyCatalogSource =
  | 'priority_institutions'
  | 'priority_institutions_all_degrees'
  | 'selection_process'
  | 'default'

export interface TestSpecialtyOption {
  id: number
  code: string
  name: string
  color: string
  /** Questoes desta especialidade nas provas na integra consideradas. */
  questions_count: number
  exams_count: number
  /** Peso da especialidade no conjunto de provas (%). Nulo quando nao ha base. */
  share: number | null
  position: number
}

export interface TestSpecialtyInstitution {
  id: number
  name: string
  abbreviation: string | null
  state: string | null
  /** 0 quando a instituicao existe no catalogo mas nao tem prova na integra. */
  exams_count: number
}

/** Verbete de Catalogos > Especialidades, para o "adicionar outra especialidade". */
export interface TestSpecialtyCatalogEntry {
  id: number
  code: string
  name: string
  color: string
}

/** Resposta de GET /manual_entries/test_specialties. */
export interface TestSpecialtyCatalog {
  /** Especialidades derivadas das provas — o que o formulario pede por padrao. */
  specialties: TestSpecialtyOption[]
  /** Catalogo completo, so para o escape de "adicionar outra especialidade". */
  all_specialties: TestSpecialtyCatalogEntry[]
  source: TestSpecialtyCatalogSource
  selection_process: string | null
  residency_degree: string | null
  institutions: TestSpecialtyInstitution[]
  /** Nomes de instituicao prioritaria que nao existem no catalogo de provas. */
  unmatched_institutions: string[]
  exams_count: number
  questions_count: number
}

export interface CreateTestAndSimulationData {
  test_simulation_date: string
  test_name: string
  test_year?: string
  test_ted?: number
  test_correction?: number
  go_number_of_questions?: number
  go_number_of_hits?: number
  go_number_of_studied_questions?: number
  go_number_of_hits_in_studied_questions?: number
  ped_number_of_questions?: number
  ped_number_of_hits?: number
  ped_number_of_studied_questions?: number
  ped_number_of_hits_in_studied_questions?: number
  prev_number_of_questions?: number
  prev_number_of_hits?: number
  prev_number_of_studied_questions?: number
  prev_number_of_hits_in_studied_questions?: number
  clm_number_of_questions?: number
  clm_number_of_hits?: number
  clm_number_of_studied_questions?: number
  clm_number_of_hits_in_studied_questions?: number
  cir_number_of_questions?: number
  cir_number_of_hits?: number
  cir_number_of_studied_questions?: number
  cir_number_of_hits_in_studied_questions?: number
  /**
   * Resultado por especialidade. Quando presente, e ela que manda: o backend regrava as
   * linhas e espelha nas colunas acima as cinco grandes areas. Omitir a chave (e nao
   * mandar `[]`) significa "nao mexi nas especialidades".
   */
  specialty_results?: TestSpecialtyResultInput[]
}

/**
 * Envelope de paginacao das listagens do MedHunter Manual (spec r4-09).
 *
 * Sem `page` na query a API devolve a lista inteira e o `meta` vem com `page: 1`,
 * `per_page: total`, `total_pages: 1` — o cliente le "nao ha mais paginas" sem caso
 * especial. Com `page`, `per_page` e 20 por padrao e no maximo 100.
 */
export interface PaginationMeta {
  total: number
  page: number
  per_page: number
  total_pages: number
}

/**
 * O cursinho do aluno resolvido pelo servidor (spec r4-02). `lessons_count` sao as aulas
 * ATIVAS dele no catalogo: zero significa que a aba "Aula" nao tem o que oferecer.
 */
export interface ResolvedPrepCourse {
  id: number
  name: string
  lessons_count: number
}

/** Filtros de servidor das listagens do Manual. Todos opcionais; vazio = tudo. */
export interface ManualListParams {
  page?: number
  per_page?: number
  /** Igualdade exata com `study_speciality` (so estudos). */
  speciality?: string
  /** Trecho do tema (estudos) ou do nome do simulado, sem caixa. */
  q?: string
  /**
   * r11-08, so estudos: `primary` (padrao da API), `revision` ou `all`. Sem ele a rota
   * devolve so estudos primarios, como sempre.
   */
  kind?: StudyRecordKindFilter
}

export interface ManualPrimaryStudyStats {
  total_studies: number
  total_study_time: number
  total_questions: number
  total_hits: number
  avg_hit_rate: number
  by_speciality: Record<string, number>
}

export interface ManualRevisionStats {
  total_revisions: number
  total_revision_time: number
  total_questions: number
  total_hits: number
  avg_hit_rate: number
  by_speciality: Record<string, number>
}

export interface ManualTestStats {
  total_tests: number
  total_test_time: number
  avg_performance: number
  /**
   * Chave = `specialty_code`. Nao e mais um conjunto fixo de cinco: o agregado sai das
   * linhas de `specialty_results`, entao so aparecem as especialidades que o aluno
   * realmente registrou.
   */
  by_speciality: Record<string, { name: string; questions: number; hits: number; rate: number }>
}

// Shape returned by /manual_entries/suggestions (Plano 2J refined).
// Subtopics are filtered server-side; only lessons + topics are exposed.
export interface ThemeSuggestion {
  theme: string
  speciality: string | null
  /**
   * Nivel 2 do catalogo (r6-17, R2): a tela mostra "Especialidade › Subespecialidade" na
   * linha do topico para desambiguar homonimos (TCE em Cirurgia Geral e em Pediatria).
   * Nulo em aula e no fallback legado.
   */
  sub_speciality?: string | null
  type: 'lesson' | 'topic'
  course_lesson_id?: number
  /** `topics.id` do Topico no catalogo. Ausente no fallback. */
  topic_id?: number
  level?: 'topic' | 'subtopic'
  source?: string
}

// ==================== PRIMARIO vs. REVISAO ====================
// Contrato de /manual_entries/classify_entries e /manual_entries/study_entries.
// A regra roda no servidor; o cliente so manda o que o aluno informou e, quando a
// regra e ambigua, a ESCOLHA dele.

export type StudyEntryVerdict = 'primary' | 'revision' | 'ask'

/**
 * O ESTADO do topico (spec r3/02, R1) — o vocabulario da reuniao, e o que a tela
 * renderiza:
 *
 * - `first`         primeiro registro do topico. Grava estudo primario, SEM modal.
 * - `ask`           ja registrado e nunca revisado. Modal obrigatorio, ate o aluno marcar
 *                   "revisao" pela primeira vez.
 * - `auto_revision` ja revisado. Grava revisao sem modal, com aviso curto na tela.
 *
 * A regra e do BACKEND, que a deriva do historico (`Studies::TopicClassificationState`).
 * O cliente nao deduz estado nenhum: ele le este campo. `verdict` continua dizendo o
 * destino DESTE registro, e os dois so divergem quando o aluno usa o "foi estudo
 * primario?" de um topico em `auto_revision`.
 */
export type StudyEntryClassificationState = 'first' | 'ask' | 'auto_revision'

/**
 * A escolha do aluno para UM topico de um item (spec r2/08, R1).
 *
 * Existe porque uma AULA pode ter topicos em estados diferentes — um ja revisado, outro
 * inedito — e a escolha deixou de ser do item para ser do topico. `choice` continua
 * valendo para o item inteiro (texto livre, topico solto, aula de um topico so).
 */
export interface StudyEntryTopicChoice {
  topic_id: number
  choice: 'primary' | 'revision'
}

/** Um tema do formulario, ja com o que o autocomplete resolveu do catalogo. */
export interface StudyEntryItemInput {
  name: string
  type: 'lesson' | 'topic'
  course_lesson_id?: number | null
  topic_id?: number | null
  speciality?: string | null
  /** So para item cujo veredito foi `ask` — o que o aluno respondeu no modal. */
  choice?: 'primary' | 'revision' | null
  /** Uma entrada por topico perguntado. Vazio (ou ausente) quando a escolha e do item. */
  topic_choices?: StudyEntryTopicChoice[] | null
  /**
   * So em aula: topicos do catalogo da aula que o aluno DESMARCOU. Com algum, o registro
   * nao leva o vinculo de aula, so os topicos mantidos, e cada desmarcado vira auditoria.
   */
  excluded_topic_ids?: number[] | null
  /** So em aula: topicos de fora que o aluno ACRESCENTOU. Cada um vira auditoria. */
  extra_topic_ids?: number[] | null
}

/** Um topico do catalogo, como a lista de topicos da aula o mostra. */
export interface StudyEntryLessonTopic {
  topic_id: number
  name: string
}

/**
 * Os topicos de CATALOGO de uma aula do formulario, antes do ajuste do aluno. As linhas de
 * `results` so trazem os topicos que ficaram; esta lista e o que permite remarcar um
 * topico desmarcado.
 */
export interface StudyEntryLessonTopics {
  course_lesson_id: number
  name: string
  input_name: string
  topics: StudyEntryLessonTopic[]
}

/**
 * Um veredito. A partir da spec r2/08 ele e por TOPICO: uma aula com dois topicos em
 * estados diferentes devolve DUAS decisoes com o mesmo `name` (o titulo da aula) e
 * `topic_id` diferentes.
 */
export interface StudyEntryDecision {
  name: string
  verdict: StudyEntryVerdict
  /** O estado do topico. Opcional porque respostas antigas do backend nao trazem o campo. */
  classification_state?: StudyEntryClassificationState
  reason_code: string
  reason: string
  type?: string
  course_lesson_id?: number | null
  topic_id?: number | null
  /** Nome do topico deste veredito. `null` em item sem topico (texto livre). */
  topic_name?: string | null
  /** O texto que o aluno digitou — a chave de reenvio, ver `StudyEntryPending`. */
  input_name?: string
  /** So em `r4_ambiguous_topic` (r6-24, revisao): os topicos entre os quais escolher. */
  candidates?: StudyEntryTopicCandidate[]
}

/**
 * Um topico com que o texto digitado casou (r6-24, revisao). So vem em
 * `r4_ambiguous_topic`: "HIV" em R1 casa com um topico de Clinica Medica e um de GO, e o
 * servidor pergunta qual em vez de dizer que o tema nao existe. Escolher um reenvia o
 * item com `topic_id` — o mesmo caminho do clique na sugestao do autocomplete.
 */
export interface StudyEntryTopicCandidate {
  topic_id: number
  name: string
  speciality: string | null
}

/** Item que o servidor recusou gravar sem escolha do aluno. */
export interface StudyEntryPending {
  /** Nome CANONICO (titulo da aula, nome do topico). E o que o aluno le na pergunta. */
  name: string
  /**
   * O texto que o aluno DIGITOU, e a chave pela qual a escolha volta ao servidor. Os dois
   * so coincidem quando ele clicou na sugestao: digitar "infarto agudo do miocardio" numa
   * aula cadastrada com acento fazia a escolha voltar como `null` e a pergunta se repetir
   * em laco. Opcional porque respostas antigas do backend nao trazem o campo.
   */
  input_name?: string
  /**
   * O topico desta pergunta (spec r2/08). Duas perguntas da MESMA aula chegam com o mesmo
   * `name`: sem o topico na chave, uma escolha sobrescreveria a outra — o proprio bug que
   * a spec conserta, reencenado no transporte. `null` em item sem topico.
   */
  topic_id?: number | null
  topic_name?: string | null
  reason_code: string
  reason: string
  /** So em `r4_ambiguous_topic` (r6-24, revisao): os topicos entre os quais escolher. */
  candidates?: StudyEntryTopicCandidate[]
}

export interface StudyEntryAttributes {
  study_date: string
  /**
   * NAO ENVIAR (r6-17, R3): `study_speciality` e derivada no servidor do topico/aula
   * escolhido. O campo continua no tipo so como fallback de cliente antigo.
   */
  speciality?: string
  theorical_study_time?: number
  ted_questions_time?: number
  filtered_questions?: number
  realized_questions?: number
  hits?: number
  content_was?: string
}

export interface CreatedStudyEntry {
  kind: 'primary_study' | 'revision'
  classification: StudyClassification
  themes: string[]
  /**
   * Os `topics.id` que ESTE registro cobre — o mesmo campo, no mesmo lugar, que o
   * encerramento do cronometro (`FinishedStudyEntry`) devolve. Nao confundir com
   * `primary_study.topic_ids`, que lista so as linhas de `primary_study_topics` gravadas
   * (vazias quando a aula nao se dividiu e o vinculo foi so de aula). Opcional porque
   * respostas antigas do backend nao trazem o campo.
   */
  topic_ids?: number[]
  primary_study?: ManualPrimaryStudy
  revision?: ManualRevision
}

/**
 * `needs_choice` significa que NADA foi gravado — nem estudo, nem revisao, nem
 * solicitacao ao Admin. O cliente pergunta e reenvia com `choice` preenchido.
 */
export interface CreateStudyEntriesResponse {
  success: boolean
  needs_choice?: boolean
  pending?: StudyEntryPending[]
  created?: CreatedStudyEntry[]
  decisions?: StudyEntryDecision[]
  message?: string
}

// ---- Idempotencia dos creates do Manual (r6-18, R1) ----
//
// `Idempotency-Key` (uuid por abertura do modal) nos POSTs de primary_studies,
// tests_and_simulations e study_entries. Chave repetida devolve a MESMA resposta da
// primeira vez (header `Idempotent-Replayed: true`) sem gravar de novo; chave ainda em
// processamento responde 409 com `code: 'idempotent_in_progress'`.

export interface IdempotentRequestOptions {
  idempotencyKey?: string
}

export const IDEMPOTENT_IN_PROGRESS_CODE = 'idempotent_in_progress'

export interface ClassifyEntriesResponse {
  success: boolean
  results: StudyEntryDecision[]
  /** Opcional porque respostas de backend anterior ao ajuste de topicos nao trazem. */
  lessons?: StudyEntryLessonTopics[]
}

// ==================== CRONOMETRO DE TED (spec r2/07) ====================
// Contrato de /api/v1/study_sessions. A contagem em andamento e um recurso PROPRIO, e
// nao um estudo pela metade: enquanto ela existe, nada entrou no TED, no FSRS nem na
// lista do Manual.
//
// UNIDADE: tudo aqui e SEGUNDO. As horas so aparecem no registro materializado.

export type StudySessionPhase = 'theorical' | 'questions'
export type StudySessionKind = 'primary' | 'revision'

// ---- Modos de contagem (spec r3-01) ----
//
// UNIDADE, e a regra que mais se esquece aqui: MINUTO existe em UM lugar so — os campos
// `*_minutes` de `CreateStudySessionData`, que sao o que o ALUNO digita. Tudo que volta do
// servidor e SEGUNDO, sem excecao. Misturar os dois nao quebra o build (os dois sao
// `number`) — quebra o cronometro em silencio, com uma regressiva de 25 segundos onde o
// aluno pediu 25 minutos.

export type StudySessionMode = 'stopwatch' | 'timer' | 'pomodoro'
export type PomodoroStage = 'study' | 'break' | 'long_break'

/** O preset CONGELADO na abertura, como o servidor o devolve: segundos. */
export interface PomodoroConfig {
  study_seconds: number
  break_seconds: number
  cycles: number
  long_break_seconds: number
  long_break_every: number
}

/** O que o ALUNO informa. Minutos, e so aqui — tudo que volta do servidor e segundo. */
export interface PomodoroConfigInput {
  study_minutes: number
  break_minutes: number
  cycles: number
  /** 0 = sem pausa longa. */
  long_break_minutes: number
  long_break_every: number
}

/** Um tema da contagem, com o veredito CONGELADO na abertura (R9). */
export interface StudySessionItem {
  name: string
  type: string
  verdict: 'primary' | 'revision'
  classification: StudyClassification
  reason: string
}

export interface StudySession {
  id: number
  kind: StudySessionKind
  phase: StudySessionPhase
  started_at: string
  /** Instante em que a FASE corrente comecou. E dele que sai o relogio da tela (R4). */
  phase_started_at: string
  /**
   * Instante em que o aluno clicou "Encerrar" e o servidor PAROU o relogio. Enquanto isso
   * ele ainda responde "vai fazer questoes agora?" e preenche o formulario de
   * encerramento — e nada disso pode virar tempo de estudo. Nulo = contagem correndo.
   */
  phase_stopped_at: string | null
  /** `phase_stopped_at` presente. O relogio da tela desenha o valor congelado. */
  stopped: boolean
  finished_at: string | null
  theorical_seconds: number
  questions_seconds: number
  elapsed_phase_seconds: number
  elapsed_theorical_seconds: number
  elapsed_questions_seconds: number
  elapsed_total_seconds: number
  over_cap: boolean
  cap_seconds: number
  study_date: string
  speciality: string | null
  items: StudySessionItem[]
  /**
   * O relogio do SERVIDOR no instante da resposta. O cliente ancora este valor e desenha
   * a diferenca — e por isso que uma maquina com o relogio errado nao grava tempo errado.
   */
  server_now: string

  // ---- Modos (spec r3-01). Em `stopwatch` tudo abaixo e nulo/0/false, e a tela tem que
  // continuar identica a de hoje (R6): quem le estes campos precisa tratar o nulo, nao
  // assumir Pomodoro.
  mode: StudySessionMode
  /** Alvo do modo `timer`, em SEGUNDOS. Nulo nos outros modos. */
  target_seconds: number | null
  /** Preset congelado na abertura do Pomodoro. Nulo nos outros modos. */
  pomodoro_config: PomodoroConfig | null
  /** Ciclo corrente do Pomodoro, 1-based. Vale 1 fora do Pomodoro. */
  pomodoro_cycle: number
  pomodoro_stage: PomodoroStage | null
  /** Instante em que a ETAPA corrente comecou (ISO, relogio do servidor). */
  stage_started_at: string | null
  /**
   * `stage_started_at + stage_seconds`. E DESTE campo que sai a regressiva da tela: o
   * cliente subtrai o "agora do servidor" e redesenha. Nunca acumule ticks para chegar
   * a ele — ver `stageClock.ts`.
   */
  stage_ends_at: string | null
  /** Duracao da etapa corrente, em SEGUNDOS. */
  stage_seconds: number | null
  /** Restante no instante da RESPOSTA, em segundos. Envelhece: serve de valor inicial. */
  stage_remaining_seconds: number | null
  /** A etapa venceu e ninguem avancou (aba fechada, maquina suspensa). */
  stage_overdue: boolean
  /** Pomodoro no ultimo ciclo e ja parado: o cliente segue para o encerramento. */
  pomodoro_completed: boolean
}

export interface CreateStudySessionData {
  phase: StudySessionPhase
  /**
   * Opcional, e normalmente OMITIDO: sem ele o servidor usa `Date.current`, no fuso da
   * aplicacao. Mandar a data do navegador em ISO (`toISOString`) a envia em UTC, e depois
   * das 21h em Sao Paulo isso ja e o dia seguinte — o estudo cairia amanha no TED.
   */
  study_date?: string
  /** NAO ENVIAR (r6-17, R3): a especialidade e derivada no servidor do tema escolhido. */
  speciality?: string
  /** Ausente ou vazio = `stopwatch`, e a resposta e byte a byte a de hoje (R6). */
  mode?: StudySessionMode
  /**
   * OBRIGATORIO com `mode: 'timer'`. MINUTOS (1..360) — este e um dos dois unicos campos
   * do contrato em minutos; a conversao para segundos acontece so no servidor
   * (`Studies::SessionOpener`). Mandar segundos aqui vira um timer de 360 horas recusado
   * pelo 422, ou pior, um alvo 60x errado dentro da faixa.
   */
  target_minutes?: number
  /** OBRIGATORIO com `mode: 'pomodoro'`. Tambem em MINUTOS. */
  pomodoro?: PomodoroConfigInput
}

/**
 * `needs_choice` significa que NADA foi gravado e o relogio NAO comecou: o mesmo
 * contrato de /manual_entries/study_entries, pelo mesmo motivo.
 */
export interface StudySessionResponse {
  success: boolean
  needs_choice?: boolean
  pending?: StudyEntryPending[]
  study_session?: StudySession | null
  decisions?: StudyEntryDecision[]
  message?: string
}

/** O que o cronometro nao consegue medir. Duracao nao esta aqui e nao pode estar (R4). */
export interface FinishStudySessionData {
  realized_questions?: number
  hits?: number
  filtered_questions?: number
  content_was?: string
}

/** Registro materializado pelo encerramento. Mesmo vocabulario de `CreatedStudyEntry`. */
export interface FinishedStudyEntry {
  kind: 'primary_study' | 'revision'
  classification: StudyClassification
  themes: string[]
  record_id: number
  topic_ids: number[]
}

export interface FinishStudySessionResponse {
  success: boolean
  message?: string
  theorical_seconds?: number
  questions_seconds?: number
  created?: FinishedStudyEntry[]
  decisions?: StudyEntryDecision[]
}

// ==================== WEB PUSH (spec r3-01, R4) ====================
// O aviso de fim de etapa com a aba fechada. Tudo aqui degrada: sem VAPID no ambiente o
// servidor responde `enabled: false` e o cliente cai no aviso in-app, sem erro na tela.

export interface PushPublicKeyResponse {
  success: boolean
  /** Chave VAPID publica em base64url. Nula quando `enabled` e false. */
  public_key: string | null
  /**
   * False quando o servidor nao tem as env VAPID. E o sinal de "nao adianta pedir
   * permissao ao navegador" — pedir e falhar depois gastaria o unico pedido que o
   * navegador concede por origem.
   */
  enabled: boolean
}

/** O `PushSubscription` do navegador, no formato que o servidor grava. */
export interface PushSubscriptionPayload {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

// ==================== R5 — SOLICITACOES DE CORRECAO DE CATALOGO ====================
// Fila do admin: aula/topico que um aluno informou e o catalogo nao tem.

export type CatalogCorrectionStatus = 'pending' | 'resolved' | 'rejected'

/**
 * `lesson_without_topic` (r11-09, buraco 5): aula DO catalogo sem topico vinculado — o
 * admin resolve ligando topicos a aula, e a pendencia se fecha sozinha.
 */
export type CatalogCorrectionRequestType = 'lesson' | 'topic' | 'lesson_without_topic'

export interface CatalogCorrectionRequest {
  id: number
  request_type: CatalogCorrectionRequestType
  /** Exatamente o que o aluno digitou — nao e editavel pelo admin. */
  informed_name: string
  normalized_name: string
  speciality: string | null
  status: CatalogCorrectionStatus
  admin_notes: string | null
  resolved_at: string | null
  created_at: string
  user: { id: number; email: string | null; name: string | null }
  /** O estudo ou revisao que o aluno gravou com o termo. */
  origin: { type: 'PrimaryStudy' | 'Revision'; id: number; theme: string | null } | null
  /** r11-09: como foi resolvida. Opcional: API antiga nao manda. */
  resolution?: CatalogCorrectionResolution | null
  /** r11-09: o topico do catalogo a que os registros foram vinculados. */
  resolved_topic?: { id: number; name: string } | null
  /** r11-09: a aula do catalogo, so em `lesson_without_topic`. Opcional: API antiga nao manda. */
  course_lesson?: { id: number; title: string; prep_course_id: number | null } | null
  /**
   * r11-09 — alcance do vinculo, so na pendente (nulo nas decididas): alunos e registros sem
   * topico com o mesmo termo que "Todos os alunos" pega, e os registros deste aluno.
   */
  affected_students_count?: number | null
  affected_records_count?: number | null
  student_records_count?: number | null
}

/**
 * r11-09 — `student_only`: o termo segue como topico so do aluno (TED e "Outros").
 * `linked`: os registros foram vinculados a um topico do catalogo.
 */
export type CatalogCorrectionResolution = 'student_only' | 'linked'

/** r11-09: vincular so os registros deste aluno, ou de todo aluno com o mesmo termo. */
export type CatalogCorrectionLinkScope = 'student' | 'all'

/** r11-09: topico que ja existe (`topic_id`) ou um novo (`new_topic`). */
export interface CatalogCorrectionLinkPayload {
  scope: CatalogCorrectionLinkScope
  topic_id?: number
  new_topic?: { name: string; sub_specialty_id: number }
}

export interface CatalogCorrectionRequestStats {
  total: number
  pending: number
  resolved: number
  rejected: number
}

// ---------- Auditoria de topicos ajustados na aula ----------
// Contrato de /admin/lesson_topic_adjustments. Uma linha por topico que o aluno tirou
// (`removed`) ou pos (`added`) numa aula ao registrar o estudo.

export type LessonTopicAdjustmentStatus = 'pending' | 'reviewed' | 'all'
export type LessonTopicChangeType = 'added' | 'removed'

/** Um topico da aula com os ajustes agregados nas duas direcoes. */
export interface LessonTopicAdjustmentTopic {
  topic_id: number
  name: string
  /** O topico esta HOJE vinculado a aula no catalogo. */
  in_lesson: boolean
  removed_count: number
  removed_students: number
  added_count: number
  added_students: number
}

export interface LessonTopicAdjustmentLesson {
  course_lesson: {
    id: number
    lesson_title: string
    lesson_code: string
    prep_course: string | null
    specialty: string | null
    topics_count: number
  }
  /** Ajustes ainda nao reavaliados, sempre do todo (independe do filtro). */
  pending_count: number
  students_count: number
  last_adjusted_at: string
  topics: LessonTopicAdjustmentTopic[]
}

export interface LessonTopicAdjustmentStats {
  pending: number
  lessons_pending: number
  total: number
}

export interface LessonTopicAdjustmentRow {
  id: number
  change_type: LessonTopicChangeType
  source: 'manual' | 'timer'
  topic: { id: number; name: string | null }
  user: { id: number; email: string | null; name: string | null }
  origin_record_type: string | null
  origin_record_id: number | null
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
}

export interface SpecialtyOption {
  code: string
  name: string
}

// ==================== STUDY JOURNEY TYPES ====================

// O restante do espelho de `StudyJourneyService` (StudyJourney, InstitutionJourney e
// os quatro tipos de breakdown/plano/alerta) saiu junto com a aba "Benchmark e
// integracoes", unica tela que chamava /dashboard/study_journey.
//
// JourneyStatus FICA: types/journey.ts o re-exporta e /app/jornada o usa para
// rotular a prontidao por instituicao.
export type JourneyStatus = 'excellent' | 'on_track' | 'needs_improvement' | 'behind' | 'no_data'

// ==================== METRICS ANALYSIS ====================

export interface SpecialtyPerformance {
  name: string
  code: string
  questions: number
  correct: number
  accuracy: number
  sessions: number
  time_minutes: number
  status: 'excellent' | 'good' | 'needs_work' | 'critical' | 'no_data'
}

export interface StudyVolumeWeek {
  week_start: string
  week_label: string
  questions: number
  study_sessions: number
  revision_sessions: number
  total_sessions: number
}

export interface FsrsOverdueItem {
  topic_name: string
  specialty: string | null
  days_overdue: number
  retrievability: number
  priority_score: number
  last_studied_at: string | null
  latest_score: number | null
}

export interface FsrsStatus {
  total: number
  due: number
  overdue: number
  upcoming: number
  by_state: Record<string, number>
  top_overdue: FsrsOverdueItem[]
}

export interface ThemeAnalysisItem {
  theme: string
  institution: string
  weight: number
  readiness: number
  accuracy: number
  /**
   * RESOLUCAO — questoes resolvidas sobre as que a banca cobra no tema. Chamava-se
   * `coverage` ate o spec 04; e a definicao B, e nao a cobertura de
   * /app/analise-progresso (que conta REGISTRO de estudo). Ver `GuideTheme.resolution`.
   */
  resolution: number
  institution_questions: number
  user_questions: number
  priority: string
}

export interface MetricsAnalysis {
  specialty_performance: SpecialtyPerformance[]
  study_volume: StudyVolumeWeek[]
  fsrs_status: FsrsStatus
  institution_performance: Array<{
    rank: number
    name: string
    matched: boolean
    total_questions: number
    selection_process: string | null
  }>
  theme_analysis: {
    weak: ThemeAnalysisItem[]
    strong: ThemeAnalysisItem[]
    unstudied: ThemeAnalysisItem[]
  }
  overall_stats: {
    total_sessions: number
    total_studies: number
    total_revisions: number
    total_questions: number
    total_correct: number
    overall_accuracy: number
    current_streak: number
    longest_streak: number
    total_study_days: number
    selection_process: string | null
    sub_desired_speciality: string | null
    topics_tracked: number
  }
}

// ==================== STATISTICAL GUIDE ====================
//
// DOIS PUBLICOS, UMA SO FORMA (r03-09).
//
// `GET /dashboard/statistical_guide` (aluno) e
// `GET /instructor/students/:id/statistical_guide` (padrinho) devolvem o MESMO guia,
// so que o do aluno passa por uma projecao no backend que remove duas familias de
// chaves. Por isso os campos removidos sao opcionais aqui, e nao existem duas
// interfaces: a mesma arvore de componentes serve as duas telas, e um segundo tipo
// so duplicaria a manutencao e deixaria os dois espelhos divergirem em silencio.
//
// Cada campo opcional diz POR QUE e opcional, e as razoes sao duas, distintas:
//
//   "so no payload do padrinho (r03-09)"        -> contagem ABSOLUTA de questoes.
//                                                  O aluno le o percentual equivalente.
//   "prontidao saiu do guia do aluno (r03-09)"  -> PRONTIDAO e o que deriva dela.
//                                                  Segue viva na Jornada e na Analise
//                                                  de Progresso, e no guia do padrinho.
//
// Campo sem esse comentario e obrigatorio nos dois payloads.

export interface GuideTheme {
  theme: string
  weight: number
  /** Absoluto: so no payload do padrinho (r03-09). */
  institution_questions?: number
  /** Absoluto: so no payload do padrinho (r03-09). */
  user_questions?: number
  /** Absoluto: so no payload do padrinho (r03-09). */
  user_correct?: number
  /**
   * RESOLUCAO — `user_questions / institution_questions`, ou seja quanto do que a banca
   * cobra neste tema o aluno ja resolveu. E a metade de `readiness` que pesa 0.4
   * (`StudyJourneyService.readiness`).
   *
   * Chamava-se `coverage` ate o spec 04, e esse era o bug: convivia com a COBERTURA de
   * /app/analise-progresso, que conta REGISTRO de estudo primario e revisao contra os
   * topicos que a banca cobra. Dois numeros, o mesmo nome, telas vizinhas — nunca batiam.
   * A palavra "cobertura" agora tem uma definicao so, e e a outra.
   */
  resolution: number
  accuracy: number
  /** Prontidao saiu do guia do aluno (r03-09). */
  readiness?: number
  /** Deriva da prontidao: prontidao saiu do guia do aluno (r03-09). */
  priority?: string
  /** Deriva da prontidao: prontidao saiu do guia do aluno (r03-09). */
  status?: string
}

/**
 * Topico (`topic_id` -> `topics`) dentro de uma subespecialidade — o terceiro e ultimo
 * nivel da arvore do guia.
 *
 * Sai SEMPRE do catalogo, mesmo quando a subespecialidade vem da origem: a plataforma
 * de origem nao classifica nada abaixo da subespecialidade. Questao sem topico entra
 * como "Nao classificado" em vez de sumir — senao o nivel 3 nao somaria o nivel 2.
 */
export interface GuideTopic {
  topic: string
  /** Absoluto: so no payload do padrinho (r03-09). Use `percentage` no guia do aluno. */
  question_count?: number
  /** % do total do escopo (instituicao ou conjunto compartilhado). */
  percentage: number
  /** % dentro da propria subespecialidade. */
  subspecialty_percentage: number
  institution_count: number
  institutions: string[]
}

/** Subespecialidade (`sub_specialties`) dentro de uma especialidade do guia estatistico. */
export interface GuideSubspecialty {
  subspecialty: string
  /** Absoluto: so no payload do padrinho (r03-09). Use `percentage` no guia do aluno. */
  question_count?: number
  /** % do total do escopo (instituicao ou conjunto compartilhado). */
  percentage: number
  /** % dentro da propria especialidade. */
  specialty_percentage: number
  institution_count: number
  institutions: string[]
  /** Ja ordenado pelo backend (question_count DESC). Soma `question_count`. */
  topics: GuideTopic[]
}

/** Especialidade (`speciality`) com suas subespecialidades, ja ordenada pelo backend. */
export interface GuideSpecialtyNode {
  specialty: string
  /** Absoluto: so no payload do padrinho (r03-09). Use `percentage` no guia do aluno. */
  question_count?: number
  percentage: number
  institution_count: number
  institutions: string[]
  subspecialties: GuideSubspecialty[]
}

/** Especialidades presentes em 2+ instituicoes alvo. */
export interface SharedSpecialties {
  /** Absoluto: so no payload do padrinho (r03-09). */
  total_questions?: number
  institutions: string[]
  specialties: GuideSpecialtyNode[]
}

/**
 * COBERTURA (definicao A) de UMA banca, no guia — o mesmo objeto que
 * /app/analise-progresso le em `institutions[]`, produzido por
 * `Dashboard::CoverageSummary#institutions` e juntado por `institutions.id` no backend.
 *
 * `weighted` traz so `pct` por banca (`CoverageSummary::BLANK_WEIGHTED`): a contagem de
 * topicos ja esta em `absolute`, e repeti-la ponderada nao significaria nada.
 *
 * A unidade e TOPICO do catalogo, nao TEMA da prova: o card exibe este numero acima de
 * uma tabela de temas, e por isso o hint precisa dizer qual e qual.
 */
export interface GuideInstitutionCoverage {
  absolute: CoveragePoint
  weighted: { pct: number }
}

/**
 * COBERTURA (definicao A) somada, no resumo do guia — o numero grande do hero desde o
 * spec 04 (decisao 4.1: a PONDERADA lidera, porque um tema que cai em 12% da prova nao
 * vale o mesmo que um que cai em 0,3%).
 *
 * Vem agregada do backend de proposito: o hero somava `studied_themes`/`total_themes`
 * das bancas no navegador, o que era uma segunda implementacao do mesmo total, do lado
 * errado da rede — e sobre uma terceira acepcao de "cobertura", ja truncada em 30 temas.
 *
 * Numericamente IDENTICA a `ProgressAnalysisOverall.absolute_coverage` /
 * `weighted_coverage`: e o mesmo `Dashboard::CoverageSummary`, sem `period`.
 */
export interface GuideCoverageSummary {
  absolute: CoveragePoint
  weighted: GuideWeightedCoveragePoint
}

/**
 * `WeightedCoveragePoint` do guia. Mesma cobertura ponderada de
 * /app/analise-progresso — os PERCENTUAIS sao identicos, e continuam obrigatorios —,
 * so que as duas somas de PESO (que sao contagens de questoes) nao saem no payload do
 * aluno. Nao da para reusar `WeightedCoveragePoint` direto: la elas seguem obrigatorias,
 * porque a Analise de Progresso nao passa pela projecao do guia.
 */
export interface GuideWeightedCoveragePoint extends CoveragePoint {
  /** Absoluto: so no payload do padrinho (r03-09). O `pct` cobre o guia do aluno. */
  studied_weight?: number
  /** Absoluto: so no payload do padrinho (r03-09). O `pct` cobre o guia do aluno. */
  total_weight?: number
}

export interface InstitutionGuide {
  rank: number
  name: string
  matched_name: string
  /** Absoluto: so no payload do padrinho (r03-09). */
  total_questions?: number
  total_themes: number
  /** Definicao A por banca. Substituiu `studied_themes`/`coverage_percentage` no spec 04. */
  coverage: GuideInstitutionCoverage
  /** PRONTIDAO da banca (`StudyJourneyService`), 0-100. Prontidao saiu do guia do aluno (r03-09). */
  journey_score?: number
  /** Deriva da prontidao: prontidao saiu do guia do aluno (r03-09). */
  status?: string
  themes: GuideTheme[]
  specialties: Array<{
    specialty: string
    weight: number
    /** Absoluto: so no payload do padrinho (r03-09). */
    question_count?: number
  }>
  specialty_tree: GuideSpecialtyNode[]
  /** Lista inteira ordenada por prontidao: prontidao saiu do guia do aluno (r03-09). */
  top_gaps?: Array<{ theme: string; weight: number; readiness: number; priority: string }>
  /** As frases interpolam "sua prontidao e de N%": prontidao saiu do guia do aluno (r03-09). */
  recommendations?: string[]
}

/**
 * Uma linha de "Onde focar agora".
 *
 * O nivel e o TOPICO do catalogo (`topics`), nao mais o tema. Enquanto era tema, esta era
 * a unica parte da tela que falava o vocabulario da plataforma de ORIGEM, e o mesmo
 * assunto aparecia com nome e contagem diferentes deste card para os outros dois. No
 * topico os dois lados sao `topics.id`, entao nao ha vocabulario a conciliar.
 *
 * `sub_specialty` e o pai no catalogo — o contexto que permite ao aluno se localizar sem
 * perder a especificidade do topico. Pode ser null: `topics.sub_specialty_id` e nullable.
 */
export interface CrossInstitutionGap {
  topic: string
  topic_id: number
  sub_specialty: string | null
  /**
   * A especialidade (avo do topico no catalogo). Existe porque a MESMA lista alimenta
   * /app/jornada, que a usa para pre-preencher o formulario do Manual — ver
   * Journey::TopicGaps no backend. O card daqui nao renderiza este campo.
   */
  specialty: string | null
  institutions: string[]
  institution_count: number
  max_weight: number
  /** Prontidao saiu do guia do aluno (r03-09). */
  min_readiness?: number
  /** Absoluto: so no payload do padrinho (r03-09). Use `questions_pct` no guia do aluno. */
  total_questions?: number
  /**
   * Quanto o topico representa das questoes das bancas prioritarias em que ele cai —
   * `total_questions` sobre a soma das questoes dessas bancas, em 1 casa decimal.
   * E o substituto do absoluto no guia do aluno, e por isso obrigatorio nos DOIS
   * payloads. Denominador zero vira 0.0, nunca null.
   */
  questions_pct: number
  /**
   * Resolucao e acerto do aluno na banca em que o topico esta MAIS atrasado.
   *
   * `resolution` chamava-se `coverage` ate o spec 04 — e a definicao B (questoes
   * resolvidas sobre as que a banca cobra), nao a cobertura de /app/analise-progresso.
   */
  resolution: number
  accuracy: number
  /**
   * Prontidao ja acima do corte — o aluno domina o topico. Vem no payload em vez de ser
   * filtrado no backend porque o card tem um interruptor para exibi-los; escondido e o
   * padrao. Quando true, `urgency` nao tem sentido de urgencia e nao deve ser renderizada.
   *
   * Deriva da prontidao: prontidao saiu do guia do aluno (r03-09).
   */
  completed?: boolean
  /** Deriva da prontidao: prontidao saiu do guia do aluno (r03-09). */
  urgency?: 'critical' | 'high'
}

export interface TopicHeatmapItem {
  topic_name: string
  specialty: string | null
  /** Absoluto: so no payload do padrinho (r03-09). Use `questions_pct` no guia do aluno. */
  total_questions?: number
  /** Absoluto por banca: so no payload do padrinho (r03-09). Use `by_institution_pct`. */
  by_institution?: Record<string, number>
  /**
   * Quanto o topico representa das questoes do escopo (bancas + nivel) do aluno, em 1
   * casa decimal. Substitui `total_questions` no guia do aluno; vem nos DOIS payloads.
   */
  questions_pct: number
  /**
   * O mesmo percentual por banca: nome da banca -> quanto o topico representa das
   * questoes DAQUELA banca no escopo. Substitui `by_institution` no guia do aluno; vem
   * nos DOIS payloads. As chaves sao as mesmas de `by_institution`.
   */
  by_institution_pct: Record<string, number>
  fsrs: {
    retrievability: number
    days_overdue: number
    latest_score: number | null
    study_count: number
    revision_count: number
  } | null
}

export interface StatisticalGuide {
  selection_process: string | null
  sub_desired_speciality: string | null
  institutions: InstitutionGuide[]
  cross_institution_gaps: CrossInstitutionGap[]
  shared_specialties: SharedSpecialties
  topic_heatmap: TopicHeatmapItem[]
  summary: {
    institutions_matched: number
    institutions_total: number
    /**
     * PRONTIDAO combinada (media ponderada das bancas, 1a opcao pesando mais). Com o
     * spec 04 ela deixou de liderar o hero — quem lidera e `coverage.weighted.pct`; com
     * o r03-09 ela saiu de vez do guia do aluno, e so o padrinho continua recebendo.
     *
     * IDENTICA a `ProgressAnalysisOverall.readiness.combined_score`: as duas telas leem
     * o mesmo `Dashboard::ReadinessSummary`.
     *
     * Prontidao saiu do guia do aluno (r03-09) — quem continua exibindo o numero e
     * /app/analise-progresso, que le o mesmo `Dashboard::ReadinessSummary` intocado.
     */
    combined_score?: number
    /** Deriva da prontidao: prontidao saiu do guia do aluno (r03-09). */
    status?: string
    selection_process: string | null
    total_gaps: number
    /** Definicao A, agregada no backend. Vem zerada — nunca ausente — no resultado vazio. */
    coverage: GuideCoverageSummary
    message?: string
  }
}

// ==================== TOPIC REVISIONS (FSRS) ====================

export interface TopicRevision {
  id: number
  topic_id: number
  topic_name: string
  topic_slug: string
  specialty: string | null
  priority_score: number
  retrievability: number
  fsrs_due: string | null
  days_overdue: number
  fsrs_stability: number | null
  study_count: number
  revision_count: number
  latest_score: number | null
  average_score: number | null
  success_rate: number
  total_questions_attempted: number
  filtered_questions_current: number
  last_studied_at: string | null
  last_revision_at: string | null
}

// ==================== INTERACTIVE EXAMS ====================

/**
 * Como a tentativa esta sendo feita:
 * - `online`: o aluno responde dentro da plataforma (fluxo de sempre).
 * - `offline`: a prova foi impressa (PDF); o aluno registra as respostas depois.
 * Espelha a coluna `mode` de `exam_attempts` (default `online` no backend).
 */
export type ExamAttemptMode = 'online' | 'offline'

/** Uma resposta marcada no registro offline. `null` = questao deixada em branco. */
export interface RegisterAnswerInput {
  question_index: number
  selected_answer: string | null
}

/**
 * Corpo de `PATCH /exam_attempts/:id/register_answers`.
 * `time_spent_seconds` e o tempo TOTAL da prova; o backend o distribui
 * igualmente entre as questoes respondidas.
 */
export interface RegisterAnswersPayload {
  answers: RegisterAnswerInput[]
  time_spent_seconds: number
  /**
   * r7-09 — quantas vezes o aluno pausou o cronometro do gabarito. AUSENTE quando o
   * cronometro nao foi iniciado (tempo digitado a mao): o backend grava `null`, que e
   * diferente de `0` ("usou o cronometro e nao pausou").
   */
  pause_count?: number
}

/**
 * Corpo de `POST /exam_attempts/:id/register_study`.
 * E o SEGUNDO tempo que o aluno digita na prova impressa: nao o tempo de prova
 * (esse ja foi em `RegisterAnswersPayload`), e sim quanto ele levou conferindo o
 * gabarito depois. Sao dois numeros distintos porque o MedHunter Manual pede os
 * dois separados no simulado. Segundos aqui, horas no banco — a conversao e do
 * backend, que tambem recusa acima de 86.400 (24h).
 */
export interface RegisterStudyPayload {
  /** r7-11: opcional. Ausente = nao informado (o backend grava nulo, nao zero). */
  correction_time_seconds?: number
}

/**
 * Uma linha por especialidade do simulado gerado a partir da prova impressa —
 * espelha `test_and_simulation_specialty_results`.
 *
 * `number_of_studied_questions` conta as questoes cujo TOPICO o aluno ja tinha
 * registrado antes (aula, estudo primario ou revisao); e por isso que
 * `number_of_hits_in_studied_questions` e a intersecao com os acertos — e o que
 * mede acerto no conteudo que ele de fato estudou.
 *
 * Tudo e anulavel porque a linha vem do mesmo modelo do preenchimento manual,
 * onde o aluno pode deixar campo em branco.
 */
export interface ExamStudySpecialtyResult {
  specialty_id: number | null
  specialty_code: string
  specialty_name: string
  number_of_questions: number | null
  number_of_hits: number | null
  number_of_studied_questions: number | null
  number_of_hits_in_studied_questions: number | null
  performance: number | null
}

/**
 * O simulado criado automaticamente ao fim da conferencia da prova impressa.
 * E um `TestAndSimulation` de verdade: aparece na tela de Simulados / MedHunter
 * Manual junto com os que o aluno digitou a mao, indistinguivel deles.
 *
 * ATENCAO A UNIDADE: `test_ted` (tempo de prova) e `test_correction` (tempo de
 * correcao) sao HORAS, nao segundos nem minutos — e a unidade que o formulario
 * manual usa ("Tempo de Prova (horas)", step 0.5). Formatar como segundos mostra
 * numeros absurdos.
 *
 * Os dois contadores no fim existem para o numero nao sumir em silencio, ja que a
 * classificacao do catalogo e parcial:
 * - `unclassified_questions`: questoes sem especialidade — nao entram em nenhuma
 *   linha de `specialty_results`, logo somem do total se ninguem as mostrar.
 * - `questions_without_topic`: questoes sem topico — contam em
 *   `number_of_questions`, mas nunca podem contar como estudadas, porque nao ha
 *   topico para comparar com o historico do aluno.
 */
export interface ExamStudyRegistration {
  id: number
  test_name: string | null
  test_simulation_date: string | null
  test_year: string | null
  test_ted: number | null
  test_correction: number | null
  total_performance: number | null
  specialty_results: ExamStudySpecialtyResult[]
  unclassified_questions: number
  questions_without_topic: number
}

/** Sem `platform` desde a r6-08: o aluno nao ve a origem (Medway/MedCof) da prova. */
export interface AvailableExam {
  id: number
  exam_type: string
  name: string
  institution: string
  institution_state: string | null
  year: number | null
  exam_kind: string | null
  question_count: number
  thumbnail_url: string | null
  has_comments: boolean
  global_average?: number | null
  times_made?: number | null
  user_attempt: {
    id: string
    status: 'in_progress' | 'completed' | 'abandoned'
    mode: ExamAttemptMode
    score: number | null
    progress: number
    answered_count: number
    correct_count: number
    /**
     * Mesma copia manual do campo de `ExamAttemptData`, pelo mesmo motivo: o card
     * mostra o aproveitamento e a nota lado a lado, e com anuladas fora de
     * `correct_count` os dois numeros se contradiriam.
     */
    annulled_count?: number
    time_spent: number
    started_at: string | null
    completed_at: string | null
    /**
     * Tempo da conferencia, em SEGUNDOS (diferente de `test_correction` do simulado,
     * que e em horas). `null` enquanto a prova impressa nao foi corrigida.
     */
    correction_time_seconds?: number | null
    /**
     * Simulado gerado pela conferencia desta prova. E o que o card usa para decidir
     * entre oferecer "Registrar Estudo" e nao oferecer nada: a prova impressa ja
     * finalizada mas ainda SEM simulado e o aluno que salvou o gabarito e fechou o
     * modal antes do fim — sem este campo ele ficaria sem caminho de volta.
     */
    test_and_simulation_id?: number | null
  } | null
}

export interface ExamQuestion {
  id: number
  index: number
  /**
   * Numeracao da BANCA — a fonte unica de numeracao da prova (papel, folha de
   * gabarito, registro e revisao pos-prova). E NULLABLE de verdade: a coluna
   * permite NULL e o controller a repassa como veio.
   *
   * Nunca renderize direto, e nao desambigue questao a questao: o numero da banca
   * pode VIR REPETIDO na mesma prova (ha casos reais em producao), e duas linhas
   * com o mesmo numero na folha de gabarito fazem o aluno marcar a linha errada.
   * Use `questionLabels(questions)`, que ve a lista inteira e devolve rotulos ja
   * desambiguados (`3a`/`3b` para repetido, `8.1` para questao sem numero).
   */
  question_number: number | null
  statement: string | null
  content: string | null
  /**
   * R11-01: `images` da alternativa (figura so dela, sempre desenhada abaixo do texto).
   * Opcional: so a API nova manda.
   */
  options: Array<{ letter: string; content: string; images?: unknown[] }>
  difficulty: string | null
  difficulty_label: string | null
  study_theme: string | null
  speciality: string | null
  year: number | string | null
  institution_name: string | null
  images: unknown[] | null
  flagged: boolean
  selected_answer: string | null
  /**
   * Questao anulada pela banca. Chega em DOIS payloads apenas: o da impressao
   * (`print`, para a folha de gabarito sinalizar) e o do resultado/correcao
   * (`serialize_question_with_answer`). O player (`start`) NAO traz o campo, de
   * proposito — o aluno so descobre a anulacao na correcao. Por isso e opcional:
   * ausente significa "nao ha essa informacao aqui", nao "nao anulada".
   *
   * R11-03: e "fora da nota" — vem `true` tambem para a questao DESATUALIZADA, que entra
   * na prova e sai da correcao como a anulada. `outdated` so muda o rotulo.
   */
  canceled?: boolean
  /** R11-03: questao desatualizada (rotulo "Desatualizada"). Mesmos payloads de `canceled`. */
  outdated?: boolean
  // Only in results
  correct_answer?: string | null
  is_correct?: boolean
  time_spent_seconds?: number
  comment?: string | null
  success_rate?: number | null
  response_stats?: Record<string, unknown> | null
}

export interface ExamAttemptData {
  id: string
  exam_type: string
  exam_id: number
  status: 'in_progress' | 'completed' | 'abandoned'
  mode: ExamAttemptMode
  total_questions: number
  answered_count: number
  correct_count?: number
  /**
   * Quantas das `total_questions` foram ANULADAS pela banca. `total_questions`
   * continua sendo o total IMPRESSO (o aluno respondeu todas); quem encolhe e o
   * denominador da nota: `score = correct_count / (total_questions - annulled_count)`.
   * `correct_count` ja vem sem as anuladas.
   *
   * Opcional porque a prova em andamento nao tem contagem nenhuma ainda: leia
   * sempre com `annulledCountOf`, que normaliza para 0.
   */
  annulled_count?: number
  score?: number | null
  current_question_index: number
  time_spent_seconds: number
  /**
   * Tempo da CONFERENCIA do gabarito (prova impressa), separado de
   * `time_spent_seconds`, que e o tempo de prova. Nulo enquanto o aluno nao
   * concluiu a conferencia OU quando registrou o estudo sem informa-lo (opcional desde a
   * r7-11) — o sinal de passo pendente e `test_and_simulation_id` nulo, nao este campo.
   */
  correction_time_seconds?: number | null
  /**
   * r7-09 — pausas do cronometro do gabarito (prova impressa). `null` = cronometro nao
   * usado; so vem no resultado da tentativa finalizada.
   */
  pause_count?: number | null
  /**
   * Simulado do MedHunter Manual gerado por esta tentativa. Preenchido significa
   * "estudo ja registrado": use para nao oferecer o registro duas vezes.
   */
  test_and_simulation_id?: number | null
  started_at: string | null
  completed_at?: string | null
  exam_info: {
    name: string
    institution: string
    institution_state: string | null
    year: number | null
    exam_kind: string | null
    thumbnail_url: string | null
    global_average?: number | null
    times_made?: number | null
  }
  questions: ExamQuestion[]
  answers?: Array<{
    question_index: number
    selected_answer: string | null
    flagged: boolean
    time_spent_seconds: number
  }>
  /**
   * Indice de retencao DESTA prova (spec r2/01). Espelho manual de
   * `ExamAttempts::ResultStats#retention`, que chama a MESMA instancia de servico da aba
   * "Por Prova" (`Dashboard::ExamMetrics::RetentionIndex`, curva FSRS-4.5) — e nao a
   * curva legada `#retrievability` da aba "Por Especialidade". Os dois numeros divergem
   * de proposito; ha teste de backend exigindo que este bata com o daquela aba.
   *
   * `null` na tentativa EM ANDAMENTO: o serializer da prova em curso emite a chave com
   * valor nulo (e nao a omite) para o cliente nunca ler `undefined` onde o tipo promete
   * o objeto. `retention === null` e "ainda nao ha retencao desta tentativa";
   * `retention.index === null` e "NAO MENSURAVEL" — sao coisas diferentes.
   */
  retention?: ExamAttemptRetention | null
  stats?: {
    /**
     * As quatro chaves antigas nao mudaram de valor nem de denominador (questoes
     * validas da especialidade, anuladas fora). O que entrou e a SEGUNDA leitura da
     * mesma barra — "das que eu ja tinha estudado, quantas acertei".
     */
    by_specialty: Array<{
      name: string
      total: number
      correct: number
      rate: number
      /** Das `total`, as cujo TOPICO o aluno ja tinha registrado como estudo. */
      studied_questions: number
      /** Das `studied_questions`, as que ele acertou. Sempre <= `studied_questions`. */
      hits_in_studied_questions: number
    }>
    by_theme: Array<{ name: string; total: number; correct: number; rate: number }>
    /**
     * Eixo NOVO, por topico, ordenado pela prevalencia nas bancas prioritarias do aluno.
     * Nao substitui `by_theme`, que agrupa por subespecialidade: sao niveis diferentes
     * da arvore. Sem corte de "top N" — o fim da lista e justamente onde ficam os
     * topicos sem prevalencia registrada.
     */
    by_prevalent_topic: ExamAttemptPrevalentTopic[]
    /**
     * Os contadores que explicam o que nao coube nas barras. Somados aos `total` de
     * `by_specialty`, fecham `total_questions` — `unclassified_questions`,
     * `annulled_questions` e `questions_without_answer_slot` sao as tres parcelas do
     * fechamento; `questions_without_topic` e um recorte DENTRO das barras (questao que
     * entrou numa linha mas nao pode contar como estudada) e por isso NAO entra na soma.
     */
    counters: {
      /** Questoes validas sem especialidade no catalogo — nao ha barra para recebe-las. */
      unclassified_questions: number
      /** Questoes que entraram numa barra mas nao tem topico: nunca contam como estudadas. */
      questions_without_topic: number
      /** Questoes anuladas pela banca: fora da nota e fora de todas as barras. */
      annulled_questions: number
      /**
       * Questoes do total IMPRESSO sem slot de resposta gravado nesta tentativa. Quase
       * sempre 0; e o que faz a soma fechar em tentativa antiga ou importada, cujo
       * `total_questions` e maior que o numero de linhas de `exam_answers`.
       */
      questions_without_answer_slot: number
    }
  }
  /**
   * Videos de acompanhamento liberados pela nota desta prova. So metadado — a URL
   * assinada sai de `GET /followup_videos/:id/playback`, no clique do play, para
   * nao nascer contando os 5 minutos de validade. Ausente em prova em andamento.
   */
  followup_videos?: FollowupVideoTeaser[]
}

/**
 * Bloco `retention` da tela de nota. As cinco chaves sao as mesmas de `ExamMetricRow`
 * (`retention_index`, `retention_topics_*`, `questions_without_topic`) sem o prefixo,
 * porque aqui elas ja vivem dentro de um objeto proprio — e sao produzidas pelo mesmo
 * servico, o que o teste de paridade do backend garante.
 */
export interface ExamAttemptRetention {
  /** 0..100 com uma casa. `null` = NAO MENSURAVEL, nunca 0 no lugar de "sem dado". */
  index: number | null
  /** Temas da prova com progresso do aluno — o numerador do "medido sobre N de M". */
  topics_measured: number
  /** Temas distintos cobrados pela prova. `0` significa que nao havia o que medir. */
  topics_total: number
  /** Questoes que entraram no DENOMINADOR do indice (as dos temas mensuraveis). */
  questions_measured: number
  /** Questoes validas sem tema no catalogo. Contador DISJUNTO dos dois acima. */
  questions_without_topic: number
}

/** Uma linha da leitura por topico prevalente da tela de nota. */
export interface ExamAttemptPrevalentTopic {
  topic_id: number
  name: string
  /** Questoes validas do topico NESTA prova. */
  total: number
  correct: number
  /** 0..100 com uma casa, sobre `total`. */
  rate: number
  /**
   * Quantas questoes deste topico as bancas prioritarias do aluno cobraram na janela
   * medida. `null` = nunca cobraram (ou o universo de provas nao pode ser resolvido) —
   * NAO e zero, que seria uma afirmacao sobre a banca que o recorte nao autoriza.
   * Linha com `null` vai para o fim da lista e continua aparecendo.
   */
  prevalence_questions: number | null
  /** Fatia percentual do topico nessa mesma janela, duas casas. `null` junto com o acima. */
  prevalence_share: number | null
}

/** Cartao do video na tela de resultado, antes de qualquer URL ser gerada. */
export interface FollowupVideoTeaser {
  id: number
  title: string
  description: string | null
  duration_seconds: number | null
  condition_label: string
}

/** Uma qualidade transcodificada, ja com a URL assinada de 5 minutos. */
export interface FollowupVideoSource {
  label: string
  width: number | null
  height: number | null
  content_type: string
  url: string
}

/** Resposta de `GET /followup_videos/:id/playback`. */
export interface FollowupVideoPlayback {
  id: number
  title: string
  description: string | null
  duration_seconds: number | null
  /** Maior qualidade gerada; e por ela que o player comeca. */
  default_label: string | null
  poster_url: string | null
  sources: FollowupVideoSource[]
  expires_in: number
  expires_at: string
}

// ---------- Video em contexto (spec 05) ----------
// Espelho manual de medhunter-api/app/services/followup_videos/recommendation_payload.rb
// (as oito chaves) e do FollowupVideosController do aluno. Nao ha OpenAPI entre os repos:
// divergir daquele arquivo quebra a SPA em silencio.

/**
 * Estado do aluno diante de um video, em `user_followup_videos`.
 *
 *   pending  ja foi oferecido e ele nao resolveu — e o que alimenta o banner
 *   seen     assistido; sai do contexto e do banner, e e TERMINAL (pular depois nao volta)
 *   skipped  pulado; sai do banner mas PERMANECE no contexto, na forma discreta
 */
export type FollowupVideoUserStatus = 'pending' | 'seen' | 'skipped'

/** Os cinco contextos que o backend aceita em `Recommend::CONTEXTS`. */
export type FollowupVideoContext =
  | 'exam'
  | 'journey'
  | 'manual'
  | 'onboarding'
  | 'difficulty_report'

/**
 * O video recomendado num contexto. E o `FollowupVideoTeaser` da prova mais o estado do
 * aluno — por isso o painel de contexto atende os dois: o teaser sem `user_status` e lido
 * como `pending`.
 */
export interface ContextFollowupVideo extends FollowupVideoTeaser {
  user_status: FollowupVideoUserStatus
  seen_at: string | null
  skipped_at: string | null
}

/** Resposta de `GET /followup_videos/recommendations`. */
export interface FollowupRecommendationsResponse {
  success: boolean
  context: FollowupVideoContext
  videos: ContextFollowupVideo[]
}

/** Resposta de `POST /followup_videos/:id/seen` e `/skip`. */
export interface FollowupVideoStateResponse {
  success: boolean
  video: {
    id: number
    user_status: FollowupVideoUserStatus
    seen_at: string | null
    skipped_at: string | null
  }
}

/** Uma linha do banner (R5). `subject` e o "XXX" de "voce tem video sobre XXX". */
export interface PendingFollowupVideo {
  id: number
  title: string
  duration_seconds: number | null
  subject: string
  context: FollowupVideoContext
}

/** Resposta de `GET /followup_videos/pending`. */
export interface FollowupPendingResponse {
  success: boolean
  count: number
  videos: PendingFollowupVideo[]
}

// ---------- Formulario recorrente de dificuldades (spec 05, R4) ----------

/** Uma das nove dificuldades fechadas (mesmo vocabulario da pergunta 24 do onboarding). */
export interface DifficultyReportOption {
  value: string
  label: string
}

/**
 * O relato do mes. Duas metades que nao se misturam: a SELECAO (`topics` + `difficulties`),
 * que casa video por intersecao de listas fechadas, e `notes`, texto livre que existe so
 * para leitura humana no admin — ele nunca alcanca casamento nenhum.
 */
export interface DifficultyReport {
  id: number
  /** Primeiro dia do mes de referencia (`2026-08-01`), nao a data do envio. */
  reference_month: string
  submitted_at: string | null
  topic_ids: number[]
  topics: Array<{ id: number; name: string }>
  difficulties: string[]
  notes: string | null
}

/**
 * Resposta de `GET /difficulty_reports/current`.
 *
 * Quem decide se o formulario aparece e `available` — NUNCA `report`: o backend devolve o
 * relato do mes tambem fora da janela, para o aluno rever o que enviou.
 */
export interface DifficultyReportWindowResponse {
  success: boolean
  available: boolean
  reference_month: string
  opens_on: string
  closes_on: string
  submitted: boolean
  report: DifficultyReport | null
  options: { difficulties: DifficultyReportOption[] }
}

export interface DifficultyReportPayload {
  topic_ids?: number[]
  difficulties?: string[]
  notes?: string
}

/** Resposta de `POST /difficulty_reports` (201 no primeiro envio do mes, 200 no reenvio). */
export interface CreateDifficultyReportResponse {
  success: boolean
  message: string
  report: DifficultyReport
  /** Ja casados pelo relato que acabou de ser gravado — nascem `pending`. */
  videos: ContextFollowupVideo[]
}

export interface ExamAttemptSummary {
  id: string
  /**
   * A PK numerica da tentativa, ao lado do `hash_id` que `id` carrega (spec r2/02).
   *
   * O comparativo entre provas enderecca a tentativa pelo token
   * `"exam_attempt:<numeric_id>"`, que e a forma que `ExamMetricRow.id` publica —
   * sem este campo, o botao "Comparar" desta tela e o atalho da aba "Por Prova"
   * gerariam URLs DIFERENTES para o mesmo grafico. Opcional porque o campo e
   * novo: um bundle servido contra uma API anterior nao o recebe.
   */
  numeric_id?: number
  exam_type: string
  exam_id: number
  status: 'in_progress' | 'completed' | 'abandoned'
  mode: ExamAttemptMode
  total_questions: number
  answered_count: number
  correct_count: number
  /**
   * Copia deliberada do campo de `ExamAttemptData` (este tipo e uma duplicata
   * manual daquele, nao uma extensao): sem ela a tabela de historico calcularia o
   * aproveitamento por um denominador diferente do da tela de resultado, e nada
   * no build acusaria.
   */
  annulled_count?: number
  score: number | null
  time_spent_seconds: number
  started_at: string | null
  completed_at: string | null
  exam_info: {
    name: string
    institution: string
    institution_state: string | null
    year: number | null
    exam_kind: string | null
    thumbnail_url: string | null
  } | null
}

// Onboarding (formulario de entrada 2.0) - ver docs/onboarding-spec.md secao 5
// Envelope da API: { success: true, ...dados } — nao existe chave `data`.
export interface OnboardingResponse {
  success: boolean
  onboarding: OnboardingState
}

export interface SaveOnboardingRequest {
  // Ambos opcionais: autosave de digitacao nao muda o passo, troca de passo pode nao ter resposta nova
  current_step?: number
  answers?: OnboardingAnswersPatch
}

export interface CompleteOnboardingResponse {
  success: boolean
  message: string
  onboarding: OnboardingState
  user: User
}

// Corpo do 422 de `POST /onboarding/complete`. `invalid_questions` e informativo:
// o ApiError do frontend so carrega message/status/code.
export interface CompleteOnboardingErrorResponse {
  success: false
  error: string
  invalid_questions?: QuestionId[]
}

// ==================== PRIMEIRA CARGA DE DADOS ====================
// Espelho manual de Api::V1::InitialDataLoadController. A arvore sai do catalogo
// (o mesmo do autocomplete do MedHunter manual), um nivel por tabela:
// `specialties` -> `sub_specialties` -> `topics`.

/**
 * `pending` e o unico estado que faz o convite pos-onboarding aparecer.
 * `dismissed` nao e terminal: Configuracoes continua oferecendo a carga.
 */
export type InitialDataLoadStatus = 'pending' | 'completed' | 'dismissed'

export interface InitialDataLoadState {
  status: InitialDataLoadStatus
  completed_at: string | null
}

/**
 * Folha da arvore e a UNICA coisa que vira registro: o `id` e o `hash_id` do verbete
 * em `topics` e e ele que o POST envia. `already_registered` marca o topico
 * que o usuario ja tem em Estudos Primarios — a carga o ignoraria de qualquer forma
 * (e idempotente), entao a tela o mostra marcado e travado em vez de deixar o usuario
 * reenviar sem entender por que o contador veio menor.
 */
export interface InitialDataLoadTopic {
  id: string
  name: string
  level: 'topic'
  already_registered: boolean
}

export interface InitialDataLoadSubSpecialty {
  id: string
  name: string
  level: 'sub_specialty'
  topics_count: number
  children: InitialDataLoadTopic[]
}

export interface InitialDataLoadSpecialty {
  id: string
  name: string
  level: 'specialty'
  topics_count: number
  children: InitialDataLoadSubSpecialty[]
}

export interface InitialDataLoadStateResponse {
  success: boolean
  initial_data_load: InitialDataLoadState
}

export interface InitialDataLoadTotals {
  specialties: number
  sub_specialties: number
  topics: number
}

export interface InitialDataLoadTreeResponse extends InitialDataLoadStateResponse {
  tree: InitialDataLoadSpecialty[]
  totals: InitialDataLoadTotals
}

export interface InitialDataLoadCreateResponse extends InitialDataLoadStateResponse {
  created: number
  skipped: number
  message: string
}

export interface InitialDataLoadDismissResponse extends InitialDataLoadStateResponse {
  message: string
}

// ==================== ADMIN: CATALOGO DE ESTUDO ====================
// Espelho manual de Api::V1::Admin::PrepCoursesController#serialize e
// Api::V1::Admin::CourseLessonsController#serialize. Endpoints restritos a admin
// (authorize_admin! no Api::V1::Admin::BaseController devolve 403 para os demais).

export interface AdminPrepCourse {
  id: number
  name: string
  full_name: string
  slug: string
  description: string | null
  logo_url: string | null
  active: boolean
  lessons_count: number
  created_at: string
  updated_at: string
  /** So vem no #show: { 'CLM' => 180, 'CIR' => 239, ... } */
  lessons_by_specialty?: Record<string, number>
}

export interface AdminPrepCoursePayload {
  name: string
  full_name: string
  slug?: string
  description?: string | null
  logo_url?: string | null
  active?: boolean
}

// ---------- Videos de acompanhamento (admin) ----------
// Ver medhunter-api/docs/followup-videos.md para o fluxo completo.

/**
 * Ciclo de vida do arquivo. O upload so faz a ingestao do bruto; o encode
 * acontece depois, em job assincrono.
 *
 *   pending    na fila de processamento
 *   processing encode rodando
 *   ready      unico status visivel ao aluno
 *   failed     `processing_error` diz o motivo; da para reprocessar
 */
export type AdminFollowupVideoStatus = 'pending' | 'processing' | 'ready' | 'failed'

/**
 * O discriminador da condicao (`FollowupVideo::CONDITION_TYPES`). UMA por video — um
 * video que precise aparecer em dois contextos vira duas linhas (decisao 4.1).
 *
 *   exam_score_range   a nota da prova cai em [min_score, max_score]
 *   onboarding_answer  a resposta do formulario de entrada bate
 *   topic              o video e sobre um topico do catalogo
 *   difficulty_report  o relato mensal cita o topico ou a dificuldade
 */
export type AdminFollowupVideoConditionType =
  | 'exam_score_range'
  | 'onboarding_answer'
  | 'topic'
  | 'difficulty_report'

/**
 * Os parametros da condicao, na forma canonica que o backend normaliza.
 *
 * `topic_ids` e `topics.id` — o catalogo do produto, o mesmo id que a jornada trafega em
 * `prefill.topic_id` e que o admin edita em /admin/topics. NAO e `canonical_topics.id`
 * (o dicionario do FSRS): trocar um pelo outro nao da erro de tipo em lugar nenhum, so
 * zero casamento em silencio.
 */
export interface AdminFollowupVideoConditionConfig {
  /** `onboarding_answer`: o id da pergunta (`journey_stage`, `main_difficulties`, ...). */
  question_id?: string
  /** `onboarding_answer`: as respostas que casam. Basta uma. */
  values?: string[]
  /** `topic` e `difficulty_report`: ids de `topics`. */
  topic_ids?: number[]
  /** `difficulty_report`: valores da lista fechada de dificuldades. */
  difficulties?: string[]
}

/** Uma qualidade gerada pelo encode (`480p`, `720p`, ...). */
export interface AdminFollowupVideoRendition {
  id: number
  label: string
  width: number | null
  height: number | null
  bitrate_kbps: number | null
  file_size: number | null
}

export interface AdminFollowupVideo {
  id: number
  title: string
  description: string | null
  original_filename: string | null
  content_type: string | null
  file_size: number | null
  /** Preenchidos pelo ffprobe durante o processamento — nulos enquanto pending. */
  duration_seconds: number | null
  source_width: number | null
  source_height: number | null
  has_thumbnail: boolean
  status: AdminFollowupVideoStatus
  processing_error: string | null
  processing_attempts: number
  processed_at: string | null
  renditions: AdminFollowupVideoRendition[]
  condition_type: AdminFollowupVideoConditionType
  /** Texto pronto para exibicao, ex.: "Acerto entre 0% e 40%", "Topico: Arritmias". */
  condition_label: string
  /**
   * Parametros da condicao, ja PARSEADOS pelo servidor. Vem `null` para
   * `exam_score_range`, cujos parametros sao as colunas `min_score`/`max_score`.
   *
   * Atencao a assimetria com `AdminFollowupVideoPayload.condition_config`, que e String:
   * a leitura e objeto, a escrita e JSON serializado. Ver o docstring daquele campo.
   */
  condition_config: AdminFollowupVideoConditionConfig | null
  min_score: number | null
  max_score: number | null
  priority: number
  active: boolean
  created_at: string
  updated_at: string
}

/**
 * Resposta de `GET /admin/followup_videos/:id/preview_url`.
 *
 * `url` e a melhor rendition (ou o arquivo bruto, com `?source=true`) e
 * `sources` traz todas as qualidades para o seletor do player. Antes do
 * transcode terminar, `sources` tem uma entrada so, rotulada `Original`.
 */
export interface AdminFollowupVideoPreview {
  url: string
  s3_key: string
  sources: FollowupVideoSource[]
  expires_in: number
  expires_at: string
}

/**
 * Campos do formulario. O arquivo NAO entra aqui: `create`/`update` sao
 * multipart, com o video no campo `file` e estes campos em `followup_video[...]`.
 */
export interface AdminFollowupVideoPayload {
  title: string
  description?: string | null
  condition_type?: AdminFollowupVideoConditionType
  /**
   * JSON **serializado** (`JSON.stringify`), nao objeto — e a assimetria deliberada com a
   * leitura, que volta parseada em `AdminFollowupVideo.condition_config`.
   *
   * `create`/`update` sao multipart e `buildFollowupVideoForm` faz `String(value)` em cada
   * entrada: um objeto viraria `'[object Object]'` e um array `'1,2'`. O multipart seria
   * ACEITO, o registro salvaria com lixo e so o casamento nao aconteceria — falha
   * silenciosa. O backend permite o campo como escalar String e valida o JSON na fronteira.
   *
   * Em PATCH o campo e SEMPRE reenviado quando o tipo o usa: omitir apagaria a condicao.
   */
  condition_config?: string
  min_score?: number | string
  max_score?: number | string
  priority?: number | string
  active?: boolean
}

// ---------- Videoaulas de curso (spec r4/12) ----------
// Espelho de `VideoCourses::AdminPayload` (medhunter-api). NAO e o `Course` legado do
// marketplace nem o `CourseLesson` do catalogo dos cursinhos.

/** R2: a aula bloqueada aparece opaca (`show_all`) ou nem e listada (`hide_locked`). */
export type VideoCourseDisplayMode = 'show_all' | 'hide_locked'

/** Reuniao Periodica e um VideoCourse com outro `kind`; herda aulas, planos e o resto. */
export type VideoCourseKind = 'module' | 'periodic_meeting'

/**
 * R3: o tipo da condicao de liberacao. `performance_score` esta no enum (proxima fase)
 * mas o backend recusa grava-lo — a tela nao o oferece.
 */
export type VideoLessonUnlockKind =
  | 'lesson_watched'
  | 'onboarding_answer'
  | 'periodic_form_answer'
  | 'performance_score'

/** Os parametros da condicao, por tipo. Objeto JSON de verdade (a rota nao e multipart). */
export interface VideoLessonUnlockParams {
  /** `lesson_watched`: a aula que precisa ter sido aberta. */
  lesson_id?: number
  /** `onboarding_answer` / `periodic_form_answer`: a pergunta de lista fechada. */
  question_id?: string
  /** Idem: as respostas que liberam. Basta uma. */
  values?: string[]
}

export interface AdminVideoLesson {
  id: number
  video_course_id: number
  title: string
  description: string | null
  /** O link do Vimeo como o admin colou. */
  video_url: string
  vimeo_id: string
  /** URL do player. So o admin a recebe sem condicao — o aluno, apenas quando liberada (R5). */
  embed_url: string
  position: number
  unlock_kind: VideoLessonUnlockKind | null
  unlock_params: VideoLessonUnlockParams
  /** Texto pronto, ex.: "Liberada", "Depois de assistir: Boas-vindas". */
  unlock_label: string
  active: boolean
  /** MH14-04: entra no pop-up em sequencia da primeira entrada do aluno. */
  first_access_required: boolean
  created_at: string
  updated_at: string
}

export interface AdminVideoCourse {
  id: number
  kind: VideoCourseKind
  title: string
  description: string | null
  /** URL assinada (1 h) ou null sem thumb. */
  thumbnail_url: string | null
  display_mode: VideoCourseDisplayMode
  /** R6: o curso oferecido no fim do Formulario de Entrada. No maximo um. */
  onboarding: boolean
  /**
   * PRÉ-REQUISITO: o curso só abre para quem já concluiu este outro — assistiu todas as
   * aulas dele que estão disponíveis para ele. `null` = curso aberto.
   */
  required_video_course_id: number | null
  required_video_course_title: string | null
  active: boolean
  position: number
  /** R2 "assinatura necessaria": vazio = todos os alunos. */
  stripe_plan_ids: number[]
  stripe_plan_names: string[]
  lessons_count: number
  /** So em `GET /admin/video_courses/:id` e nas respostas de escrita. */
  lessons?: AdminVideoLesson[]
  created_at: string
  updated_at: string
}

/** Um plano de assinatura, para o select do curso. */
export interface AdminVideoCoursePlan {
  id: number
  name: string
  active: boolean
}

/** Campos do formulario de curso. A thumb vai a parte, no campo `thumbnail` do multipart. */
export interface AdminVideoCoursePayload {
  title: string
  description?: string | null
  display_mode?: VideoCourseDisplayMode
  onboarding?: boolean
  /** `null` remove o pré-requisito (curso aberto). */
  required_video_course_id?: number | null
  active?: boolean
  position?: number
  /** Substituicao da lista inteira; vazia volta o curso a "todos os alunos". */
  stripe_plan_ids?: number[]
}

/** `kind` so vale na criacao: o PATCH o ignora, entao o tipo de edicao nem o aceita. */
export interface AdminVideoCourseCreatePayload extends AdminVideoCoursePayload {
  kind: VideoCourseKind
}

export interface AdminVideoLessonPayload {
  video_course_id?: number
  title: string
  description?: string | null
  video_url: string
  position?: number
  /** `null` = sem condicao (liberada). */
  unlock_kind: VideoLessonUnlockKind | null
  unlock_params: VideoLessonUnlockParams
  active?: boolean
  first_access_required?: boolean
}

/**
 * Uma linha de `GET /admin/difficulty_reports` — a tela onde o texto livre do aluno e
 * lido por um humano. Somente leitura: nao ha update nem destroy, porque o relato e
 * registro do aluno.
 */
export interface AdminDifficultyReport {
  id: number
  reference_month: string
  submitted_at: string | null
  user: { id: number; name: string; email: string } | null
  topics: Array<{ id: number; name: string }>
  /** Ja com rotulo ("Retenção de conteúdo"), nao o valor cru. */
  difficulties: DifficultyReportOption[]
  /** VERBATIM, sem truncar. Renderize como texto — nunca com `dangerouslySetInnerHTML`. */
  notes: string | null
}

export interface AdminDifficultyReportsResponse {
  success: boolean
  difficulty_reports: AdminDifficultyReport[]
  /** Paginado de verdade: sem `page`, o relato de numero 101 era inalcancavel. */
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
  }
  /** `total` e o numero GLOBAL; `filtered` descreve a listagem que a tela esta vendo. */
  stats: { total: number; filtered: number; months: string[] }
}

export interface AdminCourseLesson {
  id: number
  prep_course_id: number
  prep_course_name: string | null
  specialty_id: number
  specialty_code: string | null
  specialty_name: string | null
  lesson_code: string
  lesson_title: string
  slug: string | null
  description: string | null
  learning_objectives: string | null
  video_url: string | null
  estimated_duration_minutes: number | null
  difficulty_level: string | null
  tags: string | null
  display_order: number
  module_name: string | null
  module_order: number | null
  academic_year: number
  /** R08-20: 'YYYY-MM-DD' em que o cursinho libera a aula; null = desde o inicio do ano. */
  release_on: string | null
  /**
   * Vinculos com o catalogo. A Especialidade e uma so (`specialty_id`, acima); estes
   * dois sao N e vem das tabelas `sub_specialties` e `topics`.
   */
  sub_specialties: AdminCourseLessonSubSpecialty[]
  topics: AdminCourseLessonTopic[]
  sub_specialties_count: number
  topics_count: number
  active: boolean
  created_at: string
  updated_at: string
}

/**
 * Subespecialidade como a aula a devolve — so o necessario para exibir e remontar.
 *
 * Desde que cada nivel virou tabela propria, o ponteiro para cima tem nome proprio em
 * cada um (`specialty_id` aqui, `sub_specialty_id` no topico) em vez do `parent_id`
 * polimorfico unico que a arvore antiga usava.
 */
export interface AdminCourseLessonSubSpecialty {
  id: number
  name: string
  specialty_id: number | null
}

/** Topico como a aula o devolve; `sub_specialty_id` e o nivel de cima. */
export interface AdminCourseLessonTopic {
  id: number
  name: string
  sub_specialty_id: number | null
}

export interface AdminCourseLessonPayload {
  prep_course_id: number
  specialty_id: number
  lesson_title: string
  /** Gerado pelo backend (ex.: "CLM-0181") quando vazio. */
  lesson_code?: string
  description?: string | null
  learning_objectives?: string | null
  video_url?: string | null
  estimated_duration_minutes?: number | null
  difficulty_level?: string | null
  tags?: string | null
  display_order?: number
  module_name?: string | null
  module_order?: number | null
  academic_year?: number
  /** R08-20: 'YYYY-MM-DD' ou null (disponivel desde o inicio do ano). */
  release_on?: string | null
  active?: boolean
  /**
   * Substituicao, nao acrescimo: a lista enviada vira a lista final e `[]` desvincula
   * tudo. Omitir a chave mantem os vinculos como estao (o toggle de ativa/inativa da
   * listagem depende disso).
   *
   * O backend recusa subespecialidade fora da especialidade da aula e topico fora das
   * subespecialidades enviadas.
   */
  sub_specialty_ids?: number[]
  topic_ids?: number[]
}

export interface AdminPagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

export interface AdminCourseLessonFilters {
  prep_courses: Array<{ id: number; name: string }>
  specialties: Array<{ id: number; code: string; name: string }>
  academic_years: number[]
}

// ==================== ADMIN: VINCULO EM LOTE (r6-32) ====================
// Espelho manual de Api::V1::Admin::CourseLessonBulkLinksController.

/** exact = nome igual; single = busca com um unico topico; ambiguous = varios candidatos. */
export type AdminBulkLinkSuggestionStatus = 'exact' | 'single' | 'ambiguous' | 'unmatched'

export interface AdminBulkLinkQuery {
  page?: number
  per_page?: number
  search?: string
  prep_course_id?: number
  specialty_id?: number
  without_topic?: boolean
  suggestion?: AdminBulkLinkSuggestionStatus
}

export interface AdminBulkLinkTopic {
  id: number
  name: string
  sub_specialty_id: number | null
  sub_specialty_name: string | null
  specialty_name: string | null
}

export interface AdminBulkLinkSuggestion {
  status: AdminBulkLinkSuggestionStatus
  /** O trecho do titulo que casou (sem o parentese final ou o prefixo de area). */
  matched_text: string | null
  /** Nulo no ambiguo e no sem casamento: o admin escolhe. */
  topic: AdminBulkLinkTopic | null
  /** So no ambiguo. */
  candidates: AdminBulkLinkTopic[]
}

export interface AdminBulkLinkLesson {
  id: number
  lesson_code: string
  lesson_title: string
  prep_course_id: number
  prep_course_name: string | null
  specialty_id: number
  specialty_name: string | null
  sub_specialties: AdminCourseLessonSubSpecialty[]
  topics: AdminCourseLessonTopic[]
  /** Nulo quando a aula ja tem topico. */
  suggestion: AdminBulkLinkSuggestion | null
}

export interface AdminBulkLinkBatch {
  id: number
  lessons_count: number
  topics_added: number
  sub_specialties_added: number
  created_at: string
  user_name: string | null
  undone_at: string | null
}

export interface AdminBulkLinkListResponse {
  success: boolean
  course_lessons: AdminBulkLinkLesson[]
  pagination: AdminPagination
  filters: Pick<AdminCourseLessonFilters, 'prep_courses' | 'specialties'>
  /** Ultimo lote ainda aplicado (o que o "Desfazer" reverte). */
  last_batch: AdminBulkLinkBatch | null
}

/** Acrescimo: o que a aula ja tem fica. Topico exige a subespecialidade dele. */
export interface AdminBulkLinkItem {
  course_lesson_id: number
  sub_specialty_id?: number | null
  topic_id?: number | null
}

export interface AdminBulkLinkChange {
  course_lesson_id: number
  lesson_title: string
  lesson_code: string
  added_sub_specialties: Array<{ id: number; name: string | null }>
  added_topics: Array<{ id: number; name: string | null }>
  topics_before: number
  topics_after: number
}

export interface AdminBulkLinkResult {
  success: boolean
  dry_run: boolean
  changes: AdminBulkLinkChange[]
  unchanged_ids: number[]
  summary: {
    lessons_changed: number
    lessons_unchanged: number
    topics_added: number
    sub_specialties_added: number
  }
}

export interface AdminBulkLinkApplyResponse extends AdminBulkLinkResult {
  message: string
  /** Nulo quando nada mudou (lote idempotente). */
  batch: AdminBulkLinkBatch | null
}

export interface AdminBulkLinkUndoResponse {
  success: boolean
  message: string
  batch: AdminBulkLinkBatch
  last_batch: AdminBulkLinkBatch | null
}

/**
 * Aulas ativas do cursinho; exact/single/ambiguous/unmatched contam so as sem topico e vem
 * nulos quando o relatorio foi pedido com `suggestions=false`.
 */
export interface AdminBulkLinkReportRow {
  prep_course_id: number
  prep_course_name: string
  total: number
  with_topic: number
  without_topic: number
  exact: number | null
  single: number | null
  ambiguous: number | null
  unmatched: number | null
}

export interface AdminBulkLinkReportResponse {
  success: boolean
  report: AdminBulkLinkReportRow[]
  totals: Omit<AdminBulkLinkReportRow, 'prep_course_id' | 'prep_course_name'>
}

// ==================== ADMIN: PROVAS NA INTEGRA ====================
// Espelho manual de Api::V1::Admin::ProvasIntegraController e
// Api::V1::Admin::ProvasIntegraQuestionsController.
//
// Provas e questoes vivem em UMA tabela cada (`exams` / `exam_questions`), com a
// plataforma de origem em `source`. Por isso a chave voltou a ser o `id` inteiro — a
// chave composta "<plataforma>-<id>" existia so porque os ids colidiam entre as duas
// tabelas de antes.

export type AdminProvaSource = 'medway' | 'medcof' | 'manual'

export interface AdminProva {
  id: number
  source: AdminProvaSource
  source_label: string
  external_id: string | null
  name: string
  display_name: string
  year: number | null
  /** Formato da prova (Objetiva, Discursiva, TEGO, Pratica). Era metade do `exam_type`. */
  format: string | null
  /** Grau da residencia (R1, R+, R3). Era a outra metade do `exam_type`. */
  residency_degree: string | null
  /**
   * HISTORICO. A Especialidade desceu para a questao, onde e derivada da
   * subespecialidade — no nivel da prova ela errava 52% das questoes, porque uma prova
   * cobra as cinco especialidades na mesma folha. O formulario nao edita mais estes
   * dois campos; eles seguem no payload porque as provas antigas os tem preenchidos.
   */
  specialty_id: number | null
  specialty_name: string | null
  /** Vinculo com Catalogos > Processos Seletivos: a edicao concreta ("SUS-SP 2026"). */
  selection_process_id: number | null
  selection_process_name: string | null
  /** Vinculo com Catalogos > Instituicoes. Opcional tambem no admin (simulado proprio / prova sem banca). */
  institution_id: number | null
  /** Derivado da instituicao — nao ha mais coluna de texto na prova. */
  institution_name: string | null
  institution_state: string | null
  group_id: number | null
  group_name: string | null
  /** Contagem REAL de questoes carregadas. */
  question_count: number
  /** O que a plataforma de origem alega ter. As duas divergem muito no MedCof. */
  source_question_count: number | null
  /** Alias de `question_count`, mantido para a tela. */
  questions_loaded: number
  attempts_count: number
  is_active: boolean
  has_comments: boolean
  thumbnail_url: string | null
  /** So MedCof. */
  average_score: number | null
  /** So MedCof. */
  times_taken: number | null
  synced_at: string | null
  created_at: string
  updated_at: string
  /** So no #show. */
  questions_by_specialty?: Record<string, number>
  /** So no #show. */
  questions_by_difficulty?: Record<string, number>
}

export interface AdminProvaPayload {
  /** Origem. Na criacao manual o backend assume 'manual'. */
  source?: AdminProvaSource
  external_id?: string
  name?: string
  year?: number | null
  format?: string | null
  residency_degree?: string | null
  /** Historico; o formulario nao envia mais. Ver AdminProva. */
  specialty_id?: number | null
  /**
   * Catalogos > Processos Seletivos. Opcional: as 3.807 provas existentes nasceram
   * antes da coluna. O backend recusa quando o processo nao inclui a instituicao da
   * prova.
   */
  selection_process_id?: number | null
  /** Opcional tambem no admin (simulado proprio / prova sem banca): vinculo com Catalogos > Instituicoes. */
  institution_id?: number | null
  exam_group_id?: number | null
  thumbnail_url?: string | null
  is_active?: boolean
  has_comments?: boolean
}

export interface AdminProvaOption {
  letter: string
  content: string | null
  is_correct: boolean
  response_percentage?: number | null
}

export interface AdminProvaQuestion {
  id: number
  source: AdminProvaSource
  exam_id: number
  exam_name: string | null
  external_id: string
  question_number: number | null
  statement: string | null
  /** Comentario do gabarito (originalmente so MedCof). */
  comment: string | null
  options: AdminProvaOption[]
  /** Todas as letras marcadas como corretas — a questao aceita 0..N. */
  correct_answers: string[]
  /**
   * Primeira letra de `correct_answers`. Existe porque a correcao da prova do aluno
   * compara com uma letra so; nao e o gabarito completo.
   */
  correct_answer: string | null
  difficulty: string | null
  difficulty_label: string
  /**
   * Especialidade da questao. SOMENTE LEITURA: e derivada da subespecialidade (o pai
   * dela na arvore do catalogo) e regravada pelo backend a cada save. Nao ha campo no
   * formulario — escolher separadamente permitiria contradizer a subespecialidade.
   */
  specialty_id: number | null
  specialty_name: string | null
  /**
   * Vinculo com Catalogos > Subespecialidades (tabela `sub_specialties`). Obrigatorio
   * no admin. As strings de tema da origem sairam do schema — sobraram apenas estas
   * referencias.
   */
  sub_specialty_id: number | null
  sub_specialty_name: string | null
  /**
   * Vinculo com Catalogos > Topicos (tabela `topics`). Opcional, mas quando existe
   * precisa ser filho de `sub_specialty_id`.
   */
  topic_id: number | null
  topic_name: string | null
  year: number | null
  residency_degree: string | null
  question_format: string | null
  /** Vem da PROVA: a questao nao tem mais colunas de instituicao. */
  institution_id: number | null
  institution_name: string | null
  tags: unknown
  images: unknown
  canceled: boolean
  is_outdated: boolean
  status: 'active' | 'canceled' | 'outdated'
  success_rate: number | null
  created_at: string
  updated_at: string
}

export interface AdminProvaQuestionPayload {
  /** Obrigatorio na criacao: a prova a que a questao pertence. */
  exam_id?: number
  external_id?: string
  question_number?: number | null
  statement?: string | null
  comment?: string | null
  /** Obrigatorio no admin: vinculo com Catalogos > Subespecialidades. */
  sub_specialty_id?: number | null
  /**
   * Opcional; null desvincula a questao do topico. Precisa ser filho da
   * subespecialidade.
   */
  topic_id?: number | null
  /** Lista fechada; null = nao informada (opcao "—" do select). */
  difficulty?: 'easy' | 'medium' | 'hard' | null
  year?: number | null
  residency_degree?: string | null
  question_format?: string | null
  canceled?: boolean
  is_outdated?: boolean
  options?: Array<{ letter: string; content: string | null; is_correct: boolean }>
}

export interface AdminProvaFilters {
  sources: Array<{ value: AdminProvaSource; label: string }>
  years: number[]
  /** Formatos e graus juntos: e um filtro so na tela. */
  exam_types: string[]
  institutions: Array<{ id: number; name: string; state: string | null }>
  groups: Array<{ id: number; name: string }>
}

export interface AdminProvaStats {
  total: number
  active: number
  questions: number
  filtered: number
}

export interface AdminProvaQuestionStats {
  total: number
  active: number
  canceled: number
  outdated: number
}

// ==================== ADMIN: CATALOGOS DE REFERENCIA ====================
// Espelho manual de Api::V1::Admin::{Institutions,Topics,Specialties,Catalogs}Controller.
//
// Sao os tres catalogos que as provas, questoes e aulas referenciam:
// MedwayInstitution, CanonicalTopic e Specialty.

export interface AdminInstitution {
  id: number
  external_id: string
  name: string
  abbreviation: string | null
  display_name: string
  state: string | null
  city: string | null
  location: string
  thumbnail_url: string | null
  /** Contador vindo do import; pode estar defasado. */
  exam_count: number
  /** Contagem real de provas na tabela. */
  exams_count: number
  synced_at: string | null
  created_at: string
  updated_at: string
}

export interface AdminInstitutionPayload {
  external_id?: string
  name: string
  abbreviation?: string | null
  state?: string | null
  city?: string | null
  thumbnail_url?: string | null
}

// R12-07 — sugestao de `/catalog/institutions` e `/catalog/graduation_institutions`.
// O shape e o mesmo nas duas; na graduacao `city` vem sempre null e `usage_count` sempre 0.
export interface CatalogInstitutionSuggestion {
  id: number
  name: string
  abbreviation: string | null
  state: string | null
  city: string | null
  /** "Cidade, UF" ja montado pelo backend. */
  location: string
  /** Provas da instituicao no catalogo; ordena a lista. */
  usage_count: number
}

export interface CatalogSuggestionsResponse {
  success: boolean
  results: CatalogInstitutionSuggestion[]
  total: number
}

// R12-07 — espelho de Api::V1::Admin::GraduationInstitutionsController. Lista em que o
// aluno informa onde se formou; nao tem relacao com `institutions` (bancas).
export interface AdminGraduationInstitution {
  id: number
  external_id: string
  name: string
  abbreviation: string | null
  state: string | null
  created_at: string
  updated_at: string
}

export interface AdminGraduationInstitutionPayload {
  external_id?: string
  name: string
  abbreviation?: string | null
  state?: string | null
}

export interface AdminGraduationInstitutionsResponse {
  success: boolean
  graduation_institutions: AdminGraduationInstitution[]
  pagination: AdminPagination
  filters: { states: string[] }
  stats: { total: number; filtered: number }
}

export interface AdminGraduationInstitutionResponse {
  success: boolean
  message?: string
  graduation_institution: AdminGraduationInstitution
}

/**
 * Verbete de `canonical_topics` — o dicionario PARALELO, usado pela retencao (FSRS).
 * Nao e o Topico do catalogo de produto (`topics`, ver AdminTopic mais abaixo): sao
 * dois denominadores diferentes e ambos legitimos, e o prefixo Canonical existe
 * justamente para que a distancia entre eles seja visivel no autocomplete do editor.
 *
 * Nao tem tela propria no admin; as funcoes de api.ts existem so para a carga inicial.
 */
export interface AdminCanonicalTopic {
  id: number
  name: string
  slug: string
  specialty: string | null
  parent_topic_id: number | null
  parent_topic_name: string | null
  /** Grafias alternativas, sempre em minusculas. */
  aliases: string[]
  active: boolean
  children_count: number
  created_at: string
  updated_at: string
}

export interface AdminCanonicalTopicPayload {
  name: string
  slug?: string
  specialty?: string | null
  parent_topic_id?: number | null
  aliases?: string[]
  active?: boolean
}

export interface AdminSpecialty {
  id: number
  code: string
  name: string
  display_name: string
  slug: string | null
  color: string | null
  hex_color: string
  icon: string | null
  display_order: number
  active: boolean
  /** Uma das 5 especialidades estruturais (GO, PED, CIR, CLM, PREV). */
  is_big_five: boolean
  lessons_count: number
  created_at: string
  updated_at: string
}

export interface AdminSpecialtyPayload {
  code: string
  name: string
  slug?: string | null
  color?: string | null
  icon?: string | null
  display_order?: number
  active?: boolean
}

// ---------- Processos seletivos ----------
// Espelho manual de Api::V1::Admin::SelectionProcessesController.
//
// Relacoes: Instituicao N-N ProcessoSeletivo N-N Especialidade. As duas listas sao
// editadas pelo proprio formulario do processo.

/**
 * Graus aceitos em `residency_degree` — `User::SELECTION_PROCESSES` em maiuscula
 * (SelectionProcess::RESIDENCY_DEGREES). Nao confundir com `users.selection_process`,
 * que grava o mesmo conjunto mas com 'r1' minusculo.
 *
 * Os cinco primeiros existem em `medway_exam_questions.residency_degree`; os tres
 * ultimos so existem do lado do usuario e nao casam com nenhuma questao.
 */
export type AdminResidencyDegree =
  | 'R1'
  | 'R+CM'
  | 'R+CG'
  | 'R+PED'
  | 'R+GO'
  | 'R+OUTRAS'
  | 'AA'
  | 'AREAS'

export interface AdminSelectionProcessInstitution {
  id: number
  name: string
  state: string | null
}

export interface AdminSelectionProcessSpecialty {
  id: number
  code: string
  name: string
}

/** Objetivo vinculado ao processo (r6-23): o que o aluno quer ser, nao o que a prova cobra. */
export interface AdminSelectionProcessCareerGoal {
  id: number
  name: string
}

export interface AdminSelectionProcess {
  id: number
  name: string
  /** "Nome Ano" quando ha ano; so o nome quando nao ha. */
  display_name: string
  slug: string
  year: number | null
  residency_degree: AdminResidencyDegree | null
  active: boolean
  institutions: AdminSelectionProcessInstitution[]
  specialties: AdminSelectionProcessSpecialty[]
  career_goals: AdminSelectionProcessCareerGoal[]
  institutions_count: number
  specialties_count: number
  career_goals_count: number
  created_at: string
  updated_at: string
}

export interface AdminSelectionProcessPayload {
  name: string
  year?: number | null
  residency_degree?: AdminResidencyDegree | '' | null
  active?: boolean
  /** Substituicao, nao acrescimo: o que nao vier na lista e desvinculado. */
  institution_ids?: number[]
  specialty_ids?: number[]
  career_goal_ids?: number[]
}

// ---------- Objetivos (r6-23) ----------
// Espelho manual de Api::V1::Admin::CareerGoalsController. Catalogo global: um objetivo
// se liga a N processos. Alimenta SO "Especialidade desejada" (Configuracoes e onboarding).

export interface AdminCareerGoal {
  id: number
  name: string
  active: boolean
  /** Alunos com `users.career_goal_id` apontando para ele — bloqueia a exclusao (409). */
  users_count: number
  processes_count: number
  created_at: string
  updated_at: string
}

export interface AdminCareerGoalPayload {
  name: string
  active?: boolean
}

/** `career_goals` de cada processo em `GET /selection_processes` (publico). */
export interface SelectionProcessCareerGoal {
  id: number
  name: string
}

// ==================== CATALOGO (Especialidade > Subespecialidade > Topico) ====================
// Um nivel por tabela: `specialties` (ja existia) -> `sub_specialties` -> `topics`.
// Antes os tres niveis moravam na mesma tabela `reference_data`, separados por uma
// coluna `category`, e o ponteiro para cima era um `parent_id` polimorfico. Por isso
// os tipos daqui trazem o nome do pai por extenso (`specialty_id`, `sub_specialty_id`):
// nao ha mais um "pai" generico que possa ser de qualquer nivel.
//
// O `hash_id` sobreviveu a mudanca byte a byte e continua sendo IDENTIDADE PUBLICA: e
// ele que a Primeira Carga devolve no POST e e por ele que as sincronizacoes
// reencontram o verbete em vez de criar um duplicado. Renomear o verbete NAO o muda.

/** Verbete do nivel 2 do catalogo (tabela `sub_specialties`). */
export interface AdminSubSpecialty {
  id: number
  /** Identidade de origem; nao muda ao renomear. */
  hash_id: string
  name: string
  normalized_name: string
  specialty_id: number | null
  specialty_name: string | null
  tags: string[]
  /** Somado pelas sincronizacoes a partir de `sources`; somente leitura. */
  usage_count: number
  sources: Record<string, number>
  /** Topicos pendurados nesta subespecialidade. */
  topics_count: number
  /**
   * Questoes classificadas nesta subespecialidade. Vem do backend para o dialogo de
   * exclusao poder dizer o tamanho do estrago ANTES do clique: a FK e ON DELETE SET
   * NULL, entao excluir desclassifica todas elas sem erro nenhum.
   */
  questions_count: number
  created_at: string
  updated_at: string
}

export interface AdminSubSpecialtyPayload {
  name: string
  specialty_id?: number | null
  tags?: string[]
}

/** Verbete do nivel 3 do catalogo (tabela `topics`). Nao tem nivel abaixo. */
export interface AdminTopic {
  id: number
  hash_id: string
  name: string
  normalized_name: string
  sub_specialty_id: number | null
  sub_specialty_name: string | null
  /** Derivada via subespecialidade; so exibicao. */
  specialty_name: string | null
  tags: string[]
  usage_count: number
  sources: Record<string, number>
  /** Questoes classificadas neste topico; mesma razao da subespecialidade. */
  questions_count: number
  created_at: string
  updated_at: string
}

export interface AdminTopicPayload {
  name: string
  sub_specialty_id?: number | null
  tags?: string[]
}

/**
 * Forma comum das duas telas de catalogo do admin.
 *
 * Subespecialidade e Topico tem a MESMA UI e diferem so em qual e o nivel de cima, por
 * isso as duas telas sao a mesma implementacao. Como cada nivel agora tem tabela e
 * payload proprios, essa uniformidade deixou de existir no fio e passa a ser produzida
 * aqui: os hooks normalizam `specialty_id`/`sub_specialty_id` em `parent_id` e
 * `topics_count`/nada em `children_count`. Os tipos acima continuam fieis ao JSON.
 */
export interface AdminCatalogLevelEntry {
  id: number
  hash_id: string
  name: string
  normalized_name: string
  parent_id: number | null
  parent_name: string | null
  tags: string[]
  usage_count: number
  sources: Record<string, number>
  /** Filhos do nivel de baixo. Topico nao tem nivel abaixo: sempre 0. */
  children_count: number
  /** Questoes que a exclusao deste verbete desclassifica. Os dois niveis tem. */
  questions_count: number
  created_at: string
  updated_at: string
}

/** Opcao de subespecialidade nos seletores dos formularios do admin. */
export interface AdminSubSpecialtyOption {
  id: number
  name: string
  /** Especialidade da subespecialidade — FK direta desde que o nivel 1 virou `specialties`. */
  specialty_id: number | null
  specialty_name: string | null
}

/** Opcao de topico nos seletores dos formularios do admin. */
export interface AdminTopicOption {
  id: number
  name: string
  sub_specialty_id: number | null
  sub_specialty_name: string | null
  /**
   * Especialidade do topico, derivada pela subespecialidade. Antes vinha nula (o pai
   * do topico era o tema, e a ponte entre os dois catalogos de especialidade so
   * resolvia o nivel de cima); com a FK direta o backend consegue preenche-la.
   */
  specialty_id: number | null
}

/** Os catalogos que o admin mantem, para alimentar os seletores dos formularios. */
export interface AdminCatalogs {
  /** Catalogos > Instituicoes. */
  institutions: Array<{ id: number; name: string; state: string | null }>
  /** canonical_topics — dicionario paralelo (FSRS), sem tela propria no admin. */
  canonical_topics: Array<{ id: number; name: string; specialty: string | null }>
  /** Catalogos > Subespecialidades (tabela `sub_specialties`). */
  sub_specialties: AdminSubSpecialtyOption[]
  /** Catalogos > Topicos (tabela `topics`); `sub_specialty_id` e o nivel de cima. */
  topics: AdminTopicOption[]
  /** Catalogos > Especialidades. */
  specialties: Array<{ id: number; code: string; name: string }>
  /**
   * Catalogos > Processos Seletivos, para o seletor do formulario de prova.
   * `institution_ids` acompanha porque a prova so pode apontar para um processo que
   * inclua a instituicao dela — lista vazia nao restringe nada.
   */
  selection_processes: Array<{
    id: number
    name: string
    year: number | null
    residency_degree: string | null
    institution_ids: number[]
  }>
}

// ==================== TRACK METRICS ====================
//
// Espelho manual do payload de GET /api/v1/dashboard/track_metrics[/:code], hoje
// servido pelo Rails (Api::V1::DashboardController). O mock MSW que atendia estas
// rotas ja nao existe.

export interface TrackKPIs {
  ep_accuracy: number
  ep_questions: number
  revision_accuracy: number
  revision_questions: number
  exams_accuracy: number
  exams_questions: number
  /**
   * 0..100 com uma casa. `null` = NAO MENSURAVEL (nenhum tema da especialidade tem
   * progresso), e nunca 0 no lugar de "sem dado": retencao medida que arredonda
   * para zero e um dado real. Use `retention_topics` para separar os dois casos.
   */
  retention_index: number | null
  /** Sobre quantos temas o indice foi medido. 0 = nao mensuravel. */
  retention_topics: number
}

export type TrackStatus = 'excellent' | 'good' | 'needs_work' | 'critical' | 'no_data'

export interface TrackCardData {
  code: string
  name: string
  sort_order: number
  has_data: boolean
  status: TrackStatus
  kpis: TrackKPIs
}

export type TrackPeriod = '4w' | '12w' | '24w' | 'all'

export interface TrackMeta {
  selection_process: string
  has_track_definition: boolean
  period: TrackPeriod
}

export interface TrackMetricsResponse {
  meta: TrackMeta
  tracks: TrackCardData[]
}

export interface TrackWeeklyPoint {
  week_label: string
  /**
   * `null` na semana sem nenhuma questao — e diferente de 0%, que significaria
   * "errou tudo". O grafico pula a lacuna em vez de derrubar a linha ao chao.
   */
  accuracy: number | null
  questions: number
}

export interface TrackRetentionPoint {
  week_label: string
  /** `null` enquanto nenhum topico da trilha tiver revisao registrada ate a semana. */
  retention: number | null
  coverage: number
}

export interface TrackInstitutionRow {
  institution: string
  accuracy: number
  count: number
}

/**
 * Um TEMA da especialidade (`Dashboard::TrackThemes`).
 *
 * "Tema" aqui vale UMA coisa: `CanonicalTopic`, o verbete que
 * `user_topic_progresses` indexa — o mesmo que o KPI conta em `retention_topics`.
 * A lista e uma ABERTURA do KPI de retencao, e nao um segundo calculo:
 * `themes.length === track.kpis.retention_topics`, e a media de `retention` daqui
 * e o proprio `retention_index`.
 *
 * NAO e recortado por periodo, pela mesma razao que a retencao nao e: e o estado
 * de hoje, acumulado desde sempre.
 *
 * `accuracy`/`questions`/`correct` saem da MESMA consulta que `theme_stage_series`
 * abre em E0, E1, E2...: `questions === points.reduce(+questions)`, e `accuracy` e a
 * MEDIA DOS REGISTROS do tema, primario e revisoes (R5 do spec r4/03), calculada pelo
 * backend. Nao ha duas contas de desempenho do tema nesta tela.
 */
export interface TrackThemeRow {
  canonical_topic_id: number
  name: string
  /**
   * Quantos REGISTROS de estudo (primario + revisao) do aluno alcancam este tema —
   * pela FK de catalogo ou pelo texto do tema (as duas pontes do progresso FSRS,
   * spec r4/03). Um registro que chega ao tema pela aula E pelo topico conta 1; um registro que
   * cobre dois temas conta 1 em CADA um. Por isso a coluna nao fecha com o total de
   * registros do aluno. E a chave de ordenacao da lista: o backend devolve `themes`
   * por `[-records, name]`.
   */
  records: number
  /**
   * Media aritmetica do acerto dos registros com questao, 0..100 (nada passa de 100,
   * mesmo com mais acertos que questoes gravados). `null` = tema sem nenhuma questao
   * registrada. Nunca 0 por ausencia.
   */
  accuracy: number | null
  /** Questoes dos registros do tema. NAO fecha com o total da especialidade — ver o resultado da spec. */
  questions: number
  /** Ja limitado a `questions`, registro a registro. */
  correct: number
  /**
   * Quanto o acumulador de `user_topic_progresses` tem A MAIS do que os registros
   * explicam (registro editado ou apagado depois de somar). Fica FORA da barra e do
   * comparativo. Desde a r5-01 a tabela NAO o imprime (o "+N fora" era lido como
   * "10 + 40"); segue no payload como diagnostico para suporte.
   */
  unlinked_questions: number
  /** 0..100, curva LEGADA (`retrievability`), a mesma do KPI e da serie desta tela. */
  retention: number
}

/** Um ponto do comparativo: o tema naquele estagio de revisao. */
export interface TrackThemeStagePoint {
  /**
   * 'E0' (estudo primario) ou 'E<n>' (n-esima revisao, na ordem cronologica), SEM
   * teto — 'E3', 'E4', 'E5'... (R2 do spec r4/03). Nao e o `ThemeStage` dos badges,
   * que agrupa em 'E3+'.
   */
  stage: string
  /**
   * `null` = estagio cumprido sem questao registrada. Em E0 com mais de um estudo
   * primario e a MEDIA dos acertos deles (R3). Nunca acima de 100 (R4).
   */
  accuracy: number | null
  questions: number
  correct: number
  /** Quantos registros o ponto soma. */
  records: number
}

/**
 * Uma linha do comparativo de temas (`Dashboard::ThemeStageSeries`), na MESMA
 * ordem de `themes`. `points` vazio = tema sem registro que o alcance (nem pela FK,
 * nem pelo texto): ausencia de medida, e nao uma linha no chao.
 */
export interface TrackThemeStageSeries {
  canonical_topic_id: number
  name: string
  /** O IR do tema, repetido aqui para o grafico nao precisar cruzar com `themes`. */
  retention: number
  points: TrackThemeStagePoint[]
}

/**
 * r11-09 — termo registrado fora do catalogo ("aula de bolo"), agrupado pelo nome. Conta
 * no TED e no card da trilha, mas nao tem tema (`themes`).
 */
export interface TrackStudentTopic {
  name: string
  records: number
  questions: number
  correct: number
  /** `null` sem questao. */
  accuracy: number | null
  hours: number
  last_studied_on: string | null
}

export interface TrackDetailResponse {
  track: TrackCardData
  /** R3 do spec r2/03 — os temas da especialidade, com desempenho e retencao. */
  themes: TrackThemeRow[]
  /** r11-09. Ausente em API antiga. */
  student_topics?: TrackStudentTopic[]
  /** R4 do spec r2/03 — por tema, os pontos E0, E1, E2... (sem teto desde o r4/03). */
  theme_stage_series: TrackThemeStageSeries[]
  ep_series: TrackWeeklyPoint[]
  revision_series: TrackWeeklyPoint[]
  /**
   * Continua servido pela API, mas NAO e mais desenhado na visao por
   * especialidade (R1): "Performance em provas e simulados" mudou de endereco
   * para a aba "Por Prova".
   */
  exams_by_institution: TrackInstitutionRow[]
  retention_series: TrackRetentionPoint[]
}

// Track catalog — what each selection_process defines as its top-level study buckets.
// Consumed via GET /api/v1/dashboard/tracks (Dashboard::TrackCatalog no backend, que
// deriva a lista das provas na integra das instituicoes prioritarias) para nao duplicar
// o catalogo em arrays SPECIALTIES fixos no TS.
export interface TrackDefinition {
  code: string
  name: string
  sort_order: number
  // Palette name (not Tailwind class). Frontend maps via PALETTE_TO_TAILWIND.
  // Unknown palette names fall back to neutral.
  color: string
}

export interface TracksResponse {
  selection_process: string
  tracks: TrackDefinition[]
}

// ==================== EXAM METRICS (aba Por Prova) ====================
//
// Espelho manual do payload de GET /api/v1/dashboard/exam_metrics
// (Dashboard::ExamMetrics). Nao ha schema compartilhado entre os dois repos:
// campo esquecido aqui nao quebra o `tsc`, quebra a tela em silencio.

/**
 * As tres origens de uma prova, com o vocabulario do backend (que e diferente do
 * vocabulario antigo, onde `exam_answers` era chamado de "simulado da plataforma"):
 *
 *   exam_attempt = prova na integra       (Exam/ExamAttempt, /app/provas)
 *   quiz_attempt = simulado da plataforma (Quiz/QuizAttempt, /app/simulados)
 *   manual       = registro manual        (TestAndSimulation, /app/manual)
 */
export type ExamMetricSource = 'exam_attempt' | 'quiz_attempt' | 'manual'

export interface ExamMetricRow {
  /** `"<origem>:<id>"` — o id numerico colide entre as tres tabelas. Chave do React. */
  id: string
  source: ExamMetricSource
  /** Rotulo servido pelo backend. A tela usa o mapa proprio; este e o fallback. */
  source_label: string
  name: string
  /** `null` no registro manual (a tabela nao tem a coluna) e no quiz sem prova ligada. */
  institution: string | null
  year: number | null
  /** `"YYYY-MM-DD"`. Linha sem data e descartada pelo backend. */
  date: string
  /** Quantidade VALIDA de questoes — anuladas ficam fora (spec 01). */
  questions: number
  hits: number
  /** 0..100 com uma casa. `null` se e somente se `questions === 0` — nunca 0.0 fabricado. */
  performance: number | null
  /** 0..100 com uma casa. `null` = NAO MENSURAVEL, nunca 0 no lugar de "sem dado". */
  retention_index: number | null
  /** Temas da prova com progresso do aluno (numerador do "medido sobre N de M"). */
  retention_topics_measured: number
  /** Temas distintos cobrados pela prova (denominador). */
  retention_topics_total: number
  /**
   * Questoes que entraram no DENOMINADOR de `retention_index` — as dos temas
   * mensuraveis, e nao as da prova inteira. E o unico peso honesto para somar
   * varias provas numa media so: uma prova de 100 questoes medida sobre 1 nao
   * pode entrar com peso 100. `retention_topics_measured` conta TEMAS e nao serve.
   */
  retention_questions_measured: number
  /** Questoes validas sem tema no catalogo. Contador DISJUNTO dos dois acima. */
  questions_without_topic: number
  annulled_count: number
  /**
   * De que universo `retention_index` saiu (r6-15): `'topic'` = os temas que a prova
   * cobrou (prova na integra, simulado da plataforma); `'specialty'` = TODOS os temas
   * estudados das especialidades informadas no simulado do Manual — uma aproximacao,
   * e a tela diz isso.
   */
  retention_scope: ExamRetentionScope
  /** O porque do `retention_index` nulo, decidido no backend. `null` quando ha indice. */
  retention_reason: ExamRetentionReason | null
  /** Especialidades do simulado com pelo menos um tema estudado. `0` no escopo `'topic'`. */
  retention_specialties_measured: number
  /** Especialidades informadas no simulado. `0` no escopo `'topic'`. */
  retention_specialties_total: number
  /**
   * Quebra por especialidade que a linha carrega dentro de si. Hoje so o registro
   * manual preenche (ele nao tem tela de detalhe e o dado ja vem carregado); as
   * outras origens mandam `[]`.
   */
  specialty_results: ExamMetricSpecialtyResult[]
  /** `null` para quiz e registro manual: nao ha tela de detalhe. `null` = linha nao clicavel. */
  detail_path: string | null
}

export type ExamRetentionScope = 'topic' | 'specialty'

/**
 * `no_progress` = a prova tem tema (ou o simulado tem especialidade), mas nenhum foi
 * estudado; `no_topics` = a prova nao tem tema no catalogo; `no_specialty` = o simulado
 * do Manual nao tem especialidade com questao.
 */
export type ExamRetentionReason = 'no_progress' | 'no_topics' | 'no_specialty'

/** Uma linha de "Resultado por Especialidade" do simulado registrado a mao. */
export interface ExamMetricSpecialtyResult {
  specialty: string
  questions: number
  hits: number
  /** `null` se e somente se `questions === 0` — nunca 0.0 fabricado. */
  performance: number | null
  /**
   * Retencao media dos temas estudados DESTA especialidade (r6-15), 0..100 com uma
   * casa. `null` = nenhum tema estudado nela — NAO MENSURAVEL, nunca 0.
   */
  retention_index: number | null
  /** Temas estudados que entraram em `retention_index`. */
  retention_topics: number
}

export interface ExamMetricsMeta {
  /** `null` quando nao houve recorte de data (historico completo). */
  period: TrackPeriod | null
  period_applied: boolean
  start_date: string | null
  end_date: string | null
  total: number
  /** As tres chaves sempre presentes, mesmo com valor 0. */
  by_source: Record<ExamMetricSource, number>
  /**
   * Registros manuais que ficaram FORA da lista por nao terem data (a coluna e
   * nullable e o model nao valida nada). Contados, nao sumidos — a tela avisa.
   * Sempre 0 quando ha recorte de periodo: o SQL ja descartou a linha sem data.
   * `null` quando a origem `manual` esta em `sources_failed`: nao foi medido
   * (nil != zero), e a tela nao deve tratar como "nenhum sem data".
   */
  manual_without_date: number | null
  /**
   * Origens que o backend NAO conseguiu ler (`ActiveRecord::StatementInvalid` —
   * o caso real e producao sem uma coluna que so existe no schema.rb). Sempre
   * presente; `[]` quando tudo carregou. Nao vazio = a lista esta PARCIAL: as
   * outras origens vieram na integra e a tela avisa em vez de errar (r6-14 R3).
   */
  sources_failed: ExamMetricSource[]
}

/**
 * Acerto por grande area DENTRO das provas do periodo (r6-16 R1): os 6 cards da aba
 * "Por Prova". A MESMA conta do pentagono do Dashboard (`Dashboard::ExamTrackScores`),
 * com o periodo da aba; `total` soma tambem o que cai fora das grandes areas.
 */
export interface ExamMetricsByTrack {
  period: { from: string; to: string }
  tracks: ExamTrackScore[]
  total: { questions: number; hits: number; score_pct: number | null }
}

export interface ExamMetricsResponse {
  meta: ExamMetricsMeta
  exams: ExamMetricRow[]
  /**
   * `null` quando a agregacao caiu por schema (mesma blindagem de `sources_failed`) —
   * nao medido, e a tela avisa. AUSENTE quando o cliente pediu `with_tracks=0`
   * (chips/picker do comparativo, que so precisam das linhas).
   */
  by_track?: ExamMetricsByTrack | null
}

// ==================== EXAM COMPARISON (/app/provas/comparar) ====================
//
// Espelho manual do payload de GET /api/v1/dashboard/exam_comparison
// (Dashboard::ExamComparison). A linha e a MESMA `ExamMetricRow` da aba "Por
// Prova" — reusada aqui, e nao redeclarada, porque o backend tambem a reusa: o
// comparativo parte de `Dashboard::ExamMetrics` e so acrescenta `breakdowns`.

/**
 * UM PONTO do grafico: uma especialidade (ou um tema) dentro de UMA prova.
 *
 * As quatro primeiras chaves sao literalmente as de `stats.by_specialty` /
 * `stats.by_theme` da tela de resultado — mesmo nome, mesmo total, mesmo acerto,
 * mesmo `rate` — porque saem do MESMO servico do backend
 * (`ExamAttempts::AnswerBreakdown`). As cinco de retencao sao as de
 * `ExamMetricRow`, sem o prefixo repetido.
 */
export interface ExamComparisonBreakdown {
  name: string
  /**
   * Nome da especialidade PAI desta linha — o elo que faltava para a tela montar
   * a cascata sem recalcular nada. O backend ja agrupa `by_theme` por
   * `sub_specialty.name` e `by_specialty` por `sub_specialty.specialty.name`, ou
   * seja: os dois eixos JA SAO especialidade -> subespecialidade. So o vinculo
   * entre eles nao vinha no fio.
   *
   * Preenchido nas linhas de `by_theme` cuja subespecialidade tem pai no
   * catalogo. `null` nas linhas de `by_specialty` (que ja sao o topo da
   * hierarquia) e `null` nas de registro manual (a tabela nao tem coluna de
   * subespecialidade).
   */
  specialty: string | null
  total: number
  correct: number
  /** 0..100 com uma casa. `null` se e somente se `total === 0`. */
  rate: number | null
  /**
   * 0..100 com uma casa. `null` = NAO MENSURAVEL (nenhum tema deste grupo tem
   * progresso do aluno), e nunca 0 no lugar de "sem dado". O grafico OMITE o
   * ponto nulo e a razao vai para `meta.unavailable` — desenhar zero seria dizer
   * ao aluno que ele esqueceu tudo.
   */
  retention_index: number | null
  retention_topics_measured: number
  retention_topics_total: number
  retention_questions_measured: number
  questions_without_topic: number
}

/**
 * `null` = esta ORIGEM nao tem essa leitura por construcao (o simulado da
 * plataforma nao tem nenhuma das duas; o registro manual nao tem a de tema,
 * porque guarda agregado por especialidade e nao questao).
 * `[]` = a leitura existe, mas nenhuma questao desta prova trouxe classificacao.
 * Os dois casos tiram a prova do grafico com frases DIFERENTES em
 * `meta.unavailable`.
 */
export interface ExamComparisonBreakdowns {
  by_specialty: ExamComparisonBreakdown[] | null
  by_theme: ExamComparisonBreakdown[] | null
}

export interface ExamComparisonRow extends ExamMetricRow {
  breakdowns: ExamComparisonBreakdowns
}

/** Vocabulario da URL (`?eixo=` / `?indice=`), servido tambem pelo backend. */
export type ExamComparisonAxis = 'especialidade' | 'tema'
export type ExamComparisonIndex = 'desempenho' | 'retencao'

/**
 * R6: prova selecionada que nao entra numa leitura e INFORMADA, nunca desenhada
 * com buraco. Uma entrada por (prova x eixo x indice) que nao produz ponto
 * nenhum; a tela filtra pelo eixo e indice que o aluno esta vendo.
 */
export interface ExamComparisonUnavailable {
  /** O mesmo `"<origem>:<id>"` de `ExamComparisonRow.id`. */
  id: string
  name: string
  source: ExamMetricSource
  source_label: string
  axis: ExamComparisonAxis
  index: ExamComparisonIndex
  /** `source_without_breakdown` | `without_classification` | `without_retention`. */
  reason: string
  /** Frase pronta, em pt-BR, comecando em minuscula (a tela prefixa o nome da prova). */
  message: string
}

export interface ExamComparisonMeta {
  /** Tokens pedidos na URL. Diferente de `total` quando algum foi deduplicado. */
  requested: number
  total: number
  /** Teto de provas por comparacao, servido pelo backend para a tela nao repetir a constante. */
  limit: number
  /**
   * Corte que o backend aplica em `by_theme`, POR PROVA
   * (`ExamAttempts::AnswerBreakdown::THEME_LIMIT`, hoje 20): so os temas de maior
   * volume da prova entram na lista.
   *
   * Vem no fio porque sem ele a tela nao consegue distinguir os dois motivos de um
   * tema nao aparecer numa prova — "esta prova nao cobrou nenhuma questao deste
   * tema" e "este tema ficou fora do corte por volume desta prova" — e diria a
   * primeira frase nos dois casos, que e mentira na metade das vezes.
   */
  theme_limit: number
  by_source: Record<ExamMetricSource, number>
  unavailable: ExamComparisonUnavailable[]
}

export interface ExamComparisonResponse {
  meta: ExamComparisonMeta
  /** Ordenadas por data CRESCENTE — o eixo X e cronologico, ao contrario da aba "Por Prova". */
  exams: ExamComparisonRow[]
}

// ==================== STUDY ACTIVITY (abas Tempo + Questoes) ====================

/**
 * Os buckets de TED: os tres de sempre mais `flashcard_min` (.spec/flashcards.md, sessoes
 * de flashcards, so tempo). `extra_min` saiu do payload na spec r3/03 (R4): o tempo extra
 * nao entra em nenhum total de TED e so alimenta a propria sub-aba do Manual.
 */
export interface TedFormatPoint {
  week_label: string
  primary_min: number
  revision_min: number
  exam_min: number
  flashcard_min: number
}

export interface WeekdayTedPoint {
  weekday: string
  primary_min: number
  revision_min: number
  exam_min: number
  flashcard_min: number
}

export interface SpecialtyTedPoint {
  specialty: string
  total_min: number
}

export interface CumulativeTedPoint {
  week_label: string
  cumulative_primary: number
  cumulative_revision: number
  cumulative_exam: number
  cumulative_flashcard: number
}

/** Os mesmos tres buckets das series de tempo. Nao existe `extra_q`. */
export interface QuestionFormatPoint {
  week_label: string
  primary_q: number
  revision_q: number
  exam_q: number
}

export interface WeekdayQuestionPoint {
  weekday: string
  primary_q: number
  revision_q: number
  exam_q: number
}

export interface StudyActivityMeta {
  period: TrackPeriod
  // 'AAAA-MM-DD'. As telas fazem `new Date(valor + 'T00:00:00')` — um timestamp
  // ISO completo aqui vira Invalid Date.
  week_start: string
  week_end: string
}

/**
 * Meta TED vs. realizado de UMA semana do periodo (r5-08). `goal_min` null quando a
 * semana e anterior a primeira meta do aluno — nao e zero. `goal_inherited` marca a
 * meta carregada da semana anterior (Goal.effective_for), e nao definida naquela.
 */
export interface TedWeekPoint {
  week_label: string
  /** 'AAAA-MM-DD', a segunda-feira. */
  week_start: string
  goal_min: number | null
  goal_inherited: boolean
  realized_min: number
}

export interface StudyActivityResponse {
  meta: StudyActivityMeta
  ted_by_format: TedFormatPoint[]
  ted_by_weekday: WeekdayTedPoint[]
  ted_by_specialty: SpecialtyTedPoint[]
  ted_cumulative: CumulativeTedPoint[]
  ted_by_week: TedWeekPoint[]
  questions_by_format: QuestionFormatPoint[]
  questions_by_weekday: WeekdayQuestionPoint[]
}

// ==================== PROGRESS ANALYSIS (/app/analise-progresso) ====================
//
// Espelho manual de `GET /api/v1/dashboard/progress_analysis?period=`. Nao ha schema
// compartilhado entre os repos: mudar o payload no Rails exige mudar tambem
// src/lib/api.ts e esta secao, ou a tela quebra em silencio.
//
// Vocabulario: "topico" = uma linha de `topics`, a mesma unidade que o MedHunter
// manual grava em primary_study_topics/revision_topics. Os campos
// ainda se chamam `theme*` por compatibilidade com a UI, que fala "tema".

/** Contagem de topicos: quantos dos cobrados o aluno ja registrou. */
export interface CoveragePoint {
  /** Topicos do denominador que o aluno ja registrou (estudo primario OU revisao). */
  studied: number
  /** Topicos DISTINTOS cobrados nas provas do universo (instituicao ou trilha). */
  total: number
  pct: number
}

/**
 * Mesma cobertura, ponderada pela PREVALENCIA — o peso de um topico e o numero de
 * questoes que o cobram no universo de provas do aluno. Nao tem relacao com FSRS:
 * um topico muito cobrado pesa mais que um topico raro, independente de retencao.
 */
export interface WeightedCoveragePoint extends CoveragePoint {
  /** Soma dos pesos (questoes) dos topicos ja registrados. */
  studied_weight: number
  /** Soma dos pesos (questoes) de todos os topicos cobrados. */
  total_weight: number
}

/**
 * Agregado dos outros alunos medido sobre o MESMO conjunto de topicos do aluno atual.
 *
 * A tela le exclusivamente as medianas e o `cohort_size` (ver InlineCohortComparison):
 * as medias ficam opcionais para o backend nao pagar por um numero que ninguem mostra.
 * So cobertura: `median_pace`/`avg_pace` sairam na r7-02, com o ritmo atual.
 */
export interface CohortAggregate {
  cohort_size: number
  median_absolute_pct: number
  median_weighted_pct: number
  avg_absolute_pct?: number
  avg_weighted_pct?: number
}

export interface CohortBenchmark {
  user: {
    absolute_coverage_pct: number
    weighted_coverage_pct: number
  }
  platform: CohortAggregate | null
  desired_speciality: CohortAggregate | null
}

/**
 * Estagio de um topico, definido pelo NUMERO DE REVISOES registradas — nao por FSRS:
 *
 * - E0   estudo primario registrado, nenhuma revisao
 * - E1   1 revisao
 * - E2   2 revisoes
 * - E3+  3 ou mais revisoes
 *
 * `null` significa topico sem nenhum registro (status 'not_studied'), que por isso
 * fica de fora dos badges.
 */
export type ThemeStage = 'E0' | 'E1' | 'E2' | 'E3+'

/**
 * TENDENCIA DE DESEMPENHO do topico contra a janela anterior de mesmo tamanho
 * (`Dashboard::TopicTrend`, spec r2/04). Eixo ORTOGONAL ao `ThemeStage`: E0..E3+ diz
 * quantas vezes o aluno revisou, `ThemeTrend` diz se ele esta melhorando.
 *
 * `not_measurable` NAO e "neutro": e a ausencia de veredito — menos de
 * `MIN_SAMPLE_QUESTIONS` (10) questoes numa das duas janelas, ou nenhuma na anterior.
 * Nunca converta esse estado em 0 nem em queda.
 */
export type ThemeTrend = 'up' | 'flat' | 'down' | 'not_measurable'

export interface ThemeProgress {
  name: string
  status: 'studied' | 'not_studied'
  /** Share do topico em % (0-100, 1 casa) dentro das questoes daquela trilha. */
  prevalence: number | null
  stage: ThemeStage | null
  /**
   * Registros do topico em TODA a historia do aluno (primarios + revisoes), contados por
   * REGISTRO e nao por vinculo. NAO e recortado pelo `?period=` — Dashboard::ManualTopicRegistry
   * nao recebe periodo —, ao contrario de `unmatched_themes` e `simulados`.
   *
   * ATENCAO ao ler esta linha: desde o spec r2/04 ela mistura DUAS janelas. `study_count`
   * e `last_studied_at` sao vitalicios; `trend` e os cinco campos abaixo dele saem do
   * `?period=` e da janela anterior de mesmo tamanho (`ProgressAnalysisMeta.trend_window`).
   */
  study_count: number
  /** Ultimo registro do topico, tambem vitalicio. 'AAAA-MM-DD' | null */
  last_studied_at: string | null
  /**
   * Acerto em % (0-100, 1 casa) nas questoes do topico resolvidas na PLATAFORMA dentro da
   * janela atual. `null` = nenhuma questao na janela — NAO MENSURAVEL, nunca 0.0 fabricado.
   *
   * A fonte e so `exam_answers` (a unica das quatro de `Dashboard::ActivitySources` que
   * carrega `topic_id`). Estudo primario, revisao e simulado registrado a mao guardam
   * questoes/acertos NO REGISTRO e nao entram — ratea-los seria uma terceira regra de
   * desempenho por topico.
   */
  accuracy_current: number | null
  /** O mesmo acerto na janela ANTERIOR de igual tamanho. `null` = nada resolvido la. */
  accuracy_previous: number | null
  /**
   * `accuracy_current - accuracy_previous`, em pontos percentuais (1 casa). `null` quando
   * qualquer uma das duas e nula. Vem preenchido mesmo com `trend === 'not_measurable'`
   * por amostra pequena: o numero existe, o veredito e que nao — mesma disciplina do
   * `kpiTone` de trackStatus.ts.
   */
  delta_pp: number | null
  /** Questoes validas do topico na janela atual (denominador de `accuracy_current`). */
  questions_current: number
  questions_previous: number
  trend: ThemeTrend
  /**
   * O DESTINO DO "ver histórico" (R2 do spec r3/10): o tema a marcar no comparativo por
   * estágio de `/app/analise-metricas/:areaCode` e a trilha em que ele mora.
   *
   * `null` NÃO é campo faltando — é a resposta "não há histórico a abrir". Vem nulo
   * quando o tópico não tem ponte com `canonical_topics` ou quando o aluno não tem
   * progresso registrado no tema, que é a condição para a outra tela conhecê-lo. Como
   * esta tabela lista o denominador inteiro da banca, a maioria das linhas vem nula.
   *
   * `track_code` é resolvido pela especialidade do TEMA CANÔNICO, e por isso pode diferir
   * do `code` da trilha em que a linha aparece (esse sai da especialidade da QUESTÃO).
   * Ver `Dashboard::SpecialtyCoverage#history_for`.
   */
  canonical_topic_id: number | null
  track_code: string | null
}

export interface SpecialtyProgress {
  /** Code do `Dashboard::TrackCatalog` (CLM/CIR/PED/GO/PREV/OUTROS) — e a chave de cor. */
  code: string
  name: string
  absolute: CoveragePoint
  weighted: { pct: number }
  /**
   * Invariante do contrato: e0 + e1 + e2 + e3_plus === absolute.studied. Topico sem
   * nenhum registro nao entra em nenhum balde.
   */
  stage_distribution: { e0: number; e1: number; e2: number; e3_plus: number }
  /**
   * Invariante do contrato: up + flat + down + not_measurable === themes.length. Todo
   * topico da trilha cai em exatamente um balde — o que nunca foi respondido cai em
   * `not_measurable`, e nao fora da conta.
   *
   * A contagem vem do BACKEND de proposito: se a tela contasse as linhas que sobraram de
   * `?status=` / `?q=`, o cabecalho das categorias mudaria a cada digito buscado.
   */
  trend_distribution: TrendDistribution
  themes: ThemeProgress[]
}

/** Contagem dos quatro estados de `ThemeTrend`. As quatro chaves sempre presentes. */
export interface TrendDistribution {
  up: number
  flat: number
  down: number
  not_measurable: number
}

export interface InstitutionProgress {
  /**
   * Rank DENSO (`index + 1` sobre os nomes nao vazios). NAO e o mesmo `rank` de
   * `InstitutionGuide`, que e o SLOT 1..5 e tem buracos: o aluno que deixou a 1a opcao
   * em branco tem bancas diferentes sob o mesmo rank nas duas telas. O backend junta as
   * duas metricas por `institutions.id`, nunca por rank.
   */
  rank: number
  name: string
  matched_name: string | null
  absolute: CoveragePoint
  weighted: { pct: number }
  /** Prontidao da banca (decisao 4.2: combinada no hero, por banca no detalhe). */
  readiness: InstitutionReadiness
}

export interface ProgressSimuladoRecent {
  id: number
  hash_id: string | null
  /**
   * `exam_attempts` deixou de ser polimorfico e nao tem coluna `exam_type`: o valor vem
   * da origem da prova (`exams.source` — 'medway', 'medcof' ou 'manual') e pode ser nulo
   * quando a prova sumiu. A UI cai para '—' apenas nesse caso nulo; origem nao mapeada
   * ela imprime como veio.
   */
  exam_type: string | null
  exam_id: number
  status: string
  total_questions: number
  answered_count: number
  correct_count: number
  score: number | null
  completed_at: string | null
}

export interface ProgressSimuladoSummary {
  total: number
  completed: number
  questions_practiced: number
  recent: ProgressSimuladoRecent[]
}

export interface ProgressAnalysisMeta {
  selection_process: string
  // Data alvo, semanas restantes e a flag de data alvo sairam (r6-20): sem prazo final
  // na V1 nao ha data alvo — e chave sempre nula seria contrato mentiroso.
  /** As duas janelas que a coluna de tendencia compara. Base do rotulo da tabela. */
  trend_window: TrendWindow
}

/**
 * A comparacao e sempre contra a JANELA ANTERIOR DE MESMO TAMANHO, e nao contra "a semana
 * anterior": uma semana fixa dentro de um periodo de 24 daria uma seta que muda a cada
 * refresh. Com `?period=all` a janela anterior cai inteira antes da primeira atividade do
 * aluno e a coluna fica toda "sem tendencia" — a tela precisa dizer isso.
 */
export interface TrendWindow {
  weeks: number
  /** 'AAAA-MM-DD'. */
  current_start: string
  current_end: string
  previous_start: string
  previous_end: string
}

/**
 * PRONTIDAO combinada — `Dashboard::ReadinessSummary#overall`, que so LE
 * `StudyJourneyService` (`resolucao * 0.4 + acerto * 0.6`). Nao ha segunda
 * implementacao: `combined_score` e `status` sao byte a byte os mesmos que
 * `StatisticalGuide.summary` traz, e um teste de paridade no backend garante isso.
 *
 * Aluno sem banca casada: `{ combined_score: 0, status: 'no_data' }` — zeros honestos,
 * sem uma query. Passe `status` por `resolveJourneyStatus` antes de exibir.
 */
export interface ReadinessScore {
  combined_score: number
  status: string
}

/** PRONTIDAO de UMA banca — `institutions[].journey_score` do guia, mesmo numero. */
export interface InstitutionReadiness {
  score: number
  status: string
}

export interface ProgressAnalysisOverall {
  absolute_coverage: CoveragePoint
  weighted_coverage: WeightedCoveragePoint
  /**
   * Numero principal da tela desde o spec 04 (R3 + decisao 4.2). NAO depende do
   * `?period=`: prontidao e vitalicia.
   *
   * Sem ritmo: a r6-20 tirou o ritmo alvo e a projecao, e a r7-02 tirou o ritmo atual
   * (`current_pace`) — sem prazo final ele nao diz nada ao aluno.
   */
  readiness: ReadinessScore
  /**
   * Os quatro estados sobre TODOS os topicos do universo (distintos). Soma sempre
   * `absolute_coverage.total`. E o numero do cabecalho das categorias expansiveis — nao
   * confundir com o `trend_distribution` de cada trilha, que soma `themes.length` e pode
   * contar o mesmo topico em duas trilhas.
   */
  trend_distribution: TrendDistribution
}

export interface ProgressAnalysisResponse {
  meta: ProgressAnalysisMeta
  overall: ProgressAnalysisOverall
  /**
   * Vazio so quando o aluno nao configurou nenhuma instituicao prioritaria. Nome digitado
   * que nao existe no catalogo CONTINUA na lista, com `matched_name: null` e zeros.
   */
  institutions: InstitutionProgress[]
  specialties: SpecialtyProgress[]
  simulados: ProgressSimuladoSummary
  /** Registros manuais do periodo que nao casaram com nenhum topico do catalogo. */
  unmatched_themes: { count: number; pct: number }
  cohort: CohortBenchmark
}

// API Error class
export class ApiError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

// ==================== AULAS DO CURSINHO POR TOPICO ====================
//
// GET /api/v1/course_lessons?topic_id= — as aulas do cursinho DO ALUNO que cobrem um
// topico do catalogo. Consumido pelo cartao de estudo primario de /app/jornada.

export interface TopicCourseLesson {
  id: number
  code: string
  title: string
  module_name: string | null
  specialty: string | null
  academic_year: number
  duration_minutes: number | null
  video_url: string | null
  /** Quantas vezes o aluno ja registrou estudo desta aula. 0 = nunca. */
  study_count: number
  last_studied_at: string | null
}

export interface TopicCourseLessonsResponse {
  success: boolean
  /**
   * `null` quando `users.preparation_course` (texto livre) nao casa com nenhum cursinho
   * do catalogo, ou esta em branco. NAO e erro: a tela usa isso para pedir que o aluno
   * informe o cursinho, em vez de mostrar falha.
   */
  prep_course: { id: number; name: string } | null
  /** O que o aluno digitou, para a tela conseguir dizer o que nao casou. */
  declared_course: string | null
  lessons: TopicCourseLesson[]
}

// ==================== TEMPO EXTRA (estudo passivo) ====================
//
// Espelho manual de `medhunter-api/app/controllers/api/v1/extra_study_times_controller.rb`
// e do model `ExtraStudyTime`. Nao ha schema compartilhado entre os repos: mudar o payload
// no Rails exige mudar tambem src/lib/api.ts e esta secao, ou a tela quebra em silencio.
//
// UNIDADE: `duration_minutes` esta em MINUTOS, ao contrario das colunas legadas de TED
// (que estao em horas e o backend converte). Nada no frontend converte este campo — o
// `formatDuration` de @/lib/utils ja recebe minutos.
//
// FRONTEIRA: flashcard NAO e tempo extra. Flashcard e recuperacao ativa, entao e TED
// teorico ativo e se registra em Estudos. A regra esta impressa no ExtraTimeModal.

/** Conjunto FECHADO — espelho de `ExtraStudyTime::KINDS`. */
export type ExtraStudyTimeKind =
  | 'aula_assistida'
  | 'leitura_apostila'
  | 'leitura_artigo'
  | 'videoaula'
  | 'outro'

export interface ExtraStudyTimeKindOption {
  value: ExtraStudyTimeKind
  label: string
}

export interface ExtraStudyTime {
  id: number
  /** 'AAAA-MM-DD'. Mesma semantica de `study_date`: o dia da atividade, nao o do registro. */
  activity_date: string
  kind: ExtraStudyTimeKind
  /** Rotulo em portugues servido pelo backend — a SPA nao mantem uma segunda traducao. */
  kind_label: string
  /** MINUTOS. */
  duration_minutes: number
  description: string | null
  /** Decisao 4.3 da spec: especialidade OPCIONAL, e so ela (sem topico). */
  specialty_id: number | null
  specialty_name: string | null
  source: string
  created_at: string
  updated_at: string
}

/**
 * Total PROPRIO da sub-aba, em MINUTOS. Decisao 4.2: o "Tempo Total" de Estudos e o de
 * Simulados continuam significando tempo ATIVO e nao mudaram.
 */
export interface ExtraStudyTimeStats {
  total_entries: number
  total_minutes: number
  /** Todas as cinco chaves sempre presentes, inclusive em zero. */
  by_kind: Record<ExtraStudyTimeKind, number>
}

export interface CreateExtraStudyTimeData {
  activity_date: string
  kind: ExtraStudyTimeKind
  duration_minutes: number
  description?: string | null
  specialty_id?: number | null
}

export interface ExtraStudyTimesResponse {
  success: boolean
  extra_study_times: ExtraStudyTime[]
  stats: ExtraStudyTimeStats
  kind_options: ExtraStudyTimeKindOption[]
}

// ============================================================================
// Flashcards — sessao de revisao mista, sem tema (.spec/flashcards.md, caso 2)
// ----------------------------------------------------------------------------
// Espelho de `FlashcardSessionsController`. TED ATIVO (entra no TED como o formato
// "Flashcards"), mas nao move retencao. `minutes` em MINUTOS nos dois sentidos.
// ============================================================================

/** Conjunto FECHADO — espelho de `FlashcardSession::TOOLS`. */
export type FlashcardTool = 'anki' | 'outro'

export interface FlashcardToolOption {
  value: FlashcardTool
  label: string
}

export interface FlashcardSession {
  id: number
  study_date: string
  minutes: number
  /** Informativo (R5): so entra no total e na media da sub-aba. */
  cards_reviewed: number | null
  tool: FlashcardTool
  tool_label: string
  specialty_id: number | null
  specialty_name: string | null
  description: string | null
  created_at: string
  updated_at: string
}

export interface FlashcardSessionStats {
  total_sessions: number
  total_minutes: number
  total_cards: number
  active_days: number
  average_minutes_per_day: number
}

export interface CreateFlashcardSessionData {
  study_date: string
  minutes: number
  cards_reviewed?: number | null
  tool: FlashcardTool
  specialty_id?: number | null
  description?: string | null
}

export interface FlashcardSessionsResponse {
  success: boolean
  flashcard_sessions: FlashcardSession[]
  stats: FlashcardSessionStats
  tool_options: FlashcardToolOption[]
}

// ---------------------------------------------------------------------------
// Documentos legais (paginas publicas de Termos e Politica)
// ---------------------------------------------------------------------------

export type LegalDocumentKind = 'terms' | 'privacy' | 'image_consent' | 'marketing_consent'
export type LegalDocumentStatus = 'draft' | 'published' | 'archived'

export interface LegalDocument {
  id: number
  kind: LegalDocumentKind
  /** Nome legivel do tipo ("Termos de Uso", "Politica de Privacidade"...). */
  label: string
  version: number
  title: string
  /** true = obrigatorio para usar a plataforma; false = consentimento opcional. */
  required: boolean
  status: LegalDocumentStatus
  /** SHA-256 do corpo publicado. Rascunho nao tem. */
  content_sha256: string | null
  published_at: string | null
  /** Markdown. */
  body: string
}

export interface LegalDocumentsResponse {
  success: boolean
  documents: LegalDocument[]
}

// ==================== Checkout (r7-19) ====================
// Espelho de `Api::V1::CheckoutController` / `Checkout::Quote`. Todo valor aqui e
// CALCULADO NO SERVIDOR; o front so exibe. O pedido nunca leva preco, desconto nem
// cartao — o cartao e digitado so na fatura do Asaas (`payment_url`).

export interface CheckoutQuoteProduct {
  id: number
  name: string
  description: string | null
  duration_description: string
  /** r7-20: preco cheio do produto e teto de parcelas dele. */
  price: number
  max_installments: number
  /** R10-07: plano gratuito — o checkout libera o acesso sem o Asaas. */
  free?: boolean
}

/** r7-20: `GET /checkout/products/:id` — lista branca, sem custo/margem. */
export interface CheckoutProductResponse {
  success: boolean
  product: CheckoutQuoteProduct
}

export interface CheckoutInstallmentOption {
  count: number
  /** Valor de cada parcela (total / count). */
  value: number
  /**
   * R11-12: total cobrado no Asaas nesta opcao. De 2x em diante inclui a taxa do Asaas
   * quando `CheckoutQuote.fee_included`; a vista e o valor final.
   */
  total: number
}

export interface CheckoutQuote {
  product: CheckoutQuoteProduct
  base_price: number
  discount_amount: number
  final_amount: number
  coupon: { code: string; name: string; free_pass: boolean } | null
  /** R10-07: plano gratuito (sem parcelas, sem cupom, sem Asaas). */
  free?: boolean
  payment_method: 'credit_card'
  max_installments: number
  installment_options: CheckoutInstallmentOption[]
  /** R11-12: falso quando as taxas do Asaas nao puderam ser lidas (parcelas sem taxa). */
  fee_included: boolean
}

export interface CheckoutQuoteResponse {
  success: boolean
  quote: CheckoutQuote
}

/** Codigos de erro do `GET /checkout/quote` e do `POST /checkout/orders`. */
export type CheckoutErrorCode =
  | 'product_not_found'
  | 'coupon_invalid'
  | 'coupon_expired'
  | 'coupon_exhausted'
  | 'coupon_product'
  | 'coupon_email'
  | 'coupon_used'
  | 'coupon_minimum'
  | 'card_data_not_accepted'
  /** MH14-11: nenhuma parcela chega a R$ 5,00 (minimo do Asaas), ou parcelas forjadas acima do teto valido. */
  | 'installment_minimum'

/** Corpo do "criar venda": so identificadores e dados do comprador. */
export interface CheckoutOrderRequest {
  produto: string
  cupom?: string
  installments: number
  name: string
  email: string
  cpf: string
  phone: string
}

export interface CheckoutOrder {
  id: number
  /** `created` (cobranca nova), `reused` (pedido pendente igual), `approved` (free pass). */
  state: 'created' | 'reused' | 'approved'
  status: 'pending' | 'completed' | 'active'
  product_name: string
  amount: number
  discount_amount: number
  installments: number
  /** Fatura do Asaas onde o aluno digita o cartao. `null` no free pass. */
  payment_url: string | null
}

export interface CheckoutOrderResponse {
  success: boolean
  order: CheckoutOrder
}

// ============================================================================
// VENDA-03 — pagina publica do link de venda (`/pagar/:token`)
// ----------------------------------------------------------------------------
// Espelho manual de `SalesLinks::PublicView` (medhunter-api). Lista branca: so o bruto
// (o que o cliente paga); liquido, taxas, vendedor e nota nunca chegam aqui.
// ============================================================================

/** `expired`/`cancelled` vem SEM valores (so `state`). */
export type PublicSalesLinkState = 'open' | 'used' | 'partially_paid' | 'paid' | 'expired' | 'cancelled'

export interface PublicSalesLinkPart {
  position: number
  payment_method: 'pix' | 'credit_card'
  installments: number
  gross_amount: number
  installment_value: number
  /** 1, 2, 3... entre os cartoes ("Pagar cartão N"); `null` no Pix. */
  card_number: number | null
  /** Os campos abaixo so existem depois da confirmacao (state != open). */
  status?: 'pending' | 'paid' | 'failed' | 'cancelled'
  paid_at?: string | null
  /** Fatura do Asaas (so pendente, so dominio do Asaas). */
  payment_url?: string | null
  /** PNG em base64 (sem o prefixo `data:`). */
  pix_qr_code?: string | null
  pix_copy_paste?: string | null
}

export interface PublicSalesLinkOpen {
  state: Exclude<PublicSalesLinkState, 'expired' | 'cancelled'>
  expires_at: string
  plan: { name: string; description: string | null; duration_description: string | null }
  gross_total: number
  /** O plano tem contrato (R12-02): assinado na plataforma depois do pagamento. */
  contract_required: boolean
  /** O link foi gerado para um e-mail/CPF especifico (o servidor recusa outro). */
  buyer_locked: { email: boolean; cpf: boolean }
  parts: PublicSalesLinkPart[]
}

export interface PublicSalesLinkClosed {
  state: 'expired' | 'cancelled'
}

export type PublicSalesLink = PublicSalesLinkOpen | PublicSalesLinkClosed

export interface PublicSalesLinkResponse {
  success: boolean
  sales_link: PublicSalesLink
}

/** Corpo de `POST /sales_links/:token`. Nenhum valor: o servidor recusa (`amount_not_accepted`). */
export interface SalesLinkBuyerPayload {
  name: string
  email: string
  cpf: string
  phone: string
}

// ============================================================================
// r7-25 — Planos: features por plano e acesso do usuario
// ----------------------------------------------------------------------------
// Espelho manual de `PlanAccess::FeatureCatalog` (medhunter-api). A chave e estavel e
// e gravada em `asaas_plans.features`; nome e descricao vem do endpoint admin
// `GET /admin/products/features`, nunca daqui.
// ============================================================================

export type PlanFeatureKey =
  | 'dashboard'
  | 'jornada'
  | 'calendario'
  | 'meta_ted'
  | 'central_do_aprovado'
  | 'registro_estudos'
  | 'cronometro'
  | 'analise_metricas'
  | 'analise_estatistica'
  | 'provas_integra'
  | 'simulados'
  | 'video_aulas'
  | 'aulas_ao_vivo'
  | 'videos_acompanhamento'
  | 'relato_dificuldades'
  | 'match_hunter'
  | 'conversas'
  | 'integracoes'

/** Plano que libera acesso ao usuario hoje (venda paga ou vinculo manual). */
export interface PlanAccessPlan {
  id: number
  name: string
  expires_at: string | null
  manual: boolean
  /**
   * R10-07/R10-08: plano de preco zero e o checkout do plano pago a oferecer no fim do
   * acesso (null sem upgrade ativo). Opcionais: `user` antigo no localStorage nao tem.
   */
  free?: boolean
  upgrade_checkout_path?: string | null
  upgrade_plan_name?: string | null
}

/**
 * O que o menu do aluno faz com a feature que o plano NAO libera (`asaas_plans.locked_features_display`):
 * `hide` some do menu; `locked` aparece com cadeado e opacidade reduzida.
 */
export type LockedFeaturesDisplay = 'hide' | 'locked'

/** `User#plan_access` — devolvido em `/auth/me` como `user.plan_access`. */
export interface PlanAccess {
  has_access: boolean
  /** Admin e instrutor: acesso total, todas as features. */
  full_access: boolean
  features: PlanFeatureKey[]
  plans: PlanAccessPlan[]
  /**
   * Algum plano ativo pede `locked`: o que falta aparece bloqueado no menu, em vez de sumir.
   * Opcional: `user` antigo no localStorage nao tem o campo (vale como `hide`).
   */
  show_locked_features?: boolean
}

export interface PlanFeatureDefinition {
  key: PlanFeatureKey
  name: string
  description: string
}

export interface PlanFeatureGroup {
  key: string
  label: string
  features: PlanFeatureDefinition[]
}

/** `GET /admin/products/features` */
export interface PlanFeatureCatalogResponse {
  success: boolean
  groups: PlanFeatureGroup[]
}

/** Vinculo usuario -> plano no admin (`/admin/users/:id/plans`). */
export interface AdminUserPlanLink {
  id: number
  plan: { id: number; name: string }
  status: 'active' | 'inactive' | 'completed' | 'expired' | 'pending'
  manual: boolean
  grants_access: boolean
  starts_at: string | null
  expires_at: string | null
  created_at: string
  /** Removido pelo admin: sem acesso, mesmo que a venda siga ativa no Asaas. */
  revoked_at: string | null
  /** E-mail do admin que removeu. */
  revoked_by: string | null
  /** Parcelas pendentes ou em atraso no Asaas (0 em vinculo manual). */
  open_payments: number
  /** r7-21: pagamento confirmado com valor divergente; o webhook nao liberou o acesso. */
  review_reason: string | null
  review_flagged_at: string | null
}

/** MH13-40 — `POST /admin/users/:id/plans`: vinculo manual, com opcao de nao mandar as boas-vindas. */
export interface AdminGrantUserPlanPayload {
  asaas_plan_id: number
  expires_at: string
  /** Padrao no servidor: envia. So `false` dispensa o e-mail de boas-vindas. */
  send_welcome_email?: boolean
}

/** MH13-40 — o que aconteceu com o e-mail de boas-vindas do plano no vinculo. */
export type AdminPlanWelcomeEmailResult =
  | 'sent'
  | 'skipped'
  | 'already_sent'
  | 'free_plan'
  | 'not_student'
  | 'failed'

export interface AdminGrantUserPlanResponse {
  success: boolean
  message: string
  plan: AdminUserPlanLink
  welcome_email: AdminPlanWelcomeEmailResult
}

/** `GET /admin/users/:id/history` — historico de edicao do usuario. */
export type UserHistoryActorKind = 'self' | 'admin' | 'system'

export type UserHistoryEvent = 'user.update' | 'plan.grant' | 'plan.revoke'

export interface UserHistoryEntry {
  id: number
  event: UserHistoryEvent
  actor_kind: UserHistoryActorKind
  /** Nome de quem fez; nulo para o sistema (ou rotulo, como "ActiveAdmin (email)"). */
  actor: string | null
  /** So em `user.update`: campo -> valor antes e depois. Papel e objetivo vem pelo nome. */
  changes: Record<string, { from: unknown; to: unknown }>
  /** So em `plan.*`: plano, se era vinculo manual, validade e parcelas em aberto. */
  metadata: {
    plan_name?: string
    manual?: boolean
    expires_at?: string | null
    open_payments?: number
  }
  created_at: string
}

export interface AdminUserHistoryResponse {
  success: boolean
  entries: UserHistoryEntry[]
  page: number
  per_page: number
  total: number
  total_pages: number
}

export interface AdminUserPlansResponse {
  success: boolean
  plans: AdminUserPlanLink[]
  access: PlanAccess
}

/** Situacao de acesso de um vinculo: libera hoje, nao libera, ou venceu. */
export type PlanLinkAccessStatus = 'active' | 'inactive' | 'expired'

/** Linha de `GET /admin/plan_links` — vinculo aluno -> plano cadastrado (tela /admin/subscriptions). */
export interface AdminPlanLink {
  id: number
  user: { id: number; name: string; email: string }
  plan: { id: number; name: string }
  status: AdminUserPlanLink['status']
  access_status: PlanLinkAccessStatus
  manual: boolean
  total_value: number | null
  starts_at: string | null
  expires_at: string | null
  created_at: string
}

export interface AdminPlanLinksResponse {
  success: boolean
  plan_links: AdminPlanLink[]
  pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
  stats: { total: number; active: number; inactive: number; expired: number; manual: number; expiring_this_week: number }
}

export interface AdminPlanLinksParams {
  page?: number
  per_page?: number
  search?: string
  status?: PlanLinkAccessStatus | 'manual'
  plan_id?: number
}

/**
 * Unidade da duracao do plano (`AsaasPlan.interval`). R10-07: `day` para o gratuito.
 * MH14-12: `fixed_date` = acesso ate `fixed_end_date`, qualquer que seja o dia da compra.
 */
export type PlanInterval = 'month' | 'year' | 'day' | 'fixed_date'

/** Corpo do 402/403 do gate de plano. */
export type PlanGateErrorCode = 'subscription_required' | 'feature_not_in_plan'

/** `GET /payments/plans` — planos ativos a venda (publico). Usado pela tela "sem plano". */
export interface PublicPlan {
  id: number
  /** r7-25: endereco do checkout, `/checkout/<slug>`. */
  slug: string
  name: string
  description: string | null
  price: number
  interval: PlanInterval
  duration_years: number
  /** MH14-12: 'YYYY-MM-DD' com `interval` `fixed_date`; null nos outros. */
  fixed_end_date?: string | null
  duration_description: string
  installment_value: number
  price_with_interest: number
}

// ---------------------------------------------------------------------------
// Prova principal do aluno (R08-02) — espelho de `MainExam.payload` (medhunter-api)
// ---------------------------------------------------------------------------

/**
 * Data-alvo + instituicao-alvo da prova principal. Vem de `GET/PATCH /main_exam` (o
 * proprio aluno) e de `main_exam` em cada aluno de `/instructor/students`.
 *
 * `institution` e a 1a escolha das instituicoes prioritarias — so leitura aqui.
 * `days_until` e null sem data (nao medido nao e zero) e negativo se a prova ja passou.
 */
export interface MainExam {
  institution: string | null
  /** 'YYYY-MM-DD' — 1a fase do processo seletivo aprovado quando houver (HERMES); senao, a data manual. */
  exam_date: string | null
  days_until: number | null
  /** INST-05: de onde veio `exam_date` — edital cadastrado pelo admin ou data manual (R08-02). */
  /** Opcional so para os fixtures antigos; a API sempre manda. */
  exam_date_source?: MainExamDateSource | null
  /** A data manual de R08-02 (fallback), que e o que o editor grava. */
  manual_exam_date?: string | null
}

export type MainExamDateSource = 'notice' | 'manual'

/** Corpo do `PATCH /main_exam` e do `PATCH /instructor/students/:id/main_exam`. */
export interface MainExamUpdatePayload {
  exam_date: string | null
}

// ---------------------------------------------------------------------------
// Parametros do ciclo (R08-05) — espelho de `CycleParameters::Report` (medhunter-api)
// ---------------------------------------------------------------------------

/**
 * As seis metas do ciclo, na ordem do payload. MH9-03: a prontidao saiu do ciclo (a projecao
 * da R08-16 continua em `/instructor/students/:id/readiness_projection`).
 */
export type CycleParameterKey = 'ted' | 'primary_study' | 'revision' | 'exams' | 'questions' | 'retention'

/** Quem setou o alvo: padrinho, aluno (Modo Essential, R08-22) ou sistema (R08-06). */
export type CycleParameterOrigin = 'mentor' | 'student' | 'system'

/**
 * Unidade do alvo e do medido. `themes`, `exams` e `questions` sao contagens POR SEMANA
 * (MH9-03); `hours_per_week` e taxa; `percent` vai de 0 a 100.
 */
export type CycleParameterUnit = 'hours_per_week' | 'themes' | 'exams' | 'questions' | 'percent'

/**
 * MH9-03: sempre a SEMANA (segunda a domingo). Mes corrente: a semana de hoje (pode comecar no
 * mes anterior); mes encerrado: a ultima semana completa dentro dele.
 */
export interface CycleParameterWindow {
  kind: 'week'
  /** 'YYYY-MM-DD' */
  start_date: string
  /** 'YYYY-MM-DD' */
  end_date: string
  days: number
  /** Dias decorridos, contando hoje. 0 quando a janela nao comecou. */
  elapsed_days: number
  complete: boolean
}

/**
 * Um parametro: alvo · medido · desvio. Nao medido nao e zero — `measured` null com
 * `status` 'not_started' (janela futura).
 *
 * `expected` e o alvo pro-rata dos dias decorridos nas contagens, e o proprio alvo nas
 * taxas. `deviation` = measured - expected; `deviation_pct` e null para percentuais.
 */
export interface CycleParameter {
  key: CycleParameterKey
  unit: CycleParameterUnit
  window: CycleParameterWindow
  target: number | null
  origin: CycleParameterOrigin | null
  set_by: { id: number; name: string } | null
  updated_at: string | null
  status: 'measured' | 'not_started'
  measured: number | null
  expected: number | null
  deviation: number | null
  deviation_pct: number | null
  /**
   * Varia por parametro: `realized_minutes` (ted); `primary`/`revision`/`exams` (questions);
   * `records` + `time_cost` (primary_study, revision), `by_type` (revision:
   * theory/questions/mixed/unspecified); `without_correction` (exams); contadores do IR
   * (retention). Vazio quando nao medido.
   */
  detail: CycleParameterDetail
}

export interface CycleParameterTimeCost {
  minutes_per_theme: number
  records_used: number
  themes_used: number
  calibrated: boolean
  source: 'student_log' | 'profile_default'
  profile: string | null
}

export interface CycleParameterDetail {
  realized_minutes?: number
  primary?: number
  revision?: number
  exams?: number
  records?: number
  time_cost?: CycleParameterTimeCost
  by_type?: Record<'theory' | 'questions' | 'mixed' | 'unspecified', number>
  without_correction?: number
  topics_measured?: number
  topics_dropped?: number
  topics_below_floor?: number
  topics_block_only?: number
}

/** `GET /cycle_parameters` e `GET|PUT /instructor/students/:id/cycle_parameters`. */
export interface CycleReport {
  /** 'YYYY-MM' */
  month: string
  /** 'YYYY-MM-DD' — hoje, ou o fim do mes se ele ja terminou. */
  as_of: string
  parameters: CycleParameter[]
}

/**
 * Corpo do `PUT /instructor/students/:id/cycle_parameters`. Valor null apaga o alvo;
 * chave ausente fica como estava. So o mes corrente e os seguintes.
 */
export interface CycleParametersUpdatePayload {
  /** 'YYYY-MM' */
  month: string
  parameters: Partial<Record<CycleParameterKey, number | null>>
}

/**
 * Base da proposta do sistema (R08-06, `CycleParameters::Proposal`): o alvo do mes anterior,
 * o medido do mes anterior (janela fechada, valor > 0) ou, so no TED, a meta TED semanal do
 * proprio aluno.
 */
export type CycleProposalBasis = 'previous_target' | 'previous_measured' | 'student_ted_goal'

export interface CycleProposalEntry {
  /** Na unidade do alvo; inteiro nas contagens. */
  value: number
  basis: CycleProposalBasis
}

/**
 * Proposta do sistema para o mes. Calculada na leitura, nunca gravada. `values[key]` null =
 * sem base para propor (nunca zero inventado). Mes encerrado: `editable` false e tudo null.
 * Aceitar o valor proposto sem mudar grava `origin: 'system'`.
 */
export interface CycleProposal {
  editable: boolean
  /** Momento do ciclo, so como contexto (nao ajusta valor). */
  cycle_moment: MainExam
  values: Record<CycleParameterKey, CycleProposalEntry | null>
}

export interface CycleCoherenceCostLine {
  themes: number
  minutes_per_theme: number
  minutes: number
  /** false = custo default do perfil, nao do log do aluno (R08-03). */
  calibrated: boolean
}

export interface CycleCoherenceConflict {
  /** `ted_missing` so no Modo Essential (R08-22): temas pedidos sem TED bloqueiam o salvar. */
  code: 'ted_below_themes' | 'ted_missing'
  keys: CycleParameterKey[]
  /** Texto pronto em pt-BR. */
  message: string
}

/**
 * Coerencia dos alvos na semana (R08-06, `CycleParameters::Coherence`; MH9-03): TED x 7/7 dias
 * contra temas novos x custo + revisoes x custo. `incomplete` = sem TED ou sem temas. Para o
 * padrinho o conflito avisa, nao bloqueia. `limits_pending`: a tolerancia ainda e
 * placeholder (R08-27).
 */
export interface CycleCoherence {
  status: 'ok' | 'conflict' | 'incomplete'
  horizon_days: number
  tolerance: number
  limits_pending: boolean
  budget_minutes: number | null
  required_minutes: number | null
  required_ted_hours_per_week: number | null
  breakdown: Partial<Record<'primary' | 'revision', CycleCoherenceCostLine>>
  conflicts: CycleCoherenceConflict[]
}

/** `GET|PUT /instructor/students/:id/cycle_parameters` (R08-06 acrescentou proposta e coerencia). */
export interface InstructorCycleParametersResponse {
  success: boolean
  cycle: CycleReport
  proposal: CycleProposal
  /** Coerencia dos alvos SALVOS do mes. */
  coherence: CycleCoherence
  /** MH9-04: estado do ciclo do mes. So o endpoint do padrinho manda. */
  month_status?: CycleMonthStatus
}

// ---------------------------------------------------------------------------
// MH9-14 — sugestao do ciclo pela IA (`CycleSuggestions::Presenter`, medhunter-api)
// ---------------------------------------------------------------------------

/**
 * `pending` aguarda o padrinho; `accepted` aplicada como veio; `edited` aplicada depois de
 * ajustada no formulario do ciclo; `dismissed` descartada; `superseded` trocada por outra
 * geracao do mesmo mes.
 */
export type CycleSuggestionStatus = 'pending' | 'accepted' | 'edited' | 'dismissed' | 'superseded'

export interface CycleSuggestionItem {
  key: CycleParameterKey
  unit: CycleParameterUnit
  /** O alvo que vale hoje (do mes sugerido, se ja salvo; senao do mes anterior). null = sem alvo. */
  current_target: number | null
  suggested_target: number
  changed: boolean
  /** Motivo em pt-BR, escrito pela IA. */
  reason: string
}

/** O que embasou a sugestao (resumo para o card). */
export interface CycleSuggestionBasis {
  exam?: { date: string; days_until: number | null; institution: string | null } | null
  reference_month?: { month: string; partial: boolean; attainment_pct: number | null } | null
  commitments?: {
    totals: {
      cumpridos: number
      nao_cumpridos: number
      repactuados: number
      abertos_no_prazo: number
      abertos_vencidos: number
    }
    fulfillment_pct: number | null
  } | null
  /** Datas ('YYYY-MM-DD') das 1:1 transcritas lidas. */
  meetings?: string[]
}

export interface CycleSuggestion {
  id: number
  student_id: number
  /** 'YYYY-MM' — o mes do ciclo sugerido. */
  month: string
  status: CycleSuggestionStatus
  source: 'on_demand' | 'monthly_job'
  summary: string | null
  items: CycleSuggestionItem[]
  basis: CycleSuggestionBasis
  /** Alvos do mes depois de aceitar/editar. */
  applied_targets: Partial<Record<CycleParameterKey, number>> | null
  model: string | null
  usage: { prompt_tokens: number; completion_tokens: number; cost_usd: number | null }
  created_at: string
  decided_at: string | null
  decided_by: { id: number; name: string } | null
}

/** `GET /instructor/students/:id/cycle_suggestion?month=YYYY-MM` (padrao: mes seguinte). */
export interface CycleSuggestionResponse {
  success: boolean
  month: string
  /** O padrinho tem o assistente de IA liberado (MH9-13) e pode gerar. */
  can_generate: boolean
  /** A mais recente do padrinho para o mes, de qualquer status. */
  suggestion: CycleSuggestion | null
}

/** POST de gerar (201) e das decisoes (accept, apply_edit, dismiss). */
export interface CycleSuggestionMutationResponse {
  success: boolean
  suggestion: CycleSuggestion
}

/**
 * MH9-06 — estado de um parametro na semana (CycleParameters::WeeklySeries): `met` alcancou o
 * alvo; `on_track` contagem em semana em curso, no esperado pro-rata; `below` abaixo do
 * esperado; `no_target` sem alvo no mes; `no_data` sem medida (retencao sem topico medido).
 */
export type CycleWeekState = 'met' | 'on_track' | 'below' | 'no_target' | 'no_data'

export interface CycleWeekParameter {
  key: CycleParameterKey
  unit: CycleParameterUnit
  target: number | null
  measured: number | null
  /** Contagem: alvo pro-rata dos dias decorridos; taxa: o proprio alvo. */
  expected: number | null
  /** medido / alvo em %, sem teto (alvo zero = 100). Nulo sem alvo ou sem medida. */
  attainment_pct: number | null
  state: CycleWeekState
}

export interface CycleWeek {
  /** 'YYYY-MM-DD' (segunda) */
  start_date: string
  /** 'YYYY-MM-DD' (domingo) */
  end_date: string
  days: number
  elapsed_days: number
  complete: boolean
  /** A semana de hoje. */
  current: boolean
  parameters: CycleWeekParameter[]
}

/**
 * MH9-12 — grupos do relatorio do ciclo da carteira (CycleParameters::MonthlyReport):
 * nota media >= `met_pct` atingiu; >= `far_below_pct` nao atingiu; abaixo, bem abaixo.
 * `no_cycle`: o mes nao tem nenhum alvo.
 */
export type CycleReportGroup = 'met' | 'missed' | 'far_below' | 'no_cycle'

export interface CycleReportMetric {
  key: CycleParameterKey
  unit: CycleParameterUnit
  /** Meta semanal do mes. */
  target: number
  /** Media semanal medida (semanas com medida). */
  average_measured: number | null
  /** Media das semanas, cada uma com teto de 100. Semana em curso: contra o esperado pro-rata. */
  attainment_pct: number | null
  weeks_met: number
  weeks_measured: number
}

export interface CycleReportStudent {
  id: number
  name: string
  /** Media dos parametros; nula quando nada foi medido. Ausente em `no_cycle`. */
  score_pct?: number | null
  weeks?: number
  metrics?: CycleReportMetric[]
  /** So em `no_cycle`: `to_review` se o mes anterior tinha alvo. */
  cycle_status?: 'missing' | 'to_review'
}

export interface MonthlyCycleReport {
  /** 'YYYY-MM' */
  month: string
  /** Mes corrente, ainda em curso. */
  partial: boolean
  as_of: string
  thresholds: { met_pct: number; far_below_pct: number; pending: boolean }
  counts: Record<CycleReportGroup, number>
  groups: Record<CycleReportGroup, CycleReportStudent[]>
}

/** `GET /instructor/cycle_report?month=YYYY-MM` (MH9-12). Sem `month`: o mes anterior. */
export interface CycleReportResponse {
  success: boolean
  cycle_report: MonthlyCycleReport
}

/** `GET /instructor/students/:id/cycle_weeks?month=YYYY-MM` (MH9-06). */
export interface CycleWeeksResponse {
  success: boolean
  cycle_weeks: {
    /** 'YYYY-MM' */
    month: string
    as_of: string
    /** O mes tem algum alvo. */
    defined: boolean
    /** Semanas com segunda no mes ate a auditada, mais a auditada (pode comecar no mes anterior). */
    weeks: CycleWeek[]
  }
}

/**
 * MH9-04 — `defined`: o mes tem algum alvo; `to_review`: o mes esta vazio e o anterior tinha
 * (virada do mes); `missing`: nenhum dos dois. Espelho de CycleParameters::PendingMonth.status_for.
 */
export interface CycleMonthStatus {
  status: 'defined' | 'missing' | 'to_review'
  /** 'YYYY-MM' */
  month: string
  /** 'YYYY-MM' */
  previous_month: string
}

/** Corpo do `POST /instructor/students/:id/cycle_parameters/coherence` (nao grava). */
export type CycleCoherenceCheckPayload = CycleParametersUpdatePayload

// ---------------------------------------------------------------------------
// R08-22 — Modo Essential: o aluno sem padrinho seta os proprios parametros
// ---------------------------------------------------------------------------

/** `essential` = aluno sem padrinho (seta); `mentored` = tem padrinho (so le); `read_only` = nao aluno. */
export type CycleSelfSettingMode = 'essential' | 'mentored' | 'read_only'

export interface CycleSelfSetting {
  mode: CycleSelfSettingMode
  can_edit: boolean
  /** Para o aluno a coerencia BLOQUEIA: combinacao em conflito volta 422 e nada e gravado. */
  coherence_blocks: boolean
  /** Limite de coerencia ainda placeholder (R08-27). */
  limits_pending: boolean
  /** Video de guias e metas (so https); null = ainda sem video. */
  guide_video_url: string | null
}

/** `GET|PUT /cycle_parameters` do proprio aluno (R08-22 acrescentou proposta, coerencia e modo). */
export interface MyCycleParametersResponse extends InstructorCycleParametersResponse {
  self_setting: CycleSelfSetting
}

// ---------------------------------------------------------------------------
// R08-07 — lista de TED de todos os afilhados (tela inicial do padrinho, §1.2)
// ---------------------------------------------------------------------------

/** `not_registered` (nada na semana passada nem na atual) · `fell` · `stable`. */
export type TedPortfolioSignal = 'not_registered' | 'fell' | 'stable'

/**
 * TED de uma semana. "Nao registrou" NAO e zero: sem nenhum registro, `minutes` e null e
 * `registered` false; registro de 0 h vem com `minutes: 0`.
 */
export interface TedPortfolioWeek {
  minutes: number | null
  registered: boolean
}

export interface TedPortfolioCurrentWeek extends TedPortfolioWeek {
  /** Meta x dias decorridos / 7 (pro-rata), em minutos. Null sem meta. */
  expected_minutes: number | null
  /** Realizado / esperado x 100. Null sem meta ou sem registro na semana (nunca 0 inventado). */
  pace_pct: number | null
}

/**
 * Meta da coluna "Meta": a meta TED semanal do proprio aluno (`student_goal`, possivelmente
 * herdada de semana anterior) e, sem ela, o alvo TED do padrinho no mes (`cycle_parameter`).
 */
export interface TedPortfolioGoal {
  minutes: number
  source: 'student_goal' | 'cycle_parameter'
  inherited: boolean
}

export interface TedPortfolioStudent {
  id: number
  name: string
  email: string
  goal: TedPortfolioGoal | null
  last_week: TedPortfolioWeek
  /** Media das 3 semanas anteriores a passada, so das semanas COM registro. */
  baseline: { average_minutes: number | null; weeks_registered: number }
  /** Semana passada - media das 3 semanas. Null sem uma das duas. */
  delta_minutes: number | null
  delta_pct: number | null
  current_week: TedPortfolioCurrentWeek
  /** 'YYYY-MM-DD' — data mais recente com registro, ate hoje. */
  last_record_on: string | null
  signal: TedPortfolioSignal
  /** 1 = pior. Ordem padrao da tela. */
  rank: number
}

export interface TedPortfolioWeekRange {
  /** 'YYYY-MM-DD' */
  start_date: string
  end_date: string
}

/** `GET /instructor/ted_portfolio` — `ted_portfolio`. */
export interface TedPortfolio {
  /** 'YYYY-MM-DD' */
  as_of: string
  weeks: {
    current: TedPortfolioWeekRange & { elapsed_days: number; days: number }
    last: TedPortfolioWeekRange
    baseline: TedPortfolioWeekRange[]
  }
  /** `pending: true` = o limite de "caiu" e placeholder (R08-27). */
  thresholds: { fall_ratio: number; pending: boolean }
  students: TedPortfolioStudent[]
}

// R08-08 — condutas (padrinho.md §4): decisao de metodo combinada com o padrinho, sem prazo,
// com vigencia. Espelho de `Conducts::Presenter` (medhunter-api). Sem edicao nem exclusao:
// muda-se uma conduta revogando (com motivo) e registrando outra.

export type ConductStatus = 'active' | 'revoked'

export interface ConductPerson {
  id: number
  name: string
}

export interface Conduct {
  id: number
  body: string
  status: ConductStatus
  /**
   * Mesmo vocabulario de `CycleParameterOrigin`. 'mentor' = registrada pelo padrinho;
   * 'system' = nascida da discordancia de abandono de topico (R08-17).
   */
  origin: CycleParameterOrigin
  /** R08-17: topico da discordancia de abandono; nulo em toda outra conduta. */
  topic_id: number | null
  /** R08-12: 1:1 em cujo modo reuniao a conduta foi combinada; nulo fora da reuniao. */
  meeting_id: number | null
  /** 'YYYY-MM-DD'; nunca no futuro. */
  effective_from: string
  /** Quem registrou; nulo se o usuario foi removido. */
  mentor: ConductPerson | null
  /** ISO 8601; nulo enquanto vigente. */
  revoked_at: string | null
  revoked_by: ConductPerson | null
  revocation_reason: string | null
  created_at: string
}

/** Vigentes primeiro (vigencia mais recente no topo), depois revogadas. */
export interface ConductsResponse {
  success: boolean
  conducts: Conduct[]
}

export interface ConductResponse {
  success: boolean
  conduct: Conduct
}

/** `POST /instructor/students/:id/conducts` — `effective_from` ausente = hoje. */
export interface ConductCreatePayload {
  body: string
  effective_from?: string
}

// R08-09 — compromissos (padrinho.md §4): o que o aluno se compromete a fazer ate a proxima
// reuniao, com prazo. Espelho de `Commitments::Presenter` (medhunter-api). Teto de abertos
// por aluno; fecha como cumprido / nao cumprido / repactuado (este com motivo). Sem edicao
// nem exclusao.

export type CommitmentStatus = 'open' | 'fulfilled' | 'not_fulfilled' | 'renegotiated'
export type CommitmentClosedStatus = Exclude<CommitmentStatus, 'open'>

/** Conferencia da plataforma (mesma medida dos parametros do ciclo). */
export type CommitmentVerificationKind = 'exams' | 'questions' | 'primary_study' | 'revision' | 'ted_hours'

export interface CommitmentVerification {
  kind: CommitmentVerificationKind
  target: number
  /** Aberto: medido agora; fechado: a foto do fechamento. Horas com uma casa. */
  measured: number
  met: boolean
  /** 'YYYY-MM-DD': do inicio do compromisso ate o prazo. */
  window: { start_date: string; end_date: string }
}

export interface Commitment {
  id: number
  body: string
  /** 'mentor' = registrado pelo padrinho; 'system' = da discordancia de abandono (R08-17). */
  origin: CycleParameterOrigin
  /** R08-17: topico da discordancia de abandono; nulo em todo outro compromisso. */
  topic_id: number | null
  /** R08-12: 1:1 em cujo modo reuniao o compromisso foi registrado; nulo fora da reuniao. */
  meeting_id: number | null
  status: CommitmentStatus
  /** 'YYYY-MM-DD' */
  starts_on: string
  due_on: string
  /** Aberto com prazo antes de hoje. */
  overdue: boolean
  mentor: ConductPerson | null
  /** Nulo = manual (so o check do aluno). */
  verification: CommitmentVerification | null
  /** true = cumpri, false = nao cumpri, null = nao marcou. */
  student_done: boolean | null
  student_checked_at: string | null
  student_comment: string | null
  student_commented_at: string | null
  /** R08-11: comentario do aluno, em compromisso aberto, que nenhum padrinho leu ainda. */
  comment_unread: boolean
  /** O aluno marcou e a plataforma diz o contrario. Dado, nao erro. */
  divergent: boolean
  closed_at: string | null
  closed_by: ConductPerson | null
  close_reason: string | null
  created_at: string
}

export interface CommitmentLimits {
  max_open: number
  /** Orientacao do "3 a 5 por ciclo"; nao e regra. */
  recommended_min: number
  open_count: number
}

/** Abertos primeiro (prazo mais proximo), depois fechados (mais recentes no topo). */
export interface CommitmentsResponse {
  success: boolean
  commitments: Commitment[]
}

export interface InstructorCommitmentsResponse extends CommitmentsResponse {
  limits: CommitmentLimits
}

export interface CommitmentResponse {
  success: boolean
  commitment: Commitment
}

/** `POST /instructor/students/:id/commitments` — `due_on` ausente = hoje + 7; sem tipo = manual. */
export interface CommitmentCreatePayload {
  body: string
  due_on?: string
  verification_kind?: CommitmentVerificationKind
  verification_target?: number
}

/** `PATCH .../commitments/:id/close` — `reason` obrigatorio em 'renegotiated'. */
export interface CommitmentClosePayload {
  status: CommitmentClosedStatus
  reason?: string
}

/** `PATCH /commitments/:id` (aluno) — chave ausente nao mexe; `student_done: null` desmarca. */
export interface CommitmentStudentPayload {
  student_done?: boolean | null
  student_comment?: string
}

// R08-10 — agenda das 1:1 e linha do dia (padrinho.md §1.1 e §11). Espelho de
// `Meetings::Presenter` e `Mentorships::DayLine` (medhunter-api). Instantes em ISO 8601.
export type MeetingStatus = 'scheduled' | 'canceled'
// held = presenca registrada pelo sistema ao abrir o modo reuniao (R08-12).
export type MeetingState = 'canceled' | 'held' | 'past' | 'in_progress' | 'upcoming'
// 'unavailable' so com o briefing desligado no servidor (`Meetings::Briefing::ENABLED`, ligado na R08-15).
// 'expired' = 1:1 encerrada sem o briefing aberto antes do fim (R08-15): a tela nao oferece o link.
export type MeetingBriefingStatus = 'unavailable' | 'none' | 'opened' | 'ready' | 'not_yet' | 'expired'
// TRANSC: status da transcricao da 1:1 (`MentorshipMeeting#transcription_status`).
export type TranscriptionStatus = 'none' | 'recording' | 'pending_upload' | 'transcribing' | 'done' | 'failed'

export interface MeetingPerson {
  id: number
  name: string
}

export interface MeetingBriefing {
  status: MeetingBriefingStatus
  ready_at: string
  opened_at: string | null
}

export interface MentorshipMeeting {
  id: number
  scheduled_at: string
  ends_at: string
  duration_minutes: number
  status: MeetingStatus
  state: MeetingState
  student: MeetingPerson
  mentor: MeetingPerson | null
  // So o padrinho que agendou remarca, cancela e registra presenca.
  can_manage: boolean
  rescheduled_count: number
  last_rescheduled_at: string | null
  canceled_at: string | null
  cancel_reason: string | null
  attended_at: string | null
  minutes_closed_at: string | null
  briefing: MeetingBriefing
  // TRANSC-08: gravacao do Transcritor ligada a 1:1 ('none' = nao gravada).
  transcription_status: TranscriptionStatus
  // MH9-10: link da reuniao (Zoom, Meet...) informado ao agendar; null sem link.
  meeting_url: string | null
}

export interface MeetingsResponse {
  success: boolean
  meetings: MentorshipMeeting[]
}

export interface AgendaResponse extends MeetingsResponse {
  range: { from: string; to: string }
}

export interface MeetingResponse {
  success: boolean
  meeting: MentorshipMeeting
}

export interface MeetingSchedulePayload {
  scheduled_at?: string
  duration_minutes?: number
  // MH9-10: vazio/null apaga o link.
  meeting_url?: string | null
}

// MH9-10 — POST /instructor/students/:id/meetings/:id/start ("Iniciar reuniao").
// `deep_link` abre o Transcritor com um codigo de uso unico (10 min); nenhum JWT vai na URL.
export interface MeetingStart {
  deep_link: string
  expires_at: string
  meeting_url: string | null
  transcription_enabled: boolean
  // Link de instalacao do Transcritor (admin > Transcritor); null = ainda nao configurado.
  download_url: string | null
}

export interface MeetingStartResponse {
  success: boolean
  meeting: MentorshipMeeting
  start: MeetingStart
}

// TRANSC-08 — transcricao da 1:1 (contrato §5.2). Canal L = Instrutor, R = Aluno.
export type TranscriptSpeaker = 'instructor' | 'student'

export interface TranscriptSegment {
  speaker: TranscriptSpeaker
  start_ms: number
  end_ms: number
  text: string
}

export interface MeetingTranscript {
  meeting_id: number
  recording_id: number
  language: string | null
  provider: string | null
  model: string | null
  audio_duration_ms: number | null
  created_at: string
  speakers: { instructor: MeetingPerson | null; student: MeetingPerson }
  segments: TranscriptSegment[]
}

/** `GET /instructor/students/:id/meetings/:meeting_id/transcript` (sempre 200 se a 1:1 e visivel). */
export interface MeetingTranscriptResponse {
  success: boolean
  transcription_status: TranscriptionStatus
  /** So em 'failed'. */
  failure_reason: string | null
  failure_label: string | null
  /** Nulo fora de 'done'. */
  transcript: MeetingTranscript | null
}

// R08-12 — modo reuniao (padrinho.md §3): ata da 1:1 com o Roteiro de blocos como checklist
// vivo ("NAO ABORDADO" e valido), notas, compromissos e condutas da reuniao. Fecha so com
// pelo menos um compromisso registrado na reuniao; fechada, e imutavel.

/** `null` = ainda nao marcado (vira 'not_addressed' ao fechar a ata). */
export type MeetingBlockStatus = 'addressed' | 'not_addressed'

export interface MeetingMinutesBlock {
  key: string
  title: string
  status: MeetingBlockStatus | null
  note: string | null
}

export type MeetingMinutesCloseBlocker = 'no_commitment'

export interface MeetingMinutes {
  id: number
  meeting_id: number
  status: 'open' | 'closed'
  /** Versao do Roteiro copiado na abertura; nulo enquanto o Roteiro nao esta definido (R08-27). */
  script_version: string | null
  /** Copia do Roteiro na abertura; vazia enquanto a lista dos 12 blocos nao esta definida. */
  blocks: MeetingMinutesBlock[]
  notes: string | null
  /** INST-09: recado para o aluno — compartilhado quando a ata fecha (a ata e privada). */
  student_message: string | null
  student_message_updated_at: string | null
  /** So o padrinho que agendou, 1:1 nao cancelada; continua valendo com a ata fechada. */
  can_edit_student_message: boolean
  mentor: MeetingPerson | null
  closed_at: string | null
  closed_by: MeetingPerson | null
  /** So o padrinho que agendou e so com a ata aberta. */
  can_edit: boolean
  close_blockers: MeetingMinutesCloseBlocker[]
  commitments: Commitment[]
  conducts: Conduct[]
  created_at: string
}

/** Estado do Roteiro no servidor (`Meetings::Script`). */
export interface MeetingScriptInfo {
  defined: boolean
  expected_count: number
  version: string | null
  /** Cartao que decide a lista dos blocos. */
  pending_decision: string
}

/** `GET|POST|PATCH .../meetings/:meeting_id/minutes` e `POST .../minutes/close`. */
export interface MeetingMinutesResponse {
  success: boolean
  meeting: MentorshipMeeting
  /** Nulo enquanto o modo reuniao nao foi aberto. */
  minutes: MeetingMinutes | null
  script: MeetingScriptInfo
  /** O viewer pode abrir o modo reuniao agora (quem agendou, nao cancelada, a partir de 30 min antes). */
  can_open: boolean
}

/** INST-09 — recado da reuniao como o ALUNO ve (`Meetings::StudentMessagePresenter`): sem a ata. */
export interface MeetingStudentMessage {
  id: number
  meeting_id: number
  /** Inicio da 1:1, ISO 8601. */
  meeting_at: string
  body: string
  updated_at: string | null
  mentor: MeetingPerson | null
}

/** `GET /api/v1/meeting_messages` — o da ultima reuniao e o historico (mais recente primeiro). */
export interface MeetingStudentMessagesResponse {
  success: boolean
  latest: MeetingStudentMessage | null
  messages: MeetingStudentMessage[]
}

/** `PATCH .../minutes` — so as chaves enviadas mudam. */
export interface MeetingMinutesUpdatePayload {
  notes?: string
  blocks?: { key: string; status?: MeetingBlockStatus | null; note?: string | null }[]
}

export interface DayLinePending {
  meetings_without_minutes: MentorshipMeeting[]
  month_parameters_missing: { month: string; students: MeetingPerson[] }
  briefings_not_opened: MentorshipMeeting[]
}

export interface DayLine {
  date: string
  briefing_enabled: boolean
  meetings_today: MentorshipMeeting[]
  pending: DayLinePending
  pending_count: number
}

// ---------------------------------------------------------------------------
// R08-11 — sinais da carteira na tela inicial do padrinho (§1.3)
// `Mentorships::Signals` (medhunter-api). Ja vem ordenado por urgencia.
// ---------------------------------------------------------------------------

export type PortfolioSignalType =
  | 'overdue_commitment'
  | 'band_drop'
  | 'no_record'
  | 'new_comment'
  | 'abandoned_topics'
  | 'exam_deadline'

/** Aba da ficha do afilhado que resolve o sinal (`/instructor/students/:id?tab=`). */
export type PortfolioSignalTab = 'commitments' | 'overview' | 'farewell'

interface PortfolioSignalBase {
  /** Unica e estavel dentro da lista. */
  key: string
  tab: PortfolioSignalTab
  student: MeetingPerson
}

export interface OverdueCommitmentSignal extends PortfolioSignalBase {
  type: 'overdue_commitment'
  commitment_id: number
  body: string
  /** 'YYYY-MM-DD' */
  due_on: string
  days_overdue: number
}

export interface BandDropSignal extends PortfolioSignalBase {
  type: 'band_drop'
  /** Melhor faixa observada na janela anterior. */
  from_band: ReadinessBand
  /** Faixa de hoje. */
  to_band: ReadinessBand
  /** 'YYYY-MM-DD' da observacao de referencia. */
  reference_on: string
}

export interface NoRecordSignal extends PortfolioSignalBase {
  type: 'no_record'
  /** Nulo = nunca registrou. */
  last_record_on: string | null
  days_without_record: number | null
}

export interface NewCommentSignal extends PortfolioSignalBase {
  type: 'new_comment'
  commitment_id: number
  body: string
  comment: string
  commented_at: string
}

export interface AbandonedTopicsSignal extends PortfolioSignalBase {
  type: 'abandoned_topics'
  count: number
  /** Ate 3 tópicos; a lista inteira esta na aba Despedida. */
  topics: { topic_id: number; name: string | null; days_since: number }[]
}

/**
 * R08-26: prazo do edital da instituicao-alvo vencendo em ate `deadline_window_days`, ainda nao
 * marcado como feito pelo aluno. Um sinal por prazo e aluno.
 */
export interface ExamDeadlineSignal extends PortfolioSignalBase {
  type: 'exam_deadline'
  deadline_id: number
  kind: ExamDeadlineKind
  title: string
  /** 'YYYY-MM-DD' */
  due_on: string
  /** 0 = vence hoje. */
  days_until: number
  url: string | null
  institution: { id: number; name: string }
}

export type PortfolioSignal =
  | OverdueCommitmentSignal
  | BandDropSignal
  | NoRecordSignal
  | NewCommentSignal
  | AbandonedTopicsSignal
  | ExamDeadlineSignal

export interface PortfolioSignals {
  as_of: string
  /** Limites provisorios (`pending: true` ate o cliente fechar os numeros). */
  thresholds: {
    no_record_days: number
    band_window_days: number
    recency_days: number
    /** R08-26: dias antes do vencimento em que o prazo do edital vira sinal. */
    deadline_window_days: number
    pending: boolean
  }
  counts: Record<PortfolioSignalType, number>
  signals: PortfolioSignal[]
}

// ---------------------------------------------------------------------------
// R08-16 — projecao de prontidao na data-alvo, com as duas saidas (§2, §5, §6)
// ---------------------------------------------------------------------------

/** Faixa de prontidao (§6: "alta/media/baixa, nunca R numerico"). */
export type ReadinessBand = 'high' | 'medium' | 'low'

export type ReadinessProjectionStatus =
  | 'projected'
  | 'no_exam_date'
  | 'exam_passed'
  | 'no_institution'
  | 'institution_not_matched'
  | 'no_prevalence'

export type ReadinessLeverReason =
  | 'already_top_band'
  | 'cost_not_calibrated'
  | 'no_revision_time'
  | 'unreachable'

export type ReadinessProjectionCaveat =
  | 'cost_not_calibrated'
  | 'stability_default'
  | 'no_recent_activity'
  | 'horizon_beyond_calibration'

/**
 * Uma das duas saidas. `available: false` = nao ha numero (custo nao calibrado, ja na faixa
 * alta, sem revisao); `reachable: false` = nem o teto da busca sobe a faixa. `message` ja
 * vem pronta em pt-BR ("+4 h/semana de TED", "−12 min por revisão (de 40 para 28 min)").
 */
export interface ReadinessProjectionLever {
  available: boolean
  reachable: boolean | null
  reason: ReadinessLeverReason | null
  target_band: ReadinessBand | null
  extra_hours_per_week?: number | null
  current_minutes?: number | null
  target_minutes?: number | null
  reduction_minutes?: number | null
  message: string
}

export interface ReadinessProjectionPace {
  window_days: number
  ted_hours_per_week: number
  new_themes_per_week: number
  revisions_per_week: number
  time_split: { primary: number; revision: number; source: 'student_log' | 'default' }
}

export interface ReadinessProjectionCost {
  minutes_per_theme: number
  calibrated: boolean
}

/** `GET /instructor/students/:id/readiness_projection` — sem numero de prontidao, so faixas. */
export interface ReadinessProjection {
  status: ReadinessProjectionStatus
  message: string
  main_exam: MainExam
  horizon_days: number | null
  current_band: ReadinessBand | null
  projected_band: ReadinessBand | null
  target_band: ReadinessBand | null
  pace: ReadinessProjectionPace | null
  time_cost: { primary: ReadinessProjectionCost; revision: ReadinessProjectionCost } | null
  outputs: { ted: ReadinessProjectionLever; revision_cost: ReadinessProjectionLever } | null
  caveats: ReadinessProjectionCaveat[]
  /** Tetos da busca das saidas ainda sao placeholder (R08-27). */
  limits_pending: boolean
}

export interface InstructorReadinessProjectionResponse {
  success: boolean
  projection: ReadinessProjection
}

// R08-17 — lista de despedida (padrinho.md §2, §6): topicos que a instituicao-alvo cobra, que
// o aluno ja estudou e que estao sem nenhum registro ha mais de `threshold_days` dias, do mais
// cobrado para o menos. Espelho de `Studies::FarewellList` (medhunter-api).

export type FarewellListStatus = 'listed' | 'no_institution' | 'institution_not_matched' | 'no_prevalence'

/** Conduta vigente nascida da discordancia; o compromisso e o mais recente do topico. */
export interface FarewellDisagreementRef {
  conduct_id: number
  commitment_id: number | null
  /** 'YYYY-MM-DD' — vigencia da conduta. */
  since: string
}

export interface FarewellTopic {
  topic_id: number
  name: string | null
  specialty: string | null
  /** Questoes da instituicao-alvo que cobram o topico. */
  questions: number
  /** % das questoes da instituicao-alvo, uma casa. */
  share: number
  high_prevalence: boolean
  /** 'YYYY-MM-DD' do ultimo registro (primario ou revisao). */
  last_studied_on: string
  days_since: number
  disagreement: FarewellDisagreementRef | null
  /** Textos pre-preenchidos para "discordar do abandono". */
  suggestion: { conduct_body: string; commitment_body: string }
}

export interface FarewellList {
  status: FarewellListStatus
  /** Motivo em pt-BR quando `status` != 'listed'. */
  message: string | null
  institution: string | null
  threshold_days: number
  /** Criterio provisorio: fatia acumulada das questoes; `pending` ate o cliente fechar. */
  high_prevalence: { cumulative_share: number; pending: boolean }
  high_prevalence_count: number
  topics: FarewellTopic[]
}

export interface InstructorFarewellResponse {
  success: boolean
  farewell: FarewellList
}

/** `POST .../farewell_topics/:topic_id/disagreement` — em branco = sugestao / prazo padrao. */
export interface FarewellDisagreementPayload {
  conduct_body?: string
  commitment_body?: string
  due_on?: string
}

export interface FarewellDisagreementResponse {
  success: boolean
  conduct: Conduct
  commitment: Commitment
}

// R08-20 — chegada tardia (padrinho.md §5): temas de alta prevalencia da instituicao-alvo que o
// cursinho do aluno so libera depois de agosto. Espelho de `Studies::LateArrival` (medhunter-api).

export type LateArrivalStatus =
  | 'listed'
  | 'no_prep_course'
  | 'no_institution'
  | 'institution_not_matched'
  | 'no_prevalence'
  | 'no_release_dates'

export type LateArrivalChoice = 'other_source' | 'accept_replan'

/** Conduta vigente escolhida para o topico no ano; revogada, o topico volta ao aviso. */
export interface LateArrivalDecisionRef {
  id: number
  choice: LateArrivalChoice
  label: string
  conduct_id: number
  /** 'YYYY-MM-DD' — vigencia da conduta. */
  since: string
}

export interface LateArrivalLesson {
  id: number
  title: string
  /** 'YYYY-MM-DD' ou null (disponivel desde o inicio do ano). */
  release_on: string | null
}

export interface LateArrivalTopic {
  topic_id: number
  name: string | null
  specialty: string | null
  /** Questoes da instituicao-alvo que cobram o topico. */
  questions: number
  /** % das questoes da instituicao-alvo, uma casa. */
  share: number
  /** 'YYYY-MM-DD' da aula mais cedo do cursinho que cobre o topico. */
  release_on: string
  lessons: LateArrivalLesson[]
  decision: LateArrivalDecisionRef | null
  /** Texto pre-preenchido de cada conduta. */
  suggestion: Record<LateArrivalChoice, string>
}

export interface LateArrival {
  status: LateArrivalStatus
  /** Motivo em pt-BR quando `status` != 'listed'. */
  message: string | null
  year: number
  /** 'YYYY-MM-DD' — 31/08: liberada depois disso e chegada tardia. */
  cutoff_on: string
  prep_course: { id: number; name: string } | null
  institution: string | null
  /** Criterio provisorio de alta prevalencia (o mesmo da lista de despedida). */
  high_prevalence: { cumulative_share: number; pending: boolean }
  choices: { value: LateArrivalChoice; label: string }[]
  topics: LateArrivalTopic[]
  /** Topicos sem conduta vigente no ano. */
  pending_count: number
}

export interface InstructorLateArrivalResponse {
  success: boolean
  late_arrival: LateArrival
}

/** `POST .../late_arrivals/:topic_id/decision` — `body` em branco = sugestao da escolha. */
export interface LateArrivalChoicePayload {
  choice: LateArrivalChoice
  body?: string
}

export interface LateArrivalChoiceResponse {
  success: boolean
  conduct: Conduct
  decision: { id: number; topic_id: number; year: number; choice: LateArrivalChoice; conduct_id: number }
}

// R08-18 — memoria vs. compreensao por topico (padrinho.md §2, §6). Espelho de
// `Studies::MemoryComprehension` (medhunter-api): memoria = ja foi boa e caiu (revisar);
// compreensao = ultimas notas individuais baixas e estaveis, com ou sem piso (voltar ao
// primario); 'recent_low' = retido pelo IR, mas a saida para EP da fila ja disparou.

export type MemoryComprehensionAxis = 'comprehension' | 'memory' | 'inconclusive' | 'observing'

export type MemoryComprehensionConduct = 'back_to_primary' | 'review'

/** Por que o topico ficou em 'inconclusive' ou 'observing'. */
export type MemoryComprehensionReason = 'block_only' | 'few_notes' | 'rising' | 'small_sample' | 'recent_low'

/** Status da regra do IR (R08-04, `Studies::TopicRetention`). */
export type TopicRetentionStatus = 'no_evidence' | 'block_only' | 'below_floor' | 'retained' | 'dropped'

export interface MemoryComprehensionNote {
  /** 'YYYY-MM-DD' */
  date: string
  questions: number
  hits: number
  /** % com uma casa. */
  performance: number
  source: 'primary_study' | 'revision'
}

export interface MemoryComprehensionTopic {
  topic_id: number
  name: string
  specialty: string | null
  sub_specialty: string | null
  axis: MemoryComprehensionAxis
  conduct: MemoryComprehensionConduct | null
  reason: MemoryComprehensionReason | null
  retention_status: TopicRetentionStatus
  /** IR 0..100 (so memoria/retido); nulo quando o topico nao e medivel. */
  index: number | null
  individual_notes: number
  block_notes: number
  /** Ate as `criteria.min_notes` ultimas notas individuais, da mais antiga para a mais recente. */
  recent_notes: MemoryComprehensionNote[]
  last_individual_on: string | null
  /** A regra de saida para EP da Jornada ja tirou o topico da fila do aluno. */
  primary_study_exit: boolean
}

export interface MemoryComprehension {
  counts: Record<MemoryComprehensionAxis | 'retained', number>
  /** Sem os retidos; ordem: compreensao, memoria, nao conclusivo, em observacao. */
  topics: MemoryComprehensionTopic[]
  /** Janela e subida minima ainda nao validadas com o cliente. */
  criteria_pending: boolean
  /** `rising_min_gain` e `low_below` em pontos percentuais. */
  criteria: { min_notes: number; rising_min_gain: number; low_below: number }
}

export interface InstructorMemoryComprehensionResponse {
  success: boolean
  memory_comprehension: MemoryComprehension
}

// R08-13 — cabecalho de confiabilidade de toda tela de metrica do padrinho (padrinho.md §6).
// `Studies::ReliabilityHeader` (medhunter-api). Sem dado, os numeros saem null — nunca zero.
export type ReliabilityHeaderDays = 7 | 14 | 30 | 60 | 90

export interface ReliabilityHeader {
  period: { start_date: string; end_date: string; days: number }
  /** `platform_exams`: tentativas da plataforma ainda nao conferidas (as conferidas contam em `exams`). */
  records: { total: number; primary_studies: number; revisions: number; exams: number; platform_exams: number }
  /**
   * Origem da nota dos registros de estudo/revisao COM questao: 1 topico = individual,
   * N > 1 = bloco, 0 = sem vinculo. `*_share` em % (0..100), null sem nota.
   */
  notes: {
    total: number
    individual: number
    block: number
    unlinked: number
    individual_share: number | null
    block_share: number | null
    unlinked_share: number | null
  }
  /** Σ min ÷ Σ questoes, correcao incluida; `method_signal` quando `value` < `threshold`. */
  minutes_per_question: {
    value: number | null
    minutes: number
    questions: number
    records_used: number
    records_without_time: number
    exams_without_correction: number
    /** Prova da plataforma nao conferida: sem tempo de correcao, fica fora da media. */
    platform_exams_without_correction: number
    method_signal: boolean
    threshold: number
  }
  /** IR global (0..100) — so no cabecalho. */
  retention: {
    index: number | null
    topics_measured: number
    topics_dropped: number
    topics_below_floor: number
    topics_block_only: number
  }
  allowed_days: ReliabilityHeaderDays[]
}

export interface InstructorReliabilityHeaderResponse {
  success: boolean
  reliability_header: ReliabilityHeader
}

// ---------------------------------------------------------------------------
// R08-15 — briefing pre-reuniao (padrinho.md §2): pauta gerada (1-3 linhas por urgencia) e as
// secoes do §2, montados a partir de 24 h antes da 1:1. Espelho de `Meetings::BriefingContent`
// e `Meetings::BriefingAgenda` (medhunter-api). Cada secao reusa o tipo do dono da metrica.
// ---------------------------------------------------------------------------

export type BriefingAgendaType =
  | 'commitments'
  | 'readiness'
  | 'no_record'
  | 'abandoned_topics'
  | 'back_to_primary'
  | 'ted'
  | 'method'
  | 'none'

export interface BriefingAgendaLine {
  type: BriefingAgendaType
  text: string
}

export interface BriefingAgenda {
  /** 1 a 3 linhas, a mais urgente primeiro; sem sinal, uma linha 'none'. */
  lines: BriefingAgendaLine[]
  /** Sinais que ficaram fora das 3 linhas. */
  more_count: number
  /** A ordem de urgencia ainda e provisoria (R08-27). */
  order_pending: boolean
}

export interface BriefingSinceLastSession {
  last_meeting_id: number | null
  /** 'YYYY-MM-DD' da ultima 1:1 com ata fechada. */
  last_meeting_on: string | null
  /** 'YYYY-MM-DD' — inicio do periodo (ata fechada; sem ata, os ultimos 14 dias). */
  since: string
  /** O periodo foi cortado em 90 dias. */
  capped: boolean
  last_record_on: string | null
  /** Null = nunca registrou. */
  days_without_record: number | null
  header: Omit<ReliabilityHeader, 'allowed_days'>
}

export interface BriefingTed {
  row: TedPortfolioStudent | null
  weeks: TedPortfolio['weeks']
  thresholds: TedPortfolio['thresholds']
  cycle: CycleParameter | null
  /** A proporcao 4:2:1 do §2 nao esta definida; nao e calculada. */
  ratio_pending: boolean
}

export interface BriefingFarewellTopic {
  topic_id: number
  name: string | null
  specialty: string | null
  share: number
  days_since: number
  high_prevalence: boolean
  disagreement: FarewellDisagreementRef | null
}

export interface BriefingFarewell extends Omit<FarewellList, 'topics'> {
  total: number
  /** Alta prevalencia sem discordancia vigente — o que a pauta cobra. */
  abandoned_high_prevalence: number
  /** Os 5 primeiros; a lista inteira esta na aba Despedida. */
  topics: BriefingFarewellTopic[]
}

export interface BriefingMemoryComprehension {
  counts: MemoryComprehension['counts']
  criteria_pending: boolean
  /** Compreensao e memoria, ate 5. */
  topics: Pick<MemoryComprehensionTopic, 'topic_id' | 'name' | 'specialty' | 'axis' | 'conduct'>[]
}

export interface BriefingTargetCoverage {
  rank: number
  institution_id: number | null
  typed_name: string
  matched_name: string | null
  absolute: { studied: number; total: number; pct: number }
  weighted: { pct: number }
}

export interface BriefingCoverage {
  primary_study: CycleParameter | null
  revision: CycleParameter | null
  /** Null quando ha instituicao-alvo com prevalencia; senao o motivo. */
  target_status: FarewellListStatus | null
  target_institution: BriefingTargetCoverage | null
}

export interface BriefingTrack {
  code: string
  name: string
  status: TrackStatus
  ep_accuracy: number | null
  ep_questions: number
  revision_accuracy: number | null
  revision_questions: number
  exams_accuracy: number | null
  exams_questions: number
}

export interface BriefingLastMeetingCommitments {
  meeting_id: number | null
  counts: Record<CommitmentStatus, number>
  /** Todos os compromissos do aluno vencidos e em aberto (nao so os da ultima reuniao). */
  overdue_open: number
  items: Commitment[]
}

export interface BriefingSections {
  since_last_session: BriefingSinceLastSession
  ted: BriefingTed
  readiness: ReadinessProjection
  farewell: BriefingFarewell
  memory_comprehension: BriefingMemoryComprehension
  coverage: BriefingCoverage
  performance_by_area: { period: string; tracks: BriefingTrack[] }
  last_meeting_commitments: BriefingLastMeetingCommitments
  conducts: Conduct[]
}

export interface MeetingBriefingContent {
  version: number
  meeting_id: number
  scheduled_at: string
  student: MeetingPerson
  /** 'YYYY-MM-DD' da foto. */
  as_of: string
  agenda: BriefingAgenda
  sections: BriefingSections
  /** Encaixe nos 12 blocos do Roteiro pendente (R08-27). */
  script: MeetingScriptInfo
  pending: { ratio_421: boolean; block_mapping: boolean; agenda_order: boolean }
}

/** `GET .../meetings/:meeting_id/briefing` e `POST .../briefing/refresh`. */
export interface MeetingBriefingResponse {
  success: boolean
  meeting: MentorshipMeeting
  /** Null antes de 24 h da 1:1 ou com a 1:1 cancelada (ver `meeting.briefing`). */
  briefing: MeetingBriefingContent | null
  generated_at: string | null
  /** Quem agendou, com foto, ate o fim da 1:1. */
  can_refresh: boolean
}

// ---------------------------------------------------------------------------
// Guia (IA) e biblioteca enviavel (R08-23) — espelho de `MeetingGuidesController`,
// `MentorGuide::Ask.present`, `MaterialLibrary::Catalog` e `MaterialLibrary::Presenter`
// (medhunter-api)
// ---------------------------------------------------------------------------

/** Chaves de `MeetingBriefingContent['sections']`: o "bloco" do Guia e a secao do briefing. */
export type BriefingSectionKey = keyof BriefingSections

/** Resposta do Guia a uma secao (conduta + caminho diagnostico). */
export interface MentorGuideAnswer {
  section: BriefingSectionKey
  conduct: string
  diagnostic_path: string[]
  caveats: string[]
  provider: string
  model: string
  created_at: string
}

/** `GET .../meetings/:meeting_id/guide` (so o padrinho que agendou; 404 para os outros). */
export interface MentorGuideStatusResponse {
  success: boolean
  /** False sem provedor de IA configurado no ambiente (`reason` diz isso). */
  available: boolean
  reason: string | null
  sections: BriefingSectionKey[]
  /** Respostas ja dadas para a foto atual do briefing, por secao. */
  answers: Partial<Record<BriefingSectionKey, MentorGuideAnswer>>
  /** Escalonamento para a Lais: canal a definir (R08-27); sempre `available: false`. */
  escalation: { available: boolean; message: string }
}

/** `POST .../meetings/:meeting_id/guide`. Erros: 503 sem provedor, 429 teto diario (tentativas), 409 mesma secao ja em andamento, 502 falha. */
export interface MentorGuideAskResponse {
  success: boolean
  answer: MentorGuideAnswer
}

export type MaterialShareKind = 'exam' | 'statistical_guide' | 'link'

export interface MaterialShare {
  id: number
  kind: MaterialShareKind
  /** Foto do nome no envio. */
  title: string
  note: string | null
  exam_id: number | null
  institution_name: string | null
  /** So no tipo `link`. */
  url: string | null
  mentor: MeetingPerson | null
  viewed_at: string | null
  created_at: string
}

export interface MaterialSharesResponse {
  success: boolean
  material_shares: MaterialShare[]
}

export interface MaterialShareResponse {
  success: boolean
  material_share: MaterialShare
}

export interface MaterialShareCreatePayload {
  kind: MaterialShareKind
  exam_id?: number
  institution_name?: string
  url?: string
  title?: string
  note?: string
}

export interface MaterialLibraryExam {
  id: number
  name: string
  year: number | null
  institution: string | null
}

/** `GET .../students/:id/material_library?q=`. */
export interface MaterialLibraryResponse {
  success: boolean
  /** Null sem 1a escolha; `matched: false` quando o nome nao casa com o acervo. */
  target_institution: { name: string; matched: boolean } | null
  target_exams: MaterialLibraryExam[]
  /** Busca no acervo (vazia sem `q` de 2+ letras). */
  exams: MaterialLibraryExam[]
  guides: { institution_name: string; rank: number; main_exam: boolean }[]
}

// ---------------------------------------------------------------------------
// Opt-in do WhatsApp (R08-14) — espelho de `WhatsappOptInsController#payload` (medhunter-api)
// ---------------------------------------------------------------------------

/** `null` = nunca pediu. `pending_verification` = aceitou, mas ainda nao confirmou o codigo. */
export type WhatsappOptInStatus = 'opted_in' | 'pending_verification' | 'opted_out'

/**
 * Aceite do proprio usuario para avisos no WhatsApp (`GET/PUT /whatsapp_opt_in`,
 * `POST /whatsapp_opt_in/confirm`).
 *
 * `phone` e o numero informado, em E.164 (+55...); `suggested_phone` vem do telefone do perfil,
 * so como sugestao. `opted_in` so e true depois de o dono do numero confirmar o codigo
 * (`verified_at`). `verification_sent_at` (so com status pendente): quando o codigo saiu; null =
 * nao saiu (canal desligado ou falha). `channel_enabled` false = nenhum provedor configurado.
 *
 * MH9-07: o padrinho recebe o termo dos avisos pelo Hermes (WhatsApp da MedHunter);
 * `consent_outdated` = ativo, mas aceitou um termo anterior (precisa aceitar o novo).
 * `channel`: por onde os avisos saem ('hermes' para o padrinho, 'twilio'), null = nenhum.
 */
export interface WhatsappOptIn {
  opted_in: boolean
  status: WhatsappOptInStatus | null
  phone: string | null
  opted_in_at: string | null
  opted_out_at: string | null
  verified_at: string | null
  verification_sent_at: string | null
  suggested_phone: string | null
  consent_text: string
  consent_version: string
  consent_outdated: boolean
  channel_enabled: boolean
  channel: 'hermes' | 'twilio' | null
}

/** Corpo do `PUT /whatsapp_opt_in`. `phone` so e exigido com `opted_in: true`. */
export interface WhatsappOptInUpdatePayload {
  opted_in: boolean
  phone?: string
}

// R08-19 — guia estatistico por instituicao, matriz prevalencia x desempenho e carteira por
// instituicao-alvo (padrinho.md §6).

/** Uma instituicao prioritaria do aluno (1a a 5a escolha); a 1a e a instituicao-alvo. */
export interface InstitutionOption {
  rank: number
  name: string
  main_exam: boolean
}

/** Qual instituicao o guia do padrinho leu (`institution: null` = guia combinado). */
export interface StatisticalGuideFocus {
  institution: string | null
  rank: number | null
  main_exam: boolean
  options: InstitutionOption[]
}

export interface InstructorStatisticalGuideResponse {
  success: boolean
  data: StatisticalGuide
  focus: StatisticalGuideFocus
}

export type PrevalenceQuadrantKey = 'priority' | 'maintain' | 'secondary' | 'sufficient'
export type PrevalenceBand = 'high' | 'low'
export type PrevalenceUnmeasuredReason = 'not_covered' | 'no_individual_note' | 'small_sample'

export interface PrevalenceMatrixTopic {
  topic_id: number
  name: string | null
  specialty: string | null
  questions: number
  /** % das questoes da instituicao neste tema. */
  share: number
  high_prevalence: boolean
  covered: boolean
  /** Acerto das notas individuais (%); null quando nao medido. */
  performance: number | null
  individual_questions: number
  measure_reason: PrevalenceUnmeasuredReason | null
  quadrant: PrevalenceQuadrantKey | null
  /** Eixo da R08-18; 'retained' = retido. null sem nota. */
  memory_axis: MemoryComprehensionAxis | 'retained' | null
  last_studied_on: string | null
  days_since: number | null
  stale: boolean
}

export interface PrevalenceQuadrant {
  key: PrevalenceQuadrantKey
  band: PrevalenceBand
  topics_count: number
  questions: number
  share: number
  /** Intensidade: % das questoes da faixa de prevalencia que caem neste quadrante. */
  band_coverage_pct: number
  specialties: { name: string | null; topics_count: number; questions: number }[]
  topic_ids: number[]
  suggestion: { conduct_body: string } | null
}

export interface PrevalenceUnmeasured {
  band: PrevalenceBand
  reason: PrevalenceUnmeasuredReason
  topics_count: number
  questions: number
  band_coverage_pct: number
}

export interface PrevalenceSpecialty {
  name: string | null
  questions: number
  share: number
  topics_total: number
  topics_covered: number
  coverage_pct: number
  /** IR da especialidade (R08-04); null sem tema medivel. */
  ir: number | null
}

export interface PrevalenceMatrixSummary {
  coverage_pct: number
  high_prevalence_coverage_pct: number
  performance_pct: number | null
  priority_topics: number
  stale_high_prevalence: number
  topics_total: number
  topics_measured: number
}

export interface PrevalenceMatrix {
  status: 'available' | 'no_institution' | 'institution_not_matched' | 'no_prevalence'
  message: string | null
  institution: string | null
  total_questions: number
  criteria: {
    pending: boolean
    high_prevalence_share: number
    high_performance_min: number
    min_questions: number
    recency_days: number
  }
  summary: PrevalenceMatrixSummary | null
  quadrants: PrevalenceQuadrant[]
  unmeasured: PrevalenceUnmeasured[]
  specialties: PrevalenceSpecialty[]
  topics: PrevalenceMatrixTopic[]
}

export interface InstructorPrevalenceMatrixResponse {
  success: boolean
  prevalence_matrix: PrevalenceMatrix
}

export interface InstitutionPortfolioMentee {
  id: number
  name: string
  exam_date: string | null
  days_until: number | null
  coverage_pct: number
  high_prevalence_coverage_pct: number
  performance_pct: number | null
  priority_topics: number
  stale_high_prevalence: number
}

export interface InstitutionPortfolioColumn {
  name: string
  total_questions: number
  mentees: InstitutionPortfolioMentee[]
}

export interface InstitutionPortfolio {
  institutions: InstitutionPortfolioColumn[]
  unavailable: { id: number; name: string; status: string; message: string | null; institution: string | null }[]
  criteria_pending: boolean
}

export interface InstitutionPortfolioResponse {
  success: boolean
  institution_portfolio: InstitutionPortfolio
}

// ---------------------------------------------------------------------------
// R08-21 — resultados de prova e linha do tempo da relacao (padrinho.md §8)
// ---------------------------------------------------------------------------

/** 'requested' = pedido automatico sem campos; 'reported' = preenchido sem confirmacao. */
export type ExamResultStatus = 'requested' | 'reported' | 'confirmed'
/** 'system' = pedido automatico quando a data da prova principal passa. */
export type ExamResultOrigin = 'system' | 'student' | 'mentor'
export type ExamResultSecondPhase = 'not_applicable' | 'passed' | 'failed'
export type ExamResultConfirmationMethod = 'student' | 'upload'

export interface ExamResultEvidence {
  filename: string
  content_type: string
  byte_size: number
  /** ISO 8601. */
  uploaded_at: string
}

export interface ExamResultConsent {
  /** ISO 8601. */
  granted_at: string
  terms_version: string
}

/** Espelho de `ExamResults::Presenter.one` (medhunter-api). */
export interface ExamResult {
  id: number
  institution: string
  /** 'YYYY-MM-DD'. */
  exam_on: string
  origin: ExamResultOrigin
  status: ExamResultStatus
  score: number | null
  classification: number | null
  /** Nota menos o corte; negativa = abaixo do corte. */
  cutoff_distance: number | null
  passed: boolean | null
  called: boolean | null
  second_phase: ExamResultSecondPhase | null
  enrolled: boolean | null
  requested_at: string | null
  reported_at: string | null
  confirmed_at: string | null
  confirmation_method: ExamResultConfirmationMethod | null
  recorded_by: ConductPerson | null
  /** Sem URL: o arquivo sai so pela rota autenticada `.../evidence`. */
  evidence: ExamResultEvidence | null
  /** Consentimento comercial vigente; nulo sem consentimento ou depois de revogado. */
  consent: ExamResultConsent | null
  /** So resultado confirmado conta para bonus. */
  counts_for_bonus: boolean
  /** Confirmado E com consentimento vigente. */
  counts_for_social_proof: boolean
  created_at: string
}

/** Texto do consentimento; 'pending' = provisorio, ainda em validacao (R08-27). */
export interface ExamConsentTerms {
  version: string
  text: string
  status: 'pending' | 'final'
}

export interface ExamResultsResponse {
  success: boolean
  exam_results: ExamResult[]
  consent_terms: ExamConsentTerms
}

export interface ExamResultResponse {
  success: boolean
  exam_result: ExamResult
}

/** Campos de resultado (nulo = nao informado). */
export interface ExamResultFields {
  score: number | null
  classification: number | null
  cutoff_distance: number | null
  passed: boolean | null
  called: boolean | null
  second_phase: ExamResultSecondPhase | null
  enrolled: boolean | null
}

/** `POST .../exam_results` — prova e data so na criacao. */
export interface ExamResultCreatePayload extends ExamResultFields {
  institution: string
  exam_on: string
}

export type TimelineEventKind =
  | 'conduct_created'
  | 'conduct_revoked'
  | 'commitment_created'
  | 'commitment_closed'
  | 'minutes_closed'
  | 'exam_result_requested'
  | 'exam_result_reported'
  | 'exam_result_confirmed'
  /** Resultado ja preenchido corrigido, sem confirmacao a desfazer. */
  | 'exam_result_corrected'
  /** Resultado confirmado corrigido: a confirmacao (evento proprio, na data original) foi desfeita. */
  | 'exam_result_unconfirmed'
  | 'consent_granted'
  | 'consent_revoked'
  /** MH9-15: material da biblioteca enviavel (prova, guia ou link externo). */
  | 'material_shared'

/** Espelho de `Mentorships::Timeline` (medhunter-api); titulo e detalhe ja em pt-BR. */
export interface TimelineEvent {
  kind: TimelineEventKind
  /** ISO 8601. */
  at: string
  title: string
  detail: string
  ref: { type: 'conduct' | 'commitment' | 'meeting' | 'exam_result' | 'material_share'; id: number }
  /** Aba da ficha do afilhado onde o registro mora. */
  tab: string
  /** MH9-15: so em `material_shared` de link externo (https); nos demais, ausente ou null. */
  url?: string | null
}

export interface InstructorTimelineResponse {
  success: boolean
  timeline: { events: TimelineEvent[] }
}

// =============================================================================
// R08-24 — Espelho de qualidade do padrinho (padrinho.md §10) e NPS dos afilhados
// =============================================================================

export type QualityMirrorDays = 30 | 90 | 180

export interface QualityMirrorNpsQuarter {
  /** Ex.: "2026-Q2". */
  period_key: string
  /** Alunos distintos que responderam no trimestre. */
  respondents: number
  /** false abaixo do minimo de alunos: so a contagem aparece. */
  visible: boolean
  score: number | null
}

/** Espelho de `Mentorships::QualityMirror` (medhunter-api). Nada aqui identifica aluno. */
export interface QualityMirror {
  period: { start_date: string; end_date: string; days: number }
  regularity: {
    /** 1:1 nao canceladas, ja terminadas, com inicio no periodo. */
    planned: number
    /** Com presenca registrada pelo sistema. */
    held: number
    missed: number
    canceled: number
    rescheduled: number
    /** held / planned em %, uma casa; null sem 1:1. */
    rate: number | null
  }
  response_time: {
    answered: number
    pending: number
    median_hours: number | null
    average_hours: number | null
  }
  briefing: {
    meetings: number
    /** Briefing aberto ate o horario de inicio. */
    opened_before: number
    rate: number | null
  }
  /**
   * NPS por trimestre civil FECHADO, independente de `period`: nao muda de um dia para o outro
   * nem entre janelas, entao nao da para isolar a resposta de um aluno. Sem distribuicao.
   */
  nps: {
    /** Minimo de alunos distintos no trimestre para mostrar a nota (provisorio, R08-27). */
    min_students: number
    criteria_pending: boolean
    /** Periodicidade da pesquisa provisoria. */
    period_pending: boolean
    /** Trimestre em coleta, ex.: "2026-Q3"; nada dele aparece. */
    current_period_key: string
    /** Ultimos trimestres fechados, do mais recente ao mais antigo. */
    quarters: QualityMirrorNpsQuarter[]
  }
  allowed_days: QualityMirrorDays[]
}

export interface InstructorQualityMirrorResponse {
  success: boolean
  quality_mirror: QualityMirror
}

/** Pesquisa de NPS pendente do aluno (`Mentorships::NpsSurvey.pending_for`). */
export interface MentorNpsSurvey {
  mentor: { id: number; name: string }
  /** Ex.: "2026-Q3". */
  period_key: string
}

export interface MentorNpsResponse {
  success: boolean
  mentor_nps: { pending: MentorNpsSurvey[]; period_pending: boolean }
}

export interface MentorNpsSubmitPayload {
  mentor_id: number
  score: number
}

/** MH13-33: padrinho do proprio aluno (`Mentorships::MyMentor`), bloco "Seu mentor" do dashboard. */
export interface MyMentor {
  id: number
  name: string
  first_name: string | null
  /** Relativa a API (rota de proxy do Active Storage); null sem foto. */
  avatar_url: string | null
  avatar_initial: string
  /** wa.me com o telefone do cadastro do padrinho; null sem telefone valido. */
  whatsapp_url: string | null
  /** Inicio do vinculo (ISO 8601). */
  since: string | null
}

export interface MyMentorResponse {
  success: boolean
  /** Vazia = aluno sem padrinho. */
  mentors: MyMentor[]
}

// =============================================================================
// R08-25 — Área administrativa do padrinho (padrinho.md §11): carteira, remuneração e bônus
// (critério pendente do R08-27), contrato/documentos e cobertura de férias/plantão
// =============================================================================

export interface MentorAdminPerson {
  id: number
  name: string
}

/** Afilhado da carteira ativa (`Mentorships::Administrative#portfolio`). */
export interface MentorPortfolioMentee {
  id: number
  name: string
  /** Status do vínculo (`friendships.status`); 'pending' vale como vínculo ativo (R08-01). */
  link_status: string
  /** Data do vínculo (AAAA-MM-DD). */
  since: string
  last_activity_on: string | null
  next_meeting_at: string | null
}

/** Regra de remuneração: PENDENTE (R08-27). `amount` fica null até a decisão. */
export interface MentorCompensation {
  pending: boolean
  explanation: string
  amount: number | null
  currency: string
  active_mentees: number
}

export type MentorBonusComponentKey = 'regularity' | 'response_time' | 'briefing' | 'nps'

export interface MentorBonusComponent {
  key: MentorBonusComponentKey
  label: string
  /** 'percent' | 'hours' | 'score' */
  unit: 'percent' | 'hours' | 'score'
  /** O que o componente mede, em pt-BR. */
  measures: string
  /** Valor atual do padrinho, lido do espelho (R08-24); null sem dado. */
  current_value: number | null
  /** Peso e meta: null enquanto o critério estiver pendente. */
  weight: number | null
  target: number | null
}

/** Como o bônus é calculado (`Mentorships::BonusCriteria`). */
export interface MentorBonusCriteria {
  pending: boolean
  explanation: string
  window_days: number
  amount: number | null
  components: MentorBonusComponent[]
}

export type MentorDocumentKind = 'contract' | 'document'

/** Contrato/documento do padrinho. Sem URL: o arquivo só sai pela rota autenticada. */
export interface MentorDocument {
  id: number
  mentor_id: number
  kind: MentorDocumentKind
  title: string
  filename: string | null
  content_type: string | null
  byte_size: number | null
  uploaded_by_admin: boolean
  created_at: string
}

export type MentorCoverageReason = 'vacation' | 'on_call'
export type MentorCoverageState = 'scheduled' | 'active' | 'ended' | 'canceled'

export interface MentorCoverage {
  id: number
  mentor: MentorAdminPerson
  substitute: MentorAdminPerson
  starts_on: string
  ends_on: string
  reason: MentorCoverageReason
  state: MentorCoverageState
  canceled_at: string | null
  created_at: string
  /** Alunos cobertos hoje: só para o substituto e só com a cobertura vigente. */
  students: MentorAdminPerson[]
}

export interface MentorAdministrative {
  portfolio: { total: number; mentees: MentorPortfolioMentee[] }
  compensation: MentorCompensation
  bonus: MentorBonusCriteria
  documents: MentorDocument[]
  coverage: { as_mentor: MentorCoverage[]; as_substitute: MentorCoverage[] }
}

export interface InstructorAdministrativeResponse {
  success: boolean
  administrative: MentorAdministrative
}

/**
 * Agenda de 1:1 no Google Agenda (.spec/transc/google-agenda.md): feed iCalendar por URL
 * secreta. `url` e o link .ics (o token e a credencial), `webcal_url` o mesmo com webcal://
 * e `google_subscribe_url` abre a assinatura direto no Google Agenda.
 */
export interface CalendarFeed {
  url: string
  webcal_url: string
  google_subscribe_url: string
}

export interface CalendarFeedResponse {
  success: boolean
  data: CalendarFeed
}

export interface MentorDocumentsResponse {
  success: boolean
  mentor_documents: MentorDocument[]
}

export interface MentorDocumentResponse {
  success: boolean
  mentor_document: MentorDocument
}

export interface AdminMentorCoveragesResponse {
  success: boolean
  mentor_coverages: MentorCoverage[]
  /** Duração máxima (provisória, R08-27). */
  max_days: number
  max_days_pending: boolean
}

export interface AdminMentorCoverageResponse {
  success: boolean
  mentor_coverage: MentorCoverage
}

export interface AdminMentorCoveragePayload {
  mentor_id: number
  substitute_id: number
  starts_on: string
  ends_on: string
  reason: MentorCoverageReason
}

// ---------------------------------------------------------------------------
// R08-26 — calendario editorial de provas como fila de deadlines (§7, 2o momento)
// Espelho de ExamDeadlines::Queue e Api::V1::Admin::ExamDeadlinesController (medhunter-api)
// ---------------------------------------------------------------------------

export type ExamDeadlineKind = 'registration' | 'fee' | 'document' | 'exam' | 'result' | 'other'

/** R10-17 — 'manual' = exam_deadlines; 'hermes' = marco de processo aprovado no Hermes. */
export type ExamDeadlineSource = 'manual' | 'hermes'

export interface ExamDeadlineProcessRef {
  id: number
  name: string
  cycle_label: string | null
  category: string
}

/** Um prazo do edital na fila do aluno. */
export interface ExamDeadline {
  /** Id do exam_deadline (manual) ou do marco (hermes); use `key` como chave unica. */
  id: number
  /** "deadline-<id>" ou "milestone-<id>". */
  key: string
  source: ExamDeadlineSource
  kind: ExamDeadlineKind
  title: string
  /** 'YYYY-MM-DD' ou null. */
  opens_on: string | null
  /** 'YYYY-MM-DD' */
  due_on: string
  /** 0 = vence hoje (a fila so traz o que nao venceu). */
  days_until: number
  url: string | null
  notes: string | null
  completed: boolean
  completed_at: string | null
  /** So os manuais podem ser marcados como feitos; marco do Hermes e somente leitura. */
  completable: boolean
  milestone_type: string | null
  exam_process: ExamDeadlineProcessRef | null
}

/**
 * 'listed' com a lista da instituicao-alvo (pode vir vazia). Nos outros status
 * `institution` e null e a lista pode trazer so os exames nacionais do Hermes.
 */
export type ExamDeadlineQueueStatus = 'listed' | 'no_institution' | 'institution_not_matched'

/** `GET /exam_deadlines` e `GET /instructor/students/:id/exam_deadlines` — `exam_deadlines`. */
export interface ExamDeadlineQueue {
  status: ExamDeadlineQueueStatus
  as_of: string
  institution: { id: number; name: string } | null
  deadlines: ExamDeadline[]
}

export interface ExamDeadlineQueueResponse {
  success: boolean
  exam_deadlines: ExamDeadlineQueue
}

/** `POST|DELETE /exam_deadlines/:id/completion`. */
export interface ExamDeadlineItemResponse {
  success: boolean
  exam_deadline: ExamDeadline
}

/** Prazo como o admin ve (sem a marca de um aluno). */
export interface AdminExamDeadline extends Omit<ExamDeadline, 'completed' | 'completed_at' | 'completable'> {
  /** null = exame nacional do Hermes. */
  institution: { id: number; name: string | null } | null
  /** false = marco do Hermes (corrige-se no Hermes). */
  editable: boolean
}

export interface AdminExamDeadlinesResponse {
  success: boolean
  exam_deadlines: AdminExamDeadline[]
  kinds: ExamDeadlineKind[]
}

export interface AdminExamDeadlineResponse {
  success: boolean
  exam_deadline: AdminExamDeadline
}

export interface AdminExamDeadlinePayload {
  institution_id: number
  kind: ExamDeadlineKind
  title: string
  opens_on: string | null
  due_on: string
  url: string | null
  notes: string | null
}

// ---------------------------------------------------------------------------
// Assistente de IA do instrutor (POC) — espelho de Api::V1::Instructor::AssistantController (medhunter-api)
// ---------------------------------------------------------------------------

export type AssistantRole = 'user' | 'assistant'

export interface AssistantMessage {
  role: AssistantRole
  content: string
}

export interface AssistantUsageSummary {
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  tool_calls: number
  iterations: number
  models: { decision: string | null; answer: string | null }
}

export type AssistantErrorCode =
  | 'upstream_error'
  | 'rate_limited'
  | 'timeout'
  | 'internal'
  | 'assistant_unavailable'
  | 'daily_limit_reached'

/** Eventos do stream SSE: o `data` de cada evento e o Hash inteiro, com `type` repetido. */
export type AssistantStreamEvent =
  | { type: 'status'; message: string; tool?: string }
  | { type: 'delta'; text: string }
  | { type: 'done'; usage: AssistantUsageSummary }
  | { type: 'error'; message: string; code: AssistantErrorCode }

export interface AssistantStreamOptions {
  onEvent: (event: AssistantStreamEvent) => void
  signal?: AbortSignal
}

// ─── INST-03: resumo do formulario de entrada no detalhe do afilhado ───

/** Recorte de preparacao do onboarding que o padrinho le (sem dado pessoal). */
export type InstructorOnboardingAnswers = Pick<
  import('./onboarding').OnboardingAnswers,
  | 'journey_stage'
  | 'graduation_year'
  | 'prep_course_this_year'
  | 'prep_course_last_year'
  | 'previous_mentorship'
  | 'previous_mentorship_other'
  | 'previous_mentorship_feedback'
  | 'priority_institutions'
  | 'weekly_study_hours'
  | 'past_exams_count'
  | 'main_difficulties'
  | 'mentor_preferences'
>

/**
 * `GET /instructor/students/:id` → `student.onboarding`. `null` = formulario nao preenchido
 * (sem registro ou aberto sem nenhuma resposta).
 */
export interface InstructorStudentOnboarding {
  status: Exclude<import('./onboarding').OnboardingStatus, 'not_started'>
  completed_at: string | null
  answers: InstructorOnboardingAnswers
}

// ---------------------------------------------------------------------------
// HERMES — Processos seletivos (substituem os editais do INST-05/MH9-01).
// Contrato: .spec/hermes/contrato.md. Espelho de ExamProcesses::PublicPayload, do snapshot
// (§1) e de Api::V1::Admin::ExamProcesses*Controller (medhunter-api). Dono: agente de contrato.
// Datas 'YYYY-MM-DD'; instantes ISO 8601 com offset; enums em MAIUSCULO como na API.
// ---------------------------------------------------------------------------

export type ExamProcessCategory = 'RESIDENCIA_MEDICA' | 'EXAME_NACIONAL' | 'POS_GRADUACAO_MEDICA'

export type ExamProcessLifecycleStatus =
  | 'AWAITING_NOTICE'
  | 'NOTICE_PUBLISHED'
  | 'REGISTRATION_OPEN'
  | 'REGISTRATION_CLOSED'
  | 'NEXT_MILESTONE'
  | 'AWAITING_RESULT'
  | 'PRELIMINARY_RESULT_AVAILABLE'
  | 'FINAL_RESULT_AVAILABLE'
  | 'SUSPENDED'
  | 'CLOSED'
  | 'FINISHED'

export type ExamProcessMilestoneType =
  | 'NOTICE_PUBLICATION'
  | 'REGISTRATION_START'
  | 'REGISTRATION_END'
  | 'FIRST_PHASE'
  | 'SECOND_PHASE'
  | 'CURRICULUM_SUBMISSION'
  | 'CURRICULUM_ANALYSIS'
  | 'INTERVIEW'
  | 'PRELIMINARY_RESULT'
  | 'APPEALS'
  | 'FINAL_RESULT'

export type ExamProcessMilestoneState =
  | 'CONFIRMED'
  | 'NOT_ANNOUNCED'
  | 'NOT_APPLICABLE'
  | 'VERIFYING'
  | 'COMPLETED'
  | 'RECTIFIED'

export type ExamProcessDocumentType = 'NOTICE' | 'RECTIFICATION' | 'EXAM_BOARD_PAGE' | 'REGISTRATION_PAGE' | 'RESULT'

export type ExamProcessResultKind = 'PRELIMINARY' | 'FINAL'

export type ExamProcessReviewStatus = 'pending' | 'approved' | 'rejected'

export type ExamProcessRevisionSource = 'agent' | 'admin'

// ---------- snapshot (o que o Hermes e o formulario do admin enviam; contrato §1) ----------

export interface ExamProcessSnapshotMilestone {
  key: string
  type: ExamProcessMilestoneType
  state: ExamProcessMilestoneState
  date?: string | null
  end_date?: string | null
  /** 'HH:MM' — so quando a fonte divulga; nunca inventado. Exige `date`. */
  time?: string | null
  /** IANA; ausente = 'America/Sao_Paulo'. */
  timezone?: string | null
  /** Texto literal da fonte (ex.: 'turno da manhã'). */
  time_text?: string | null
  official_text?: string | null
  source_url?: string | null
  rectified?: boolean
  document_key?: string | null
}

export interface ExamProcessSnapshotDocument {
  key: string
  type: ExamProcessDocumentType
  /** Obrigatorio em RESULT; proibido nos outros tipos. */
  result_kind?: ExamProcessResultKind | null
  title: string
  url: string
  published_at?: string | null
}

export interface ExamProcessSnapshotChange {
  key: string
  field?: string | null
  milestone_key?: string | null
  previous_value?: string | null
  new_value?: string | null
  summary: string
  document_key: string
  detected_at: string
}

export type ExamProcessSnapshotInstitution = { id: number } | { name: string }

export interface ExamProcessSnapshot {
  schema_version: 1
  external_process_id: string
  category: ExamProcessCategory
  institution?: ExamProcessSnapshotInstitution | null
  name: string
  cycle_label: string
  cycle_year: number
  lifecycle_status: ExamProcessLifecycleStatus
  next_milestone_key?: string | null
  milestones: ExamProcessSnapshotMilestone[]
  documents: ExamProcessSnapshotDocument[]
  changes: ExamProcessSnapshotChange[]
  content_hash?: string
}

// ---------- leitura publica (padrinho e aluno; so a revisao aprovada; contrato §3.5) ----------

export interface ExamProcessMilestone {
  key: string
  type: ExamProcessMilestoneType
  state: ExamProcessMilestoneState
  date: string | null
  end_date: string | null
  time: string | null
  timezone: string
  time_text: string | null
  official_text: string | null
  source_url: string | null
  rectified: boolean
  document_key: string | null
  /** ISO 8601 com offset quando ha `date` + `time`; senao null. */
  starts_at: string | null
  /** `date` - hoje, em dias; negativo se passou; null sem data. */
  days_until: number | null
}

export interface ExamProcessDocument {
  key: string
  type: ExamProcessDocumentType
  result_kind: ExamProcessResultKind | null
  title: string
  url: string
  published_at: string | null
}

export interface ExamProcessChange {
  key: string
  field: string | null
  milestone_key: string | null
  previous_value: string | null
  new_value: string | null
  summary: string
  document_key: string
  detected_at: string
  /** detected_at nos ultimos 15 dias. */
  recent: boolean
}

/** Os cinco botoes. `result` prefere FINAL a PRELIMINARY; cada um e o mais recente do tipo. */
export interface ExamProcessDocumentLinks {
  notice: ExamProcessDocument | null
  latest_rectification: ExamProcessDocument | null
  exam_board_page: ExamProcessDocument | null
  registration_page: ExamProcessDocument | null
  result: ExamProcessDocument | null
}

export interface ExamProcessPublic {
  id: number
  category: ExamProcessCategory
  institution: { id: number; name: string } | null
  name: string
  cycle_label: string
  cycle_year: number
  lifecycle_status: ExamProcessLifecycleStatus
  next_milestone: ExamProcessMilestone | null
  recent_change: boolean
  /** So as mudancas recentes, da mais nova para a mais antiga. */
  recent_changes: ExamProcessChange[]
  /** Na ordem do snapshot (cronologica). */
  milestones: ExamProcessMilestone[]
  documents: ExamProcessDocument[]
  document_links: ExamProcessDocumentLinks
  /** Historico inteiro, da mais nova para a mais antiga. */
  changes: ExamProcessChange[]
  /** Quando a revisao vigente foi aprovada. */
  updated_at: string | null
}

/** Item da lista do aluno e do afilhado. */
export interface ExamProcessListItem extends ExamProcessPublic {
  /** Posicao da instituicao nas prioridades do aluno (1 = prova principal); null p/ exame nacional fora das prioridades. */
  target_rank: number | null
}

export interface ExamProcessUnmatchedTarget {
  typed_name: string
  rank: number
}

/** `GET /exam_processes` (aluno) e `GET /instructor/students/:id/exam_processes` */
export interface ExamProcessesResponse {
  success: boolean
  exam_processes: ExamProcessListItem[]
  /** Instituicoes digitadas que nao casam com o catalogo. */
  unmatched: ExamProcessUnmatchedTarget[]
}

// ---------- Radar e calendario do aluno (D-013, D-014; .time/pacotes/P-004-contrato.md) ----------

export type ExamRadarTargetType = 'institution' | 'exam_process'

export interface ExamRadarTarget {
  type: ExamRadarTargetType
  id: number
  name: string
}

export interface ExamRadarEntry {
  id: number
  target: ExamRadarTarget
  created_at: string
  processes: ExamProcessPublic[]
}

export interface ExamRadarOption {
  type: ExamRadarTargetType
  id: number
  name: string
  /** Cidade e UF: distingue homonimos do catalogo. Sempre null em exam_process. */
  location: string | null
  cycle_label: string | null
  process_count: number
}

export interface ExamRadarEntriesResponse {
  success: boolean
  radar_entries: ExamRadarEntry[]
}

export interface ExamRadarOptionsResponse {
  success: boolean
  radar_options: ExamRadarOption[]
}

export interface ExamRadarEntryResponse {
  success: boolean
  radar_entry: ExamRadarEntry
}

export type ExamCalendarEdge = 'single' | 'start' | 'end'

export interface ExamCalendarItem {
  key: string
  milestone_id: number
  /** Data pura YYYY-MM-DD: nao converter com Date. */
  date: string
  edge: ExamCalendarEdge
  milestone_type: ExamProcessMilestoneType
  state: ExamProcessMilestoneState
  time: string | null
  time_text: string | null
  timezone: string | null
  exam_process: { id: number; name: string; cycle_label: string; category: ExamProcessCategory }
  institution: { id: number; name: string } | null
  target_rank: number | null
}

export interface ExamCalendarResponse {
  success: boolean
  as_of: string
  calendar_items: ExamCalendarItem[]
}

// ---------- Central do Aprovado (INST-06/HERMES; contrato §3.3) ----------

export interface ApprovalCenterStudent {
  id: number
  name: string
  /** Posicao da instituicao nas prioridades do aluno (1 = prova principal). */
  rank: number
  main: boolean
}

export interface ApprovalCenterProcess extends ExamProcessPublic {
  /** EXAME_NACIONAL: vale para toda a carteira; `students` vem vazio. */
  national: boolean
  students: ApprovalCenterStudent[]
}

export interface ApprovalCenter {
  as_of: string
  /** Ordem: proximo marco datado primeiro (mais perto), sem data no fim; empate pelo nome. */
  processes: ApprovalCenterProcess[]
  counts: { processes: number; with_recent_change: number; awaiting_notice: number; mentees: number }
  unmatched: { student: { id: number; name: string }; typed_name: string; rank: number }[]
}

/** `GET /instructor/approval_center` */
export interface ApprovalCenterResponse {
  success: boolean
  approval_center: ApprovalCenter
}

// ---------- admin (contrato §3.2) ----------

export interface ExamProcessRevisionMeta {
  id: number
  number: number
  exam_process_id: number
  external_process_id: string
  content_hash: string
  review_status: ExamProcessReviewStatus
  source: ExamProcessRevisionSource
  lifecycle_status: ExamProcessLifecycleStatus
  submitted_at: string | null
  reviewed_at: string | null
  rejection_reason: string | null
  superseded_by_id: number | null
  latest: boolean
  current: boolean
}

export interface AdminExamProcessRevisionMeta extends ExamProcessRevisionMeta {
  /** Instituicao do catalogo que o servidor resolveu para a revisao (o snapshot pode trazer so o nome); e a que a aprovacao publica. */
  institution: { id: number; name: string } | null
  api_key: { id: number; name: string } | null
  submitted_by: { id: number; name: string } | null
  reviewed_by: { id: number; name: string } | null
}

export interface ExamProcessMeta {
  id: number
  external_process_id: string
  category: ExamProcessCategory
  institution: { id: number; name: string } | null
  name: string
  cycle_label: string
  cycle_year: number
  /** Da revisao aprovada vigente; null antes da primeira aprovacao. */
  lifecycle_status: ExamProcessLifecycleStatus | null
  current_revision_id: number | null
  latest_revision_id: number | null
  created_at: string | null
  updated_at: string | null
}

export interface AdminExamProcessSummary extends ExamProcessMeta {
  current_revision: AdminExamProcessRevisionMeta | null
  latest_revision: AdminExamProcessRevisionMeta | null
  /** A ultima revisao recebida espera o admin. */
  pending: boolean
}

export interface ExamProcessFieldDiff {
  field: string
  before: unknown
  after: unknown
}

export interface ExamProcessCollectionDiff {
  added: string[]
  removed: string[]
  changed: { key: string; fields: ExamProcessFieldDiff[] }[]
}

/** Pendente x aprovada vigente. `base_revision_id` null = processo novo (tudo entra em `added`). */
export interface ExamProcessDiff {
  base_revision_id: number | null
  target_revision_id: number
  fields: ExamProcessFieldDiff[]
  milestones: ExamProcessCollectionDiff
  documents: ExamProcessCollectionDiff
  changes: ExamProcessCollectionDiff
}

export interface AdminExamProcessRevisionItem {
  revision: AdminExamProcessRevisionMeta
  process: ExamProcessMeta
  /** O snapshot gravado (sem content_hash). */
  snapshot: ExamProcessSnapshot
  /** Snapshot da revisao aprovada vigente; null se nao ha. */
  current_snapshot: ExamProcessSnapshot | null
  diff: ExamProcessDiff
}

export interface AdminExamProcessListParams {
  /** 'pending' = so processos cuja ultima revisao espera o admin. */
  review?: 'pending'
  category?: ExamProcessCategory
  q?: string
  page?: number
}

/** `GET /admin/exam_processes` */
export interface AdminExamProcessesResponse {
  success: boolean
  exam_processes: AdminExamProcessSummary[]
  page: number
  next_page: number | null
}

/** `GET /admin/exam_processes/:id` */
export interface AdminExamProcessResponse {
  success: boolean
  exam_process: AdminExamProcessSummary
  current_snapshot: ExamProcessSnapshot | null
  /** Da mais nova para a mais antiga (ate 50). */
  revisions: AdminExamProcessRevisionMeta[]
}

/** `POST /admin/exam_processes` (201) e `PATCH /admin/exam_processes/:id` (200). */
export interface AdminExamProcessSavedResponse {
  success: boolean
  exam_process: AdminExamProcessSummary
  revision: AdminExamProcessRevisionMeta
}

export interface AdminExamProcessRevisionListParams {
  status?: ExamProcessReviewStatus
  exam_process_id?: number
}

/** `GET /admin/exam_process_revisions` */
export interface AdminExamProcessRevisionsResponse {
  success: boolean
  revisions: AdminExamProcessRevisionItem[]
}

/** `GET /admin/exam_process_revisions/:id`, `POST .../approve`, `POST .../reject` */
export interface AdminExamProcessRevisionResponse {
  success: boolean
  revision: AdminExamProcessRevisionItem
}

/** 422 do snapshot invalido (contrato §1.8). */
export interface ExamProcessValidationError {
  path: string
  code: string
  message: string
}

// ---------------------------------------------------------------------------
// Chaves de API dos agentes externos (MH9-01) — espelho de ApiKey#payload e
// Api::V1::Admin::ApiKeysController (medhunter-api)
// ---------------------------------------------------------------------------

export interface ApiKeyScopeOption {
  key: string
  label: string
}

export interface AdminApiKey {
  id: number
  name: string
  /** Comeco do token, para reconhecer a chave; o token inteiro nunca volta. */
  token_prefix: string
  scopes: string[]
  auto_approve: boolean
  last_used_at: string | null
  revoked_at: string | null
  revoked: boolean
  created_at: string | null
  created_by: { id: number; name: string } | null
  /** R11-13: URL que recebe os eventos de pagamento (webhook de saida); null = sem webhook. */
  webhook_url: string | null
  /** URL e segredo da assinatura presentes (o segredo em si nunca volta na listagem). */
  webhook_configured: boolean
}

/** R11-13/MH9-07: um evento do webhook de saida, como `Hermes::EventCatalog` descreve. */
export interface WebhookEventInfo {
  event: string
  group: 'payment' | 'mentor'
  label: string
  /** Quando dispara. */
  trigger: string
  /** Para quem o Hermes deve mandar a mensagem. */
  recipient: string
  /** Corpo de exemplo, com dados ficticios: `{ id, event, occurred_at, data }`. */
  example: Record<string, unknown>
  /** Tipo e descricao de cada atributo do corpo (`Hermes::EventSchema`). */
  schema: WebhookSchemaNode
}

/** Um atributo do corpo do webhook, no formato do OpenAPI. */
export interface WebhookSchemaNode {
  type: 'string' | 'integer' | 'number' | 'boolean' | 'object' | 'array'
  description: string
  format?: string
  /** A chave sempre vem, mas pode vir null. */
  nullable?: boolean
  /** A chave so existe em uma das formas do evento. */
  optional?: boolean
  enum?: string[]
  properties?: Record<string, WebhookSchemaNode>
  items?: WebhookSchemaNode
}

/** `GET /admin/api_keys/webhook_events` */
export interface WebhookEventsResponse {
  success: boolean
  events: WebhookEventInfo[]
  /** Cabecalhos que acompanham cada POST. */
  headers: Record<string, string>
}

/** R15.1-04: um bloco do "Ver exemplo" de uma rota externa (corpo, parametros ou resposta). */
export interface ExternalRouteSample {
  id: string
  title: string
  /** `Recomendado` ou `Alternativa`, nos corpos do envio de processo. */
  tag: string | null
  example: Record<string, unknown>
  schema: WebhookSchemaNode
}

/** R15.1-04: uma rota de `/api/v1/external`, como `Hermes::RouteCatalog` descreve. */
export interface ExternalRouteInfo {
  id: string
  verb: 'GET' | 'POST'
  path: string
  /** Escopo que a chave precisa ter. */
  scope: string
  label: string
  summary: string
  notes: string[]
  samples: ExternalRouteSample[]
}

/** `GET /admin/api_keys/external_routes` */
export interface ExternalRoutesResponse {
  success: boolean
  routes: ExternalRouteInfo[]
}

/** `GET /admin/api_keys` */
export interface AdminApiKeysResponse {
  success: boolean
  api_keys: AdminApiKey[]
  scopes: ApiKeyScopeOption[]
}

export interface AdminApiKeyResponse {
  success: boolean
  api_key: AdminApiKey
  /** Segredo do webhook (`whsec_...`): so vem quando acabou de ser gerado. */
  webhook_secret?: string | null
}

/** `POST /admin/api_keys` — `token` so existe nesta resposta. */
export interface AdminApiKeyCreatedResponse extends AdminApiKeyResponse {
  token: string
}

export interface AdminApiKeyPayload {
  name: string
  scopes: string[]
  auto_approve: boolean
  /** Vazio remove o webhook (e o segredo). */
  webhook_url?: string
}

// ---------------------------------------------------------------------------
// Assistente de IA no admin (MH9-13) — espelho de Api::V1::Admin::AssistantSettingsController
// e Assistant::Settings (medhunter-api)
// ---------------------------------------------------------------------------

/** Valor vigente de um campo: `admin` = gravado pela tela; `default` = vem do ambiente (ENV). */
export interface AssistantSettingField<T> {
  value: T
  source: 'admin' | 'default'
  default: T
}

export interface AssistantSettings {
  daily_token_limit: AssistantSettingField<number>
  decision_model: AssistantSettingField<string>
  answer_model: AssistantSettingField<string>
  extra_instructions: AssistantSettingField<string | null>
}

/** Lista fechada de modelos; `free` = so serve com dados ficticios. */
export interface AssistantModelOption {
  id: string
  label: string
  free: boolean
}

/** Uma versao gravada. Campo `null` = usa o padrao do ambiente. */
export interface AssistantSettingsVersion {
  id: number
  daily_token_limit: number | null
  decision_model: string | null
  answer_model: string | null
  extra_instructions: string | null
  created_at: string | null
  created_by: { id: number; name: string } | null
  restored_from_id: number | null
}

export interface AssistantInstructorRow {
  id: number
  name: string
  email: string
  assistant_enabled: boolean
  last_used_at: string | null
  tokens_today: number
}

/** `GET|PATCH /admin/assistant_settings` e `POST .../versions/:id/restore` */
export interface AdminAssistantSettingsResponse {
  success: boolean
  settings: AssistantSettings
  models: AssistantModelOption[]
  limits: {
    daily_token_limit: { min: number; max: number }
    extra_instructions_max: number
  }
  versions: AssistantSettingsVersion[]
  instructors: AssistantInstructorRow[]
}

/** O que a tela grava. `null` volta o campo para o padrao do ambiente. */
export interface AdminAssistantSettingsPayload {
  daily_token_limit: number | null
  decision_model: string | null
  answer_model: string | null
  extra_instructions: string | null
}

export interface AdminAssistantInstructorResponse {
  success: boolean
  instructor: AssistantInstructorRow
}

// =============================================================================
// ZOOM — Aulas ao vivo (webinar da Zoom + Meeting SDK, Component View).
// Contrato: .spec/zoom/contrato.md §3. Datas em ISO 8601 com offset.
// =============================================================================

export type LiveSessionStatus = 'scheduled' | 'live' | 'ended' | 'canceled'
/** Papel gravado no vinculo (`live_session_participants.role`). */
export type LiveSessionParticipantRole = 'panelist' | 'attendee'
/** Papel de quem esta logado na aula: o admin entra como host. */
export type LiveSessionMyRole = 'host' | LiveSessionParticipantRole
export type LiveSessionJoinBlockedReason = 'not_linked' | 'canceled' | 'too_early' | 'closed'
export type LiveSessionListScope = 'upcoming' | 'past'
/** R10-11 — publico da aula: so vinculados, todos os alunos com aulas ao vivo, ou planos. */
export type LiveSessionAudienceMode = 'manual' | 'all_students' | 'plans'
/** R10-11 — watch = inscrito (so assiste); interact = panelista (camera, microfone, chat, mao). */
export type LiveSessionInteraction = 'watch' | 'interact'

export interface LiveSessionRecording {
  share_url: string
  password: string | null
}

export interface LiveSessionPerson {
  id: number
  name: string
}

/** `GET /live_sessions` e `GET /live_sessions/:id` (aluno, instrutor e admin). */
export interface LiveSession {
  id: number
  title: string
  description: string | null
  starts_at: string
  ends_at: string
  duration_minutes: number
  timezone: string
  status: LiveSessionStatus
  my_role: LiveSessionMyRole
  join_opens_at: string
  join_closes_at: string
  can_join: boolean
  join_blocked_reason: LiveSessionJoinBlockedReason | null
  panelists: LiveSessionPerson[]
  /** So quando a gravacao chegou (webhook `recording.completed`). */
  recording: LiveSessionRecording | null
  /** D-023: false para aluno. Com false, `recording` vem null e a sala nao mostra nem promete gravacao. */
  can_view_recording: boolean
  /** R15.1-02: sempre null. Comparecimento e so do admin, em `/admin/live_sessions/:id/attendance`. */
  attended: null
  /** R10-13: true para quem conduz (host ou panelista) — libera `POST /live_sessions/:id/sync_recording`. */
  can_sync_recording: boolean
  /** R10-11: como o aluno entra; null para host e instrutor (conduzem). */
  interaction?: LiveSessionInteraction | null
  /** R10-11: aviso quando o plano daria camera/microfone mas o teto de panelistas foi atingido. */
  interaction_notice?: string | null
}

export interface LiveSessionsResponse {
  success: boolean
  live_sessions: LiveSession[]
}

export interface LiveSessionResponse {
  success: boolean
  live_session: LiveSession
}

/** R10-12 — aula com a janela de entrada aberta agora (`GET /live_sessions/live_now`). */
export interface LiveNowSession {
  id: number
  title: string
  status: LiveSessionStatus
  starts_at: string
  ends_at: string
  timezone: string
  my_role: LiveSessionMyRole | null
  can_join: boolean
  /** true para host (admin) ou panelista — o destaque do menu do instrutor usa so estas. */
  conducting: boolean
}

export interface LiveNowResponse {
  success: boolean
  /** Ja ao vivo primeiro; no maximo 5. Vazio com a flag da Zoom desligada. */
  live_now: LiveNowSession[]
}

/** MH13-39 — reuniao ja acontecida em que o usuario estava inscrito (`GET /live_sessions/recent`). */
export interface RecentLiveSession {
  id: number
  title: string
  status: LiveSessionStatus
  starts_at: string
  ends_at: string
  timezone: string
  panelists: LiveSessionPerson[]
  recording: LiveSessionRecording | null
  /** R15.1-02: sempre null. Comparecimento e so do admin. */
  attended: null
}

export interface RecentLiveSessionsResponse {
  success: boolean
  /** Mais recente primeiro; no maximo 5. Vazio com a flag da Zoom desligada. */
  recent: RecentLiveSession[]
}

/** Credenciais do Meeting SDK (`ZoomMtgEmbedded.join`). `role`: 1 = host, 0 = panelista/inscrito. */
export interface LiveSessionJoinConfig {
  sdk_key: string
  signature: string
  meeting_number: string
  password: string
  user_name: string
  user_email: string
  role: 0 | 1
  participant_role: LiveSessionMyRole
  /** `tk` do panelista/inscrito; null para o host. */
  tk: string | null
  /** ZAK do host; null para panelista/inscrito. */
  zak: string | null
  /** Momento em que a assinatura expira (ISO 8601). */
  signature_expires_at: string
  /** R10-11: como o aluno entra; null para host e instrutor. */
  interaction?: LiveSessionInteraction | null
  interaction_notice?: string | null
}

export interface LiveSessionJoinResponse {
  success: boolean
  join: LiveSessionJoinConfig
}

// ---- Admin ----

export interface AdminLiveSession {
  id: number
  title: string
  description: string | null
  starts_at: string
  ends_at: string
  duration_minutes: number
  timezone: string
  status: LiveSessionStatus
  zoom_webinar_id: string | null
  zoom_host_id: string | null
  zoom_join_url: string | null
  created_by: LiveSessionPerson | null
  panelists_count: number
  attendees_count: number
  recording: LiveSessionRecording | null
  started_at: string | null
  ended_at: string | null
  canceled_at: string | null
  created_at: string
  updated_at: string
  /** R10-11 — publico por regra; `plan_rules` vale como filtro em `plans` e so como permissao em `all_students`. */
  audience_mode: LiveSessionAudienceMode
  plan_rules: AdminLiveSessionPlanRule[]
  /** Teto de panelistas da licenca (ENV ZOOM_PANELIST_LIMIT, padrao 100). */
  panelist_limit: number
}

/** R10-11 — permissao de interacao de um plano na aula. */
export interface AdminLiveSessionPlanRule {
  asaas_plan_id: number
  plan_name: string | null
  interaction: LiveSessionInteraction
}

export interface AdminLiveSessionParticipant {
  user_id: number
  name: string
  email: string
  role: LiveSessionParticipantRole
  /** Tem `tk` da Zoom (inscricao/panelista confirmado). */
  registered: boolean
  joined_at: string | null
  left_at: string | null
  attendance_seconds: number
  /** R10-11: manual = vinculado pelo admin; plan = entrou pelo publico do plano. */
  origin?: 'manual' | 'plan'
}

export interface AdminLiveSessionDetail extends AdminLiveSession {
  participants: AdminLiveSessionParticipant[]
}

export interface AdminLiveSessionsPagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

export interface AdminLiveSessionsResponse {
  success: boolean
  live_sessions: AdminLiveSession[]
  pagination: AdminLiveSessionsPagination
}

export interface AdminLiveSessionListParams {
  scope?: 'upcoming' | 'past' | 'all'
  status?: LiveSessionStatus
  page?: number
}

/** Usuario que a Zoom recusou ao vincular; os demais foram gravados. */
export interface LiveSessionFailedParticipant {
  user_id: number
  name: string | null
  error: string
}

export interface AdminLiveSessionResponse {
  success: boolean
  message?: string
  live_session: AdminLiveSessionDetail
  /** Presente em create e em POST participants. */
  failed_participants?: LiveSessionFailedParticipant[]
}

/** `POST /admin/live_sessions` — `starts_at` em ISO 8601 com offset. */
export interface AdminLiveSessionCreatePayload {
  title: string
  description?: string | null
  starts_at: string
  duration_minutes: number
  instructor_ids?: number[]
  student_ids?: number[]
  /** R10-11 — ausente = manual. `plans` exige ao menos uma regra. */
  audience_mode?: LiveSessionAudienceMode
  /** Enviado substitui todas as regras; ausente mantem. */
  plan_rules?: Array<Pick<AdminLiveSessionPlanRule, 'asaas_plan_id' | 'interaction'>>
}

/** `PATCH /admin/live_sessions/:id` — participantes NAO mudam por aqui. */
export type AdminLiveSessionUpdatePayload = Partial<
  Pick<AdminLiveSessionCreatePayload, 'title' | 'description' | 'starts_at' | 'duration_minutes' | 'audience_mode' | 'plan_rules'>
>

export interface AdminLiveSessionParticipantsPayload {
  instructor_ids?: number[]
  student_ids?: number[]
}

export interface AdminLiveSessionAttendanceRow {
  user_id: number
  name: string
  email: string
  role: LiveSessionParticipantRole
  joined_at: string | null
  left_at: string | null
  attendance_seconds: number
  attended: boolean
}

export interface AdminLiveSessionAttendanceResponse {
  success: boolean
  live_session_id: number
  summary: {
    linked: number
    attended: number
    panelists_attended: number
    attendees_attended: number
  }
  attendance: AdminLiveSessionAttendanceRow[]
}

/**
 * R10-13 — `POST /live_sessions/:id/sync_recording` e `POST /admin/live_sessions/:id/sync_recording`.
 * `not_available` = a Zoom ainda nao tem gravacao (200); aula nao encerrada responde 422
 * `recording_not_eligible`.
 */
export type LiveSessionRecordingSyncStatus = 'fetched' | 'already_stored' | 'not_available'

export interface LiveSessionRecordingSyncResponse {
  success: boolean
  message: string
  recording_status: LiveSessionRecordingSyncStatus
  live_session: LiveSession
}

export interface AdminLiveSessionRecordingSyncResponse {
  success: boolean
  message: string
  recording_status: LiveSessionRecordingSyncStatus
  live_session: AdminLiveSessionDetail
}

// =============================================================================
// ZOOM — configuracao das credenciais no admin (substitui o .env).
// Espelho de Api::V1::Admin::ZoomSettingsController. Spec: .spec/zoom/configuracao-admin.md.
// Os segredos nunca voltam do servidor: so `*_set` diz se ha um salvo.
// =============================================================================

/** De onde vem a configuracao vigente: banco, ENV ou nenhuma das duas. */
export type ZoomSettingsSource = 'database' | 'env' | 'none'

/** Nomes dos campos de credencial, como o servidor os lista em `missing_fields`. */
export type ZoomSettingsField =
  | 'account_id'
  | 'client_id'
  | 'client_secret'
  | 'sdk_key'
  | 'sdk_secret'
  | 'webhook_secret_token'
  | 'host_user_ids'

export interface AdminZoomSettings {
  enabled: boolean
  source: ZoomSettingsSource
  account_id: string | null
  client_id: string | null
  sdk_key: string | null
  host_user_ids: string[]
  client_secret_set: boolean
  sdk_secret_set: boolean
  webhook_secret_token_set: boolean
  missing_fields: string[]
  webhook_url: string
  last_test: { at: string | null; ok: boolean | null; message: string | null }
  updated_at: string | null
  updated_by: { id: number; name: string } | null
}

/** `GET|PATCH /admin/zoom_settings` */
export interface AdminZoomSettingsResponse {
  success: boolean
  data: AdminZoomSettings
}

/**
 * Corpo de `PATCH /admin/zoom_settings` (vai dentro de `zoom_settings`). Segredo ausente ou
 * vazio = manter o atual, por isso os tres sao opcionais e a tela so os manda quando digitados.
 */
export interface AdminZoomSettingsUpdatePayload {
  enabled: boolean
  account_id: string
  client_id: string
  sdk_key: string
  host_user_ids: string[]
  client_secret?: string
  sdk_secret?: string
  webhook_secret_token?: string
}

export interface AdminZoomSettingsTestHost {
  id: string
  ok: boolean
  email: string | null
  message: string | null
}

/** `POST /admin/zoom_settings/test`. Falha da Zoom tambem vem 200, com `ok: false`. */
export interface AdminZoomSettingsTestResult {
  ok: boolean
  message: string
  hosts: AdminZoomSettingsTestHost[]
}

// R10-09 (admin) — pacote Zoom por padrinho e status dele como host na conta da Zoom.
// Espelho de Api::V1::Admin::ZoomHostsController.

/** queued = na fila; pending = convite da Zoom nao aceito; active = host; failed = ver `error_message`. */
export type ZoomHostStatus = 'queued' | 'pending' | 'active' | 'failed'

export interface ZoomHostAccountState {
  status: ZoomHostStatus
  zoom_user_id: string | null
  /** 1 = Basic, 2 = Licensed. */
  zoom_user_type: number | null
  error_code: string | null
  error_message: string | null
  checked_at: string | null
}

export interface AdminZoomHostInstructor {
  id: number
  name: string
  email: string
  /** E host da Zoom: tem o pacote Zoom (R10-14: a caixa concede/tira o pacote "Zoom"). */
  zoom_enabled: boolean
  /** O e-mail esta na lista de hosts de Admin > Configuracoes > Zoom. */
  in_host_pool: boolean
  host: ZoomHostAccountState | null
}

/** `GET /admin/zoom_hosts` */
export interface AdminZoomHostsResponse {
  success: boolean
  data: { instructors: AdminZoomHostInstructor[] }
}

/** `PATCH /admin/zoom_hosts/:user_id` e `POST /admin/zoom_hosts/:user_id/check` */
export interface AdminZoomHostResponse {
  success: boolean
  data: { instructor: AdminZoomHostInstructor }
}

// TRANSC-04/16 (admin) — transcricao das 1:1: provedor, modelo, chaves mascaradas, versao do app
// desktop e painel do piloto. Contrato: .spec/transc/contrato.md §4.

export type TranscriptionProviderId = 'openrouter' | 'groq' | 'deepinfra'

/** De onde vem a chave do provedor: gravada no admin, variavel de ambiente ou nenhuma. */
export type TranscriptionApiKeySource = 'database' | 'env' | 'none'

export interface AdminTranscriptionProvider {
  id: TranscriptionProviderId
  label: string
  default_model: string
  api_key_set: boolean
  /** `"••••" + ultimos 4`; nunca o valor. */
  api_key_masked: string | null
  api_key_source: TranscriptionApiKeySource
}

export interface AdminTranscriptionSettings {
  enabled: boolean
  provider: TranscriptionProviderId
  model: string
  language: string
  providers: AdminTranscriptionProvider[]
  /** Decimal em string (ex. "0.04"). */
  usd_per_audio_hour: string
  min_app_version: string
  latest_app_version: string | null
  download_url: string | null
  storage_configured: boolean
  last_test: { at: string | null; ok: boolean | null; message: string | null }
  updated_at: string | null
  updated_by: { id: number; name: string } | null
}

/** `GET|PUT /admin/transcription_settings` */
export interface AdminTranscriptionSettingsResponse {
  success: boolean
  data: AdminTranscriptionSettings
}

/**
 * Corpo de `PUT /admin/transcription_settings` (vai dentro de `transcription_settings`). Chave em
 * branco ou ausente = manter a atual, por isso as tres sao opcionais.
 */
export interface AdminTranscriptionSettingsUpdatePayload {
  enabled: boolean
  provider: TranscriptionProviderId
  model: string
  language: string
  usd_per_audio_hour: string
  min_app_version: string
  latest_app_version: string
  download_url: string
  openrouter_api_key?: string
  groq_api_key?: string
  deepinfra_api_key?: string
}

/** Corpo opcional do teste; sem corpo o servidor testa a config gravada. */
export interface AdminTranscriptionTestPayload {
  provider?: TranscriptionProviderId
  model?: string
}

/** `POST /admin/transcription_settings/test` (`data`). Falha do provedor tambem vem 200, `ok: false`. */
export interface AdminTranscriptionTestResult {
  ok: boolean
  message: string
  provider: TranscriptionProviderId
  model: string
  latency_ms: number | null
  text: string | null
}

export interface AdminTranscriptionPilotTotals {
  meetings_ended: number
  with_recording: number
  with_transcript: number
  failed: number
  in_progress: number
  /** MH9-10: 1:1 em que o padrinho clicou em "Iniciar reuniao". */
  start_clicks: number
  /** MH9-10: 1:1 em que o Transcritor abriu pelo botao (codigo trocado). */
  app_opened: number
  /** MH9-10: ganho — taxa (0..1) de ata fechada em ate 24 h do fim, com e sem transcricao. */
  minutes_on_time_with_transcript: number
  minutes_on_time_without_transcript: number
  /** 0..1 */
  adoption_rate: number
  audio_hours: number
  cost_usd: string
  cost_per_hour_usd: string
}

export interface AdminTranscriptionPilotInstructor {
  id: number
  name: string
  meetings_ended: number
  with_recording: number
  with_transcript: number
  failed: number
  adoption_rate: number
  /** MH9-10: funil do "Iniciar reuniao". */
  start_clicks: number
  app_opened: number
  audio_hours: number
  cost_usd: string
  last_app_version: string | null
}

export interface AdminTranscriptionPilotFailureReason {
  /** Nulo se a gravacao falhou sem motivo gravado (`failure_reason` e opcional no modelo). */
  reason: string | null
  label: string | null
  count: number
}

export interface AdminTranscriptionPilotFailure {
  recording_id: number
  meeting_id: number
  instructor: { id: number; name: string } | null
  student: { id: number; name: string } | null
  scheduled_at: string
  reason: string | null
  label: string | null
  error_message: string | null
  failed_at: string | null
  retryable: boolean
}

/** Transcricao pronta no periodo (as 20 mais recentes): a porta do admin para ler a 1:1 (D9). */
export interface AdminTranscriptionPilotTranscript {
  meeting_id: number
  instructor: { id: number; name: string } | null
  student: { id: number; name: string } | null
  scheduled_at: string | null
  transcribed_at: string | null
  audio_duration_ms: number | null
}

export interface AdminTranscriptionPilot {
  range: { from: string; to: string }
  totals: AdminTranscriptionPilotTotals
  instructors: AdminTranscriptionPilotInstructor[]
  failures_by_reason: AdminTranscriptionPilotFailureReason[]
  recent_failures: AdminTranscriptionPilotFailure[]
  /** Divergencia D-spa-review-2: acrescimo a §4.5. Opcional enquanto a API nova nao sobe. */
  recent_transcripts?: AdminTranscriptionPilotTranscript[]
}

/** `GET /admin/transcription_pilot?from=&to=` */
export interface AdminTranscriptionPilotResponse {
  success: boolean
  data: AdminTranscriptionPilot
}

export interface AdminTranscriptionPilotRange {
  from?: string
  to?: string
}

/** `POST /admin/transcription_pilot/recordings/:id/retry` */
export interface AdminTranscriptionRetryResponse {
  success: boolean
  recording: { id: number; status: string }
}

/** `GET /admin/mentorship_meetings/:id/transcript` — o formato do instrutor mais a 1:1. */
export type AdminMeetingTranscriptResponse = MeetingTranscriptResponse & {
  meeting: {
    id: number
    scheduled_at: string
    student: { id: number; name: string }
    mentor: { id: number; name: string } | null
  }
}

/** R10-24: uma 1:1 realizada na lista da madrinha (mais recente primeiro). */
export interface AdminMeetingMetricsMeeting {
  id: number
  scheduled_at: string
  student: { id: number; name: string | null }
  scheduled_minutes: number
  /** Minutos de audio da transcricao; nulo sem transcricao pronta. */
  recorded_minutes: number | null
  /** A duracao que entra na media: a gravada quando houver, senao a agendada. */
  duration_minutes: number
  duration_source: 'recorded' | 'scheduled'
  transcription_status: TranscriptionStatus
  has_transcript: boolean
}

/** Medias em minutos (1 casa); nulas quando nao ha base. */
export interface AdminMeetingMetricsSummary {
  with_transcript: number
  avg_minutes: number | null
  avg_recorded_minutes: number | null
  avg_scheduled_minutes: number | null
}

export interface AdminMeetingMetricsInstructor extends AdminMeetingMetricsSummary {
  id: number
  name: string | null
  meetings_count: number
  students_count: number
  meetings_per_week: number
  /** Dias entre 1:1 seguidas; nulo com menos de duas. */
  avg_interval_days: number | null
  first_meeting_at: string
  last_meeting_at: string
  meetings: AdminMeetingMetricsMeeting[]
}

export interface AdminMeetingMetrics {
  range: { from: string; to: string; weeks: number }
  totals: AdminMeetingMetricsSummary & { meetings_count: number; instructors_count: number; meetings_per_week: number }
  instructors: AdminMeetingMetricsInstructor[]
}

/** `GET /admin/mentorship_meeting_metrics?from=&to=` (R10-24). */
export interface AdminMeetingMetricsResponse {
  success: boolean
  data: AdminMeetingMetrics
}

/** TRANSC-14: `GET /transcriber/version` (publico) — versao minima/atual e link de download do app. */
export interface TranscriberVersionResponse {
  success: boolean
  min_version: string
  latest_version: string | null
  download_url: string | null
}

// ---------- R10-15: perfis de acesso do admin ----------

/** Espelho de `AdminAccess::SectionCatalog::KEYS` (medhunter-api). */
export type AdminSectionKey =
  | 'dashboard'
  | 'users'
  | 'mentorships'
  | 'subscriptions'
  | 'analytics'
  | 'revenue'
  | 'quizzes'
  | 'courses'
  | 'prep_courses'
  | 'provas_integra'
  | 'catalogs'
  | 'followup_videos'
  | 'video_courses'
  | 'live_sessions'
  | 'instructor_packages'
  | 'difficulty_reports'
  | 'plans'
  | 'coupons'
  | 'sales_links'
  | 'announcements'
  | 'settings'

export interface AdminSectionOption {
  key: AdminSectionKey
  label: string
}

export interface AdminProfile {
  id: number
  name: string
  sections: AdminSectionKey[]
  users_count: number
  created_at: string
  updated_at: string
}

export interface AdminProfilePayload {
  name: string
  sections: AdminSectionKey[]
}

/** GET /admin/admin_profiles: perfis + catalogo de secoes (na ordem do menu). */
export interface AdminProfilesResponse {
  success: boolean
  admin_profiles: AdminProfile[]
  sections: AdminSectionOption[]
}

export interface AdminProfileResponse {
  success: boolean
  admin_profile: AdminProfile
}

/** PATCH /admin/users/:id/admin_profile */
export interface AdminProfileAssignmentResponse {
  success: boolean
  message: string
  user: { id: number; admin_profile_id: number | null; admin_sections: AdminSectionKey[] | null }
}

// ============================================================================
// R12-02 — Contrato do aluno assinado pelo Autentique
// Espelho de Api::V1::ContractsController, Admin::StudentContractSettingsController e
// Admin::StudentContractsController (Contracts::Presenter). Doc: medhunter-api/docs/autentique-contrato.md
// ============================================================================

export type StudentContractStatus = 'none' | 'pending' | 'signed' | 'rejected'

/** Por que o aluno nao tem contrato: emissao desligada/sem PDF/sem token, ou sem plano pago. */
export type StudentContractUnavailableReason = 'not_configured' | 'not_eligible'

/** MH13-04: ultimo contrato assinado do aluno, independente da compra que pede assinatura agora. */
export interface StudentContractLastSigned {
  template_name: string | null
  plan_name: string | null
  signed_at: string | null
  /** PDF assinado no Autentique (null enquanto o Autentique nao devolveu). */
  signed_file_url: string | null
}

export type StudentContractInfo =
  | {
      available: false
      reason: StudentContractUnavailableReason
      status: 'none'
      /** MH13-04 (opcional: API antiga nao manda). */
      last_signed?: StudentContractLastSigned | null
    }
  | {
      available: true
      status: StudentContractStatus
      /** Link do Autentique, so enquanto `pending`. */
      sign_url: string | null
      signed_at: string | null
      /** Ha CPF valido do checkout; sem ele a tela pede o CPF antes de gerar. */
      cpf_on_file: boolean
      /** Plano da compra que pede o contrato (null em contrato antigo, sem compra). */
      plan_name: string | null
      /** Modelo de contrato atual do plano (MH13-03). */
      template_name: string | null
      /** MH13-04: PDF assinado no Autentique; so vem quando `status` e 'signed' (pode ser null). */
      signed_file_url?: string | null
      /** MH13-04 (opcional: API antiga nao manda). */
      last_signed?: StudentContractLastSigned | null
    }

/** GET/POST /contract */
export interface StudentContractResponse {
  success: boolean
  contract: StudentContractInfo
}

/** Codigos de erro do POST /contract. */
export type StudentContractErrorCode =
  | 'cpf_required'
  | 'invalid_cpf'
  | 'unavailable'
  | 'provider_error'

/** Dados que o aluno digita antes de gerar (so os que a API pediu). */
export interface StudentContractIssueData {
  cpf?: string
}

export interface AdminStudentContractSettings {
  enabled: boolean
  /** Chave geral ligada + token do Autentique presente. */
  available: boolean
  autentique: { token_set: boolean; sandbox: boolean; webhook_secret_set: boolean; webhook_url: string }
  /** Planos ativos e pagos com contrato ativo. */
  plans_with_contract: number
  counts: Record<Exclude<StudentContractStatus, 'none'>, number>
  updated_at: string | null
  updated_by: { id: number; name: string } | null
}

/** GET/PATCH /admin/student_contract_settings */
export interface AdminStudentContractSettingsResponse {
  success: boolean
  data: AdminStudentContractSettings
}

export interface AdminUserContractDetail {
  id: number
  status: Exclude<StudentContractStatus, 'none'>
  sandbox: boolean
  plan_name: string | null
  template_name: string | null
  autentique_document_id: string
  panel_url: string
  sign_url: string | null
  signed_file_url: string | null
  signer_name: string | null
  created_at: string
  signed_at: string | null
  rejected_at: string | null
  last_event_at: string | null
}

export interface AdminUserContract {
  status: StudentContractStatus
  /** O aluno receberia contrato hoje (plano pago + emissao configurada). */
  eligible: boolean
  contract: AdminUserContractDetail | null
  /** Todos os contratos do usuario, do mais recente. */
  contracts: AdminUserContractDetail[]
  history_count: number
}

/** GET /admin/users/:id/contract e POST .../contract/refresh */
export interface AdminUserContractResponse {
  success: boolean
  data: AdminUserContract
}

/** R12-08: 'pdf' = PDF fixo; 'html' = modelo com variaveis {{CHAVE}} renderizado na emissao. Nao muda depois de criado. */
export type AdminContractTemplateKind = 'pdf' | 'html'

/** Modelo de contrato que os planos escolhem. Espelho de Contracts::TemplatePresenter. */
export interface AdminContractTemplate {
  id: number
  name: string
  kind: AdminContractTemplateKind
  /** So preenchido quando kind = 'html'. */
  body_html: string | null
  /** Chaves usadas no body, sem as chaves: ["NOME_COMPLETO", "CPF"]; [] para pdf. */
  variables_used: string[]
  archived: boolean
  archived_at: string | null
  /** null para html. */
  file: { filename: string; byte_size: number; uploaded_at: string } | null
  plans: { id: number; name: string; active: boolean }[]
  plans_count: number
  contracts_count: number
  created_at: string
  updated_at: string
  updated_by: { id: number; name: string } | null
}

/** GET /admin/contract_templates */
export interface AdminContractTemplatesResponse {
  success: boolean
  data: AdminContractTemplate[]
}

/** POST/PATCH /admin/contract_templates(/:id), archive, unarchive */
export interface AdminContractTemplateResponse {
  success: boolean
  data: AdminContractTemplate
}

/**
 * Multipart: `name`, `kind` (so no POST; default 'pdf') e `file` (pdf) ou `body_html` (html).
 * PATCH com `kind` diferente do atual -> 422.
 */
export interface AdminContractTemplatePayload {
  name?: string
  kind?: AdminContractTemplateKind
  file?: File
  body_html?: string
}

/** Variavel aceita no modelo HTML; `example` e ficticio (usado na previa). */
export interface AdminContractVariable {
  key: string
  label: string
  example: string
}

/** GET /admin/contract_templates/variables */
export interface AdminContractVariablesResponse {
  success: boolean
  data: AdminContractVariable[]
}

export interface AdminContractPreview {
  html: string
  unknown_variables: string[]
  variables_used: string[]
}

/** POST /admin/contract_templates/preview — nao valida nem salva. */
export interface AdminContractPreviewResponse {
  success: boolean
  data: AdminContractPreview
}

/** GET /admin/products/contract_templates — opcoes do select "Contrato" do plano. */
export interface PlanContractTemplateOptionsResponse {
  success: boolean
  contract_templates: { id: number; name: string }[]
}

// ==================== R10-20 — PEDIDOS DE PROVA ====================
// O aluno pede uma prova que Provas na Integra nao tem; o admin aceita ou recusa; o
// aceito fica na API externa para o Hermes buscar. O grupo e a instituicao normalizada.

export type ExamRequestStatus = 'pending' | 'accepted' | 'rejected' | 'fulfilled' | 'not_found'

export interface ExamRequest {
  id: number
  institution_name: string
  year: number | null
  exam_type: string | null
  notes: string | null
  status: ExamRequestStatus
  exam_id: number | null
  created_at: string
  updated_at: string
}

export interface CreateExamRequestPayload {
  institution_name: string
  year?: number | null
  notes?: string | null
}

export interface AdminExamRequest extends ExamRequest {
  normalized_name: string
  institution: { id: number; name: string } | null
  admin_notes: string | null
  reviewed_at: string | null
  reviewed_by: { id: number; email: string } | null
  fulfilled_at: string | null
  fulfillment_notes: string | null
  /** Pedidos da mesma instituicao no mesmo status (o "quantos alunos pediram"). */
  group_count: number
  user: { id: number; email: string | null; name: string | null }
}

export interface AdminExamRequestGroup {
  normalized_name: string
  institution_name: string
  count: number
  first_requested_at: string | null
}

export type AdminExamRequestStats = Record<ExamRequestStatus | 'total', number>

export interface AdminExamRequestsResponse {
  success: boolean
  requests: AdminExamRequest[]
  groups: AdminExamRequestGroup[]
  stats: AdminExamRequestStats
}

export interface AdminExamRequestDecisionResponse {
  success: boolean
  message: string
  updated_ids: number[]
  request: AdminExamRequest
}

// ---------- R10-05 — demanda de provas (instituicao x alunos x temos prova?) ----------

/** 'available' = ha prova com questoes; 'empty' = so prova sem questao; 'missing' = nenhuma. */
export type ExamDemandStatus = 'available' | 'empty' | 'missing'

export interface ExamDemandTypedName {
  name: string
  count: number
}

export interface ExamDemandInstitutionRow {
  institution: { id: number; name: string; abbreviation: string | null; state: string | null }
  /** Alunos distintos que escolheram a instituicao em qualquer prioridade. */
  students: number
  /** Desses, quantos a puseram como 1a prioridade (primeiro slot preenchido). */
  first_priority_students: number
  /** Grafias digitadas pelos alunos que casaram com a instituicao. */
  typed_names: ExamDemandTypedName[]
  /** Provas ativas (do processo filtrado, se houver); `years` so das que tem questoes. */
  exams: { total: number; with_questions: number; years: number[] }
  status: ExamDemandStatus
}

/** Nome digitado que nao casou com nenhuma instituicao do catalogo. */
export interface ExamDemandUnmatchedRow {
  normalized_name: string
  name: string
  typed_names: ExamDemandTypedName[]
  students: number
  first_priority_students: number
}

export interface AdminExamDemandResponse {
  success: boolean
  selection_process: string | null
  institutions: ExamDemandInstitutionRow[]
  unmatched: ExamDemandUnmatchedRow[]
  stats: {
    students: number
    institutions: number
    with_exam: number
    without_exam: number
    unmatched_names: number
    unmatched_students: number
  }
}

// ---------------------------------------------------------------------------
// VENDA-02 — links de venda do comercial (admin, secao "Links de venda").
// Espelho de Api::V1::Admin::SalesLinksController + SalesLinks::AdminPayload/Quote.
// Dinheiro em reais (decimal), como Checkout::InstallmentPricing.
// ---------------------------------------------------------------------------

export type SalesLinkPaymentMethod = 'pix' | 'credit_card'

/** Status de exibicao: `open` com validade vencida ja chega como `expired`. */
export type SalesLinkStatus = 'open' | 'used' | 'partially_paid' | 'paid' | 'expired' | 'cancelled'

export type SalesLinkPartStatus = 'pending' | 'paid' | 'failed' | 'cancelled'

export interface SalesLinkPerson {
  id: number
  name: string
  email: string
}

export interface SalesLinkPlanRef {
  id: number
  name: string
  duration_description: string | null
}

export interface SalesLinkPartSummary {
  position: number
  payment_method: SalesLinkPaymentMethod
  installments: number
  status: SalesLinkPartStatus
}

export interface AdminSalesLinkRow {
  id: number
  status: SalesLinkStatus
  /** Status gravado no banco (sem a troca de exibicao para `expired`). */
  stored_status: SalesLinkStatus
  plan: SalesLinkPlanRef | null
  created_by: SalesLinkPerson | null
  buyer_email: string | null
  /** So digitos. */
  buyer_cpf: string | null
  /** Quem confirmou o link (VENDA-03); null enquanto aberto. */
  customer: SalesLinkPerson | null
  net_total: number
  gross_total: number
  parts_summary: SalesLinkPartSummary[]
  expires_at: string
  created_at: string
  cancellable: boolean
  /** VENDA-05: soma bruta das partes pagas. */
  paid_amount: number
  /** VENDA-05: bruto das partes pendentes ou recusadas (0 em link pago, expirado ou cancelado). */
  remaining_amount: number
  /** VENDA-05: o mesmo, em valor liquido. */
  remaining_net_amount: number
  /**
   * MH14-09: "Reenviar e-mail" disponivel (`SalesLinks::BuyerEmail.resendable?`): link open,
   * used ou partially_paid, com token e destinatario. Ausente em respostas antigas.
   */
  email_resendable?: boolean
  /** MH14-09: ultimo envio confirmado pelo job (`buyer_email_sent`), ISO8601. */
  last_buyer_email_at?: string | null
}

/** VENDA-05: tipos de fato da linha do tempo do link. */
export type SalesLinkEventKind =
  | 'charge_created'
  | 'charge_resent'
  | 'part_paid'
  | 'part_failed'
  | 'part_overdue'
  | 'part_cancelled'
  | 'link_created'
  | 'link_used'
  | 'link_partially_paid'
  | 'link_paid'
  | 'link_expired'
  | 'link_cancelled'
  | 'access_released'
  | 'remaining_cancelled'
  | 'partial_alert_sent'
  // MH14-09: e-mail com o link ao comprador.
  | 'buyer_email_sent'
  | 'buyer_email_failed'

export interface SalesLinkEvent {
  id: number
  kind: SalesLinkEventKind
  part_id: number | null
  part_position: number | null
  payment_method: SalesLinkPaymentMethod | null
  amount: number | null
  /** Quem agiu (vendedor, cliente); null para fatos do Asaas e do job. */
  actor: SalesLinkPerson | null
  reason: string | null
  metadata: { manual_grant_id?: number; to?: string; paid_at?: string }
  occurred_at: string
}

/** VENDA-05: acoes do vendedor em link com pagamento parcial. */
export interface SalesLinkPartialActions {
  resend_part_ids: number[]
  can_release: boolean
  can_cancel_remaining: boolean
  /** Acesso ja liberado com o que foi pago ("Liberar mesmo assim"). */
  released: boolean
}

export interface AdminSalesLinkPart {
  id: number
  position: number
  payment_method: SalesLinkPaymentMethod
  net_amount: number
  gross_amount: number
  installments: number
  installment_value: number
  status: SalesLinkPartStatus
  paid_at: string | null
  /** A cobranca ja existe no Asaas (link confirmado). */
  has_charge: boolean
  /** VENDA-05: parte recusada ou vencida, sem valor recebido, que pode ser gerada de novo. */
  resendable: boolean
  /** VENDA-05: fatos desta parte (criada, paga, recusada, vencida...). */
  events: SalesLinkEvent[]
}

export interface AdminSalesLinkDetail extends AdminSalesLinkRow {
  note: string | null
  used_at: string | null
  cancelled_at: string | null
  cancelled_by: SalesLinkPerson | null
  asaas_installment_id: number | null
  fee_included: boolean
  parts: AdminSalesLinkPart[]
  /** VENDA-05: linha do tempo do link, em ordem. */
  timeline: SalesLinkEvent[]
  actions: SalesLinkPartialActions
}

export interface AdminSalesLinksListParams {
  status?: SalesLinkStatus
  created_by_id?: number
  page?: number
  per_page?: number
}

export interface AdminSalesLinksListResponse {
  success: boolean
  data: {
    sales_links: AdminSalesLinkRow[]
    sellers: SalesLinkPerson[]
    pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
  }
}

export interface AdminSalesLinkPlanOption {
  id: number
  name: string
  price: number
  max_installments: number
  duration_description: string | null
}

export interface AdminSalesLinkLimits {
  default_expiry_days: number
  min_expiry_days: number
  max_expiry_days: number
  min_part_amount: number
  max_pix_parts: number
  max_card_parts: number
  /** MH14-11: minimo por parcela do Asaas (`Asaas::Limits`), em reais. */
  min_installment_value: number
}

export interface AdminSalesLinkOptionsResponse {
  success: boolean
  data: { plans: AdminSalesLinkPlanOption[]; limits: AdminSalesLinkLimits }
}

export interface SalesLinkPartInput {
  payment_method: SalesLinkPaymentMethod
  /** Liquido da parte, em reais ("2000.00"). */
  net_amount: string
  installments: number
}

export interface SalesLinkQuotePayload {
  asaas_plan_id: number | null
  net_total: string
  parts: SalesLinkPartInput[]
}

export interface SalesLinkCreatePayload extends SalesLinkQuotePayload {
  buyer_email?: string
  buyer_cpf?: string
  expires_in_days: number
  note?: string
}

export type SalesLinkErrorCode =
  | 'plan_not_found'
  | 'invalid_total'
  | 'no_parts'
  | 'invalid_method'
  | 'invalid_amount'
  | 'part_minimum'
  | 'pix_installments'
  | 'installments_range'
  | 'too_many_pix'
  | 'too_many_cards'
  | 'sum_mismatch'
  | 'fees_unavailable'
  /** MH14-11: parcela da parte abaixo de R$ 5,00 (vem com `position`). */
  | 'installment_minimum'
  | 'invalid_expiry'
  | 'invalid_email'
  | 'invalid_cpf'

export interface SalesLinkError {
  code: SalesLinkErrorCode | string
  message: string
  /** 1-based quando o erro e de uma parte. */
  position?: number
}

export interface SalesLinkQuotePart {
  position: number
  payment_method: string
  net_amount: number | null
  installments: number | null
  /** null quando a parte e invalida ou falta a taxa do Asaas. */
  gross_amount: number | null
  installment_value: number | null
  /**
   * MH14-11: so em cartao com valor valido — maior n (ate o teto do plano) com parcela
   * >= R$ 5,00. null no Pix ou sem valor. Ausente em respostas antigas.
   */
  max_installments?: number | null
}

export interface SalesLinkQuote {
  ok: boolean
  plan: { id: number; name: string } | null
  net_total: number | null
  gross_total: number | null
  fee_included: boolean
  max_installments: number | null
  parts: SalesLinkQuotePart[]
  errors: SalesLinkError[]
}

export interface SalesLinkQuoteResponse {
  success: boolean
  data: SalesLinkQuote
}

export interface AdminSalesLinkCreateResponse {
  success: boolean
  data: {
    sales_link: AdminSalesLinkDetail
    /** Token cru: so existe nesta resposta. */
    token: string
    /** `/pagar/<token>`, relativo a SPA. */
    public_path: string
    /** MH14-09: o e-mail ao comprador (ausente em respostas antigas). */
    email?: SalesLinkEmailResult
  }
}

export interface AdminSalesLinkResponse {
  success: boolean
  data: { sales_link: AdminSalesLinkDetail }
}

/** MH14-09: espelho de `SalesLinks::BuyerEmail#email_json`. */
export type SalesLinkEmailStatus = 'queued' | 'skipped_no_email' | 'not_queued'

export interface SalesLinkEmailResult {
  status: SalesLinkEmailStatus
  message: string
}

/** MH14-09: `POST /admin/sales_links/:id/resend_email` (200). */
export interface AdminSalesLinkResendEmailResponse {
  success: boolean
  data: { sales_link: AdminSalesLinkDetail; email: SalesLinkEmailResult }
}

/** MH14-09: codigos do 422 do reenvio (`{ success: false, error, code }`). */
export type SalesLinkResendEmailErrorCode =
  | 'resend_cooldown'
  | 'link_not_resendable'
  | 'email_unavailable'
  | 'no_recipient'

// ============================================================================
// R10-14 — Pacotes de features do instrutor (Basico, Zoom, Coordenacao de madrinhas...)
// Espelho de InstructorAccess::FeatureCatalog, Admin::InstructorPackagesController e
// Admin::UserInstructorPackagesController (medhunter-api).
// ============================================================================

/** Espelho de `InstructorAccess::FeatureCatalog::KEYS`. */
export type InstructorFeatureKey =
  | 'alunos'
  | 'central_do_aprovado'
  | 'relatorio_ciclo'
  | 'meu_espelho'
  | 'administrativa'
  | 'transcritor'
  | 'aulas_ao_vivo_zoom'
  | 'coordenacao_madrinhas'

export interface InstructorAccessPackageRef {
  id: number
  name: string
  is_default: boolean
}

/**
 * `/auth/me` -> `user.instructor_access` (null para aluno). `configured: false` = nenhum
 * pacote cadastrado ainda: tudo liberado (comportamento anterior ao R10-14).
 */
export interface InstructorAccess {
  full_access: boolean
  configured: boolean
  features: InstructorFeatureKey[]
  packages: InstructorAccessPackageRef[]
}

/** 403 do gate dos pacotes do instrutor. */
export type InstructorGateErrorCode = 'feature_not_in_package'

export interface InstructorFeatureDefinition {
  key: InstructorFeatureKey
  name: string
  description: string
}

export interface InstructorFeatureGroup {
  key: string
  label: string
  features: InstructorFeatureDefinition[]
}

export interface InstructorPackage {
  id: number
  name: string
  description: string | null
  features: InstructorFeatureKey[]
  active: boolean
  is_default: boolean
  users_count: number
  created_at: string
  updated_at: string
}

export interface InstructorPackagePayload {
  name: string
  description: string
  features: InstructorFeatureKey[]
  active: boolean
  is_default: boolean
}

/** GET /admin/instructor_packages */
export interface InstructorPackagesResponse {
  success: boolean
  instructor_packages: InstructorPackage[]
  groups: InstructorFeatureGroup[]
}

export interface InstructorPackageResponse {
  success: boolean
  instructor_package: InstructorPackage
}

export interface UserInstructorPackageOption {
  id: number
  name: string
  active: boolean
  is_default: boolean
  features: InstructorFeatureKey[]
}

/** GET/PUT /admin/users/:id/instructor_packages */
export interface UserInstructorPackagesResponse {
  success: boolean
  message?: string
  instructor_package_ids: number[]
  packages: UserInstructorPackageOption[]
  access: InstructorAccess | null
  zoom_host: ZoomHostAccountState | null
}

// ---------- Primeira entrada (MH13-19/MH14-04) ----------

/**
 * MH14-04: `popup` = abrir a sequencia de videos obrigatorios; `none` = nada.
 * MH14-04b: `popup` volta em toda entrada enquanto houver aula nao assistida.
 */
export type FirstAccessVideosDisplay = 'popup' | 'none'

/** Uma aula marcada "Obrigatória no primeiro acesso", ja liberada para o aluno. */
export interface FirstAccessVideo {
  lesson_id: number
  video_course_id: number
  title: string
  embed_url: string
}

export interface FirstAccessVideosState {
  display: FirstAccessVideosDisplay
  /** Na ordem das videoaulas (curso, depois aula). Vazia quando `display` e `none`. */
  videos: FirstAccessVideo[]
}

/** Resposta de `POST /first_access/videos/:lesson_id/watched`. */
export interface FirstAccessVideoWatched {
  lesson_id: number
  watched: true
}

export interface EntranceTestState {
  /** Existe simulado de entrada (`quiz_type: entrance_test`) com questao. */
  available: boolean
  completed: boolean
  /** Mostrar o alerta "Prova liberada": aluno, liberado e nao concluido. */
  pending: boolean
  quiz: { id: number; title: string } | null
}
